/**
 * ====================================================================
 * V1→V2 PRICING STRATEGY MIGRATION (This migration occured for the release of new advance pricing strategies for quotation pricing)
 * ====================================================================
 *
 * MIGRATIONS:
 *
 * 1. STRATEGY MAPPING ENHANCEMENT:
 *    - Enhanced getRenderComponentByPricingStrategy to handle v1/v2 variants
 *    - Both v1 and v2 strategies use the same components (unified mapping)
 *    - Added feature flag system for conditional feature rendering
 *
 * 2. TAX SYSTEM MIGRATION:
 *    - REMOVED: INewQuoteState.tax_percentage (root level)
 *    - ADDED: Per-option tax structure in options[].taxes.total_percentage
 *    - Backend backfills data for existing quotes automatically
 *
 * 3. FLIGHT ENHANCEMENT FEATURES:
 *    - Added flight-specific tax controls (Tax %, Tax Applied On)
 *    - Conditional flight rounding based on strategy + editing old quotes (before migration) conditions
 *    - Enhanced table structure (SubTotal + Flight + Total rows)
 *
 * 4. SMART API MIGRATION:
 *    - determineFinalPricingStrategy() auto-detects feature usage
 *    - Sends v2 strategy to API only when flight features (flight rounding) and rounding point (synced rounding points among options) are used
 *    - Preserves v1 strategy when no enhanced features needed (flight rounding + sync rounding point among options)
 *
 * TESTING SCENARIOS:
 * - New quotes: Enhanced features available
 * - Existing quotes: Backward compatible behavior
 * - Old quotes Before Migration (overall_v1 and per_person_v1): No enhanced features, original behavior
 * - All strategies: Universal flight tax support
 * ====================================================================
 */

import {
	Alert,
	Box,
	Container,
	Button,
	Inline,
	Icons,
	Heading,
	Stack,
	Text,
	Col,
	DeferRender,
	Divider,
	Grid,
	Spinner,
	Table,
	Money,
	SwitchInput,
	MoneySum,
	Component,
	Select,
	SelectInput,
	joinAttributes,
	Tooltip,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	NavigationPromptDialog,
	queryToSearch,
	searchToQuery,
	useNavigationPrompt,
} from "@sembark-travel/ui-router"
import {
	PERMISSIONS,
	useCheckPermissions,
	FeatureFlag,
	useHasFeatureFlag,
} from "../Auth"
import {
	utcTimestampToLocalDate,
	dateToUTCString,
	startOf,
	formatDate,
	isSame,
	addUnit,
	endOf,
	subtractUnit,
	clone,
	getDiff,
	toISOString,
	parseDate,
	localOrUtcTimestampToLocalDate,
	convertLocalToUtcAccordingToTimezone,
	createDuration,
} from "@sembark-travel/datetime-utils"
import { useXHR, XHRInstance, XHRError } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	Fragment,
} from "react"
import { $PropertyType, $Values, Required } from "utility-types"
import { NewQuoteErrorDisplay } from "./NewQuoteErrorDisplay"
import config from "../config"
import { TTransportServiceLocation } from "../TransportServices"
import {
	collect,
	getTotalPriceForExtras,
	getTotalPriceForServices,
} from "../utils"
import {
	CalculatePriceForm as CalculateHotelPrice,
	ExtraServicesForm as ExtraHotelServices,
	allocatePaxInHotels,
} from "./../HotelPrices"
import {
	CalculatePriceForm as CalculateCabPrice,
	ExtraServicesForm as ExtraTransportServices,
	ICalculateCabExtraPriceParams,
} from "./../TransportServicePrices"

import {
	CalculatePriceForm as CalculateVisaPrice,
	isVisaRateExpired,
	processVisasServiceForPax,
	validateVisaServicesPax,
	TEditVisaServiceInQuote,
	TVisaServiceInQuote,
} from "../VisaServicePrices"

import {
	MarginsAndTaxInputField,
	PackagePricePreview,
	SelectGivenCurrency,
} from "./GiveQuote"
import {
	calculateMarginFromTotalPrice,
	calculateMarginPercentageFromTotalPrice,
	determineFinalPricingStrategy,
	orderDaywiseServicesByDate,
	prepareGivenPricesPerComponentPayloadDispatcher,
	prepareGlobalTaxAppliedOnPayloadDispatcher,
	prepareMarginsPayloadForOptionDispatcher,
	preparePerComponentTaxAllocationPayloadDispatcher,
	prepareTaxEditingTotalPayloadForOptionDispatcher,
	prepareTaxPercentagePayloadForOptionDispatcher,
	prepareUnroundedGivenPricesPerComponentPayloadDispatcher,
} from "./utils"
import {
	ExtraServicesForm as ExtraQuoteServices,
	OtherExtraServicesParams,
} from "./QuoteExtras"
import {
	QuoteExtras,
	QuoteFlights,
	QuoteHotels,
	QuoteTransportsAndActivityTickets,
} from "./Quotes"
import {
	IQuote,
	IQuoteCab,
	IQuoteOtherExtras,
	IQuoteTransportExtras,
	ITrip,
	ITripQuote,
	IGivenQuote,
	IQuoteTravelActivity,
	useFetchEditableQuote,
	TPricePerPersonAllocation,
	useTripRefresh,
	getEditableHotelsFromQuote,
	IQuoteHotelParams,
	getEditableHotelExtrasFromQuote,
	IQuoteHotelExtraParams,
	normalizeHotelsForQuote,
	normalizeHotelExtrasForQuote,
	transformHotelsToRequestDataForQuote,
	transformHotelExtrasToRequestDataForQuote,
	TGivenQuotePricingStrategy,
	TPricePerTravelComponentAllocation,
	IQuoteComponentsCostPricesDistributed,
	TUnroundedGivenPrice,
	TTaxAppliedOn,
	TTaxPerComponentAllocation,
} from "./store"
import {
	getTripQuoteDetailsDiff,
	usePricingStrategyMigrationSync,
} from "./utils"
import {
	childrenFromQueryParams,
	childrenToQueryParams,
	TChildrenArray,
} from "./../Tourists"
import { useLocation, useNavigate } from "./../router-utils"
import { IBasicInfo, EditBasicInfo } from "./EditQuoteBasicInfo"
import {
	Form,
	TextAreaInputField,
	SwitchInputField,
	withServerErrors,
	useFieldValue,
	useForm,
	GetFieldValue,
	validateFormValues,
	SubmissionError,
	isTruthy,
	EmptyNumberValidator,
	TextInputField,
	SelectInputField,
	FieldArray,
	arrayMutators,
	getFormValueForKey,
	SelectField,
	RadioInputField,
	addKeyToFieldArrayItem,
	getFieldArrayItemKey,
	MarkdownInputField,
} from "@sembark-travel/ui-form"
import * as Validator from "yup"
import { TTripDestination } from "../TripDestinations"
import {
	addMoneyFromDifferentCurrencies,
	makeCurrencyConverter,
	currencyPairsArrayToObject,
	currencyPairsObjectToArray,
	divideMoneyBy,
	formatMoneyByDecimal,
	createMoney,
	moneyParseByDecimal,
	subtractMoney,
	TCurrencyPair,
	TMoney,
	addMoney,
	TCurrencyPairsObject,
	multipleMoneyBy,
	TCurrency,
	TFormattedMoney,
} from "@sembark-travel/money"
import {
	CalculateTravelActivityPriceForm,
	TValidTravelActivityDatewisePricesInputFieldValue,
} from "../TravelActivityPrices"
import { ErrorBoundary } from "@sembark-travel/ui-error-boundary"
import {
	CurrencyPairInputFieldWithRates,
	useCurrenciesOfTenant,
} from "../Currencies"
import { Dialog } from "@sembark-travel/ui-dialog"
import { IHotel, IHotelMealPlan, SelectHotels } from "../Hotels"
import { TValidTransportServiceDatewisePricesFieldValue } from "../TransportServicePrices/TransportServiceDatewisePricesInputField"
import { getActivityDurationSlotAndOptions } from "../TravelActivityPrices/utils"
import { getClosedService, showClosedServicesAlert } from "../TravelActivities"
import {
	ComponentMarkupTaxPricingBifurcation,
	ComponentMarkupTaxPerPersonPricingBifurcationWithRounding,
} from "./TravelComponentPricingStrategy"
import {
	addLabelToAirport,
	DEFAULT_FLIGHT_CLASS,
	FlightService,
	getChildrenDistribution,
	IFlightServicesParams,
	TFlightClass,
	TFlightFormFillMode,
	TFlightType,
	transformFlightsFormDataToPreviewData,
	TFlightPaxAllocation,
	flightPaxAllocation,
} from "../Flights"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	getEntryTypeDisplayName,
	visaProcessingTimeStr,
	getPurposeOfVisaDisplayName,
} from "../Visa"
import { countChildrenAboveComplimentary } from "../CabPriceCalculationMetrics"
import { Email, PhoneNumber } from "../Contacts"
import { VisaTypeDisabled } from "../VisaBookings"
import AiParsingFeedbackDialog from "./AiParsingFeedbackDialog"
import { IComment } from "../Comments"

type IQuoteCabParams = Parameters<
	Required<React.ComponentProps<typeof CalculateCabPrice>>["onChange"]
>[0]

type IQuoteTravelActivityParams = Parameters<
	Required<
		React.ComponentProps<typeof CalculateTravelActivityPriceForm>
	>["onChange"]
>[0]

type IQuoteCabExtraParams = Array<
	Required<
		Required<
			ICalculateCabExtraPriceParams,
			"transport_extras"
		>["transport_extras"][number],
		"service"
	>
>
type IQuoteOtherExtraParams = Array<
	Required<
		Required<OtherExtraServicesParams, "other_extras">["other_extras"][number],
		"service"
	>
>
type IQuoteFlightParams = Omit<IFlightServicesParams, "flight_preview">
const emptyArray: TChildrenArray = []

const MINIMIZED_SIZE = "36px"
const NOTE_WIDTH = "360px"
const NOTE_HEIGHT = "320px"

export function TripComments({ comment }: { comment: IComment | null }) {
	const [isMinimized, setIsMinimized] = useState(true)

	const minimizeNote = () => setIsMinimized(true)
	const maximizeNote = () => setIsMinimized(false)

	return (
		<Box
			position="fixed"
			style={{
				top: "65px",
				right: "15px",
				width: isMinimized ? MINIMIZED_SIZE : NOTE_WIDTH,
				height: isMinimized ? MINIMIZED_SIZE : NOTE_HEIGHT,
				maxWidth: "70vw",
				transition: "width 0.25s ease, height 0.25s ease, transform 0.25s ease",
				zIndex: "999",
			}}
		>
			<Box
				as="button"
				title="Tasks & Comments"
				width="full"
				height="full"
				backgroundColor="transparent"
				borderRadius="md"
				onClick={maximizeNote}
				style={{ display: isMinimized ? undefined : "none" }}
			>
				<Icons.StickyNote color="primary" size="full" />
			</Box>
			<Stack
				width="full"
				height="full"
				borderRadius="md"
				backgroundColor="default"
				boxShadow={{ default: "base", hover: "lg" }}
				style={{ display: isMinimized ? "none" : undefined }}
			>
				<Inline
					justifyContent="between"
					alignItems="center"
					paddingLeft="4"
					bgColor="primary"
				>
					<Heading color="primary" fontSize="base">
						Tasks & Comments
					</Heading>
					<Button
						title="Minimize Tasks & Comments"
						level="tertiary"
						align="left"
						onClick={minimizeNote}
					>
						<Icons.MinusCircle color="danger" />
					</Button>
				</Inline>
				<Divider style={{ margin: "0" }} />
				<Box
					padding="4"
					width="full"
					style={{ maxHeight: `250px`, overflowY: "auto" }}
				>
					{comment ? (
						<Text whiteSpace="preserveLine" fontWeight="semibold">
							{comment.body}
						</Text>
					) : (
						<Text color="muted">No Comments!</Text>
					)}
				</Box>
			</Stack>
		</Box>
	)
}

export default function NewQuote({
	trip,
	initialQuote,
}: {
	trip: ITrip
	initialQuote?: ITripQuote
}) {
	const { blocker, disablePrompt, enablePrompt } = useNavigationPrompt(true)
	const location = useLocation()
	const navigate = useNavigate()
	const locationSearch = location.search
	const locationQuery = useMemo(() => {
		const locationQuery = searchToQuery<{
			startDate?: string
			days?: string
			adults?: string
			children?: string
			quoteId?: string
			editing?: 1
			afterConfirmedQuote?: 1
			using?: 1
			complimentaryCount?: string
			job_uuid?: string
		}>(locationSearch)
		const startDate = locationQuery.startDate
			? startOf(locationQuery.startDate, "day")
			: undefined
		const days = locationQuery.days ? Number(locationQuery.days) : undefined
		const adults = locationQuery.adults
			? Number(locationQuery.adults)
			: undefined
		const children =
			locationQuery.children === ""
				? []
				: locationQuery.children
					? childrenFromQueryParams(locationQuery.children)
					: undefined
		const quoteId = locationQuery.quoteId
			? Number(locationQuery.quoteId)
			: undefined
		const editing = isTruthy(locationQuery.editing) ? 1 : undefined
		const afterConfirmedQuote = isTruthy(locationQuery.afterConfirmedQuote)
			? 1
			: undefined
		const using = isTruthy(locationQuery.using) ? 1 : undefined
		const complimentaryCount = locationQuery.complimentaryCount
			? Number(locationQuery.complimentaryCount)
			: undefined
		const job_uuid = locationQuery.job_uuid ?? undefined
		return {
			startDate,
			days,
			quoteId,
			adults,
			children,
			editing,
			afterConfirmedQuote,
			using,
			complimentaryCount,
			job_uuid,
		}
	}, [locationSearch])
	const { quoteId, using: isUsingExistingQuote } = locationQuery
	const { data: fetchedQuote } = useFetchEditableQuote(
		initialQuote?.quote ? undefined : quoteId
	)
	const tripQuote = fetchedQuote ?? initialQuote
	const { quote } = tripQuote || {}
	const onCancel = useCallback(() => {
		if (tripQuote) {
			if (isUsingExistingQuote) {
				navigate(`/trips/:tripId`, {
					params: { tripId: String(trip.id) },
					replace: true,
				})
			} else {
				navigate(`/trips/:tripId/quotes/:quoteId`, {
					params: { tripId: String(trip.id), quoteId: String(tripQuote.id) },
					replace: true,
				})
			}
		} else {
			navigate(`/trips/:tripId`, {
				params: { tripId: String(trip.id) },
				replace: true,
			})
		}
	}, [navigate, trip, tripQuote, isUsingExistingQuote])
	const defaultBasicDetailsRef = useRef({
		startDate: localOrUtcTimestampToLocalDate(
			trip.start_date_local,
			trip.start_date
		),
		days: trip.days,
		adults: trip.no_of_adults,
		children: JSON.parse(
			JSON.stringify(trip.children || emptyArray)
		) as TChildrenArray,
		complimentaryCount: trip.complimentary_count,
	})
	const startDate =
		locationQuery.startDate || defaultBasicDetailsRef.current.startDate
	const days = locationQuery.days || defaultBasicDetailsRef.current.days
	const adults = locationQuery.adults || defaultBasicDetailsRef.current.adults
	const children =
		locationQuery.children || defaultBasicDetailsRef.current.children
	const pathname = location?.pathname
	const { owner_quote_request } = trip
	const complimentaryCount =
		locationQuery.complimentaryCount ??
		defaultBasicDetailsRef.current.complimentaryCount
	const setQuoteBasicInfo = useCallback(
		({
			startDate,
			days,
			children,
			adults,
			complimentary_count,
		}: {
			startDate: Date
			days: number
			adults: number
			children?: TChildrenArray
			complimentary_count?: number
		}) => {
			if (pathname) {
				//Disabling accidental navigation protection before navigating intentionally
				disablePrompt()
				navigate(
					{
						pathname: pathname as "/",
						search: queryToSearch({
							...locationQuery,
							startDate: formatDate(startDate, "YYYY-MM-DD"),
							days,
							children: childrenToQueryParams(children || []),
							adults,
							complimentaryCount: complimentary_count || 0,
						}),
					},
					{ replace: true }
				)
				//Enabling accidental navigation protection again after navigating intentionally
				setTimeout(() => enablePrompt(), 100)
			}
		},
		[locationQuery, navigate, disablePrompt, enablePrompt, pathname]
	)
	const onSuccess = useCallback(
		(quote: ITripQuote) => {
			if (navigate) {
				//Disable accidental navigation protection before navigating intentionally
				disablePrompt()
				if (!quote.itinerary) {
					navigate("/trips/:tripId/quotes/:quoteId/edit-itinerary", {
						params: { tripId: String(trip.id), quoteId: String(quote.id) },
						replace: true,
					})
				} else {
					navigate("/trips/:tripId/quotes/:quoteId", {
						params: { tripId: String(trip.id), quoteId: String(quote.id) },
						replace: true,
					})
				}
			}
			showSnackbar("Quote created")
		},
		[navigate, trip, disablePrompt]
	)
	const commonProps = {
		startDate,
		days,
		tripId: trip.id,
		onChangeBasicInfo: setQuoteBasicInfo,
		adults,
		children,
		navigate,
		trip,
		baseStartDate: localOrUtcTimestampToLocalDate(
			(quote || trip).start_date_local,
			(quote || trip).start_date
		),
		baseNoOfAdults: (quote || trip).no_of_adults,
		baseChildren: (quote || trip).children,
		complimentaryCount,
		onCancel,
		onSuccess,
		isEditing: locationQuery.editing ? true : false,
		afterConfirmedQuote:
			locationQuery.afterConfirmedQuote || locationQuery?.job_uuid
				? true
				: false,
		isUsingExistingQuote: isUsingExistingQuote ? true : false,
		job_uuid: locationQuery?.job_uuid,
	}
	return (
		<Box>
			{owner_quote_request ? (
				<Box padding="8">
					<Stack gap="2">
						<Text fontWeight="semibold">
							You are creating this quote for {owner_quote_request.from?.name}
						</Text>
						{owner_quote_request.comments ? (
							<Text
								as="blockquote"
								title={`Commented by ${owner_quote_request.from?.name}`}
								whiteSpace="preserveLine"
							>
								{owner_quote_request.comments}
							</Text>
						) : null}
					</Stack>
					<Divider />
				</Box>
			) : null}
			<Fragment key={`${formatDate(startDate, "YYYY-MM-DD")}-${days}`}>
				{!quoteId && !initialQuote?.quote ? (
					<NewQuoteForm {...commonProps} />
				) : !quote ? (
					<Spinner padding="4" alignCenter />
				) : (
					<NewQuoteForm quote={tripQuote} {...commonProps} />
				)}

				{/*Accidental Back Navigation Prevention Dialog*/}
				<NavigationPromptDialog blocker={blocker} />
			</Fragment>
		</Box>
	)
}

const newQuoteValidationSchema = Validator.object().shape({
	// tax_percentage removed - now using options[].taxes.total_percentage
})

const validate = validateFormValues(newQuoteValidationSchema)

interface NewQuoteFormProps {
	trip: ITrip
	tripId: number
	quote?: ITripQuote
	startDate: Date
	baseStartDate: Date
	baseNoOfAdults: number
	baseChildren: TChildrenArray
	complimentaryCount: number
	days: number
	adults: number
	children: TChildrenArray
	isEditing: boolean
	afterConfirmedQuote: boolean
	isUsingExistingQuote: boolean
	onChangeBasicInfo?: (data: IBasicInfo) => void
	onSuccess: (quote: ITripQuote) => void
	onCancel: () => void
	job_uuid?: string
	isAIItinerarybuilder?: boolean
	generationId?: string
}
interface ICabTypeMissingError {
	dayIndex: number
	serviceName: string
	cabIndex: number
	reason: string
}

export function NewQuoteForm({
	tripId,
	quote: tripQuote,
	baseStartDate,
	baseNoOfAdults,
	baseChildren,
	onCancel,
	onChangeBasicInfo,
	onSuccess,
	trip,
	isEditing,
	afterConfirmedQuote,
	isUsingExistingQuote,
	job_uuid,
	isAIItinerarybuilder,
	generationId,
	...props
}: NewQuoteFormProps) {
	const xhr = useXHR()
	const refreshTrip = useTripRefresh()
	const tenantCurrencies = useCurrenciesOfTenant()
	const { quote, ai_autofilled } = tripQuote || {}
	const { hasPermission } = useCheckPermissions()
	const minMarkupPercentage = trip.tenant_brand?.min_margin_percentage
	const {
		startDate,
		days,
		adults: no_of_adults,
		children,
		complimentaryCount,
	} = props
	const containerRef = useRef<HTMLDivElement>(null)
	const refId = isAIItinerarybuilder ? undefined : quote?.id
	const bookingFrom = formatDate(startDate, "YYYY-MM-DD HH:mm:ss")
	const bookingTo = formatDate(
		endOf(addUnit(startDate, days - 1, "days"), "day"),
		"YYYY-MM-DD HH:mm:ss"
	)
	const hasPackageUploader = useHasFeatureFlag("trip_quote_parsing")
	const tripDestinations = trip.destinations
	const destinationCostingCurrency = useMemo(
		() => tripDestinations.map((d) => d.currency).at(0) || "INR",
		[tripDestinations]
	)
	const [initialNewQuoteValues] = useState<INewQuoteState>(() => {
		return getQuoteInitialServicesValues({
			tripQuote,
			baseStartDate,
			baseNoOfAdults,
			baseChildren,
			startDate,
			noOfAdults: no_of_adults,
			children,
			days,
			defaultCostingCurrency: destinationCostingCurrency,
			isEditing,
			afterConfirmedQuote,
			hasMultiQuoteFeature: true,
			isUsingExistingQuote,
			tenantCurrencies,
			complimentaryCount,
		})
	})

	const canModifyQuoteExchangeRate =
		Boolean(trip.converted_at || trip.on_hold_at) ||
		hasPermission(PERMISSIONS.MODIFY_QUOTE_EXCHANGE_RATE)

	// State to store API submission errors for structured display
	const [submitError, setSubmitError] = useState<XHRError | null>(null)

	return (
		<Form<typeof initialNewQuoteValues>
			initialValues={initialNewQuoteValues}
			validate={validate}
			validateOnBlur
			onSubmit={withServerErrors(
				async ({
					startDate,
					days,
					no_of_adults,
					complimentary_count,
					children,
					options,
					hotel_options,
					cabs,
					transportExtras,
					flights,
					otherExtras,
					cabPrice,
					transportExtrasPrice,
					flightPrice,
					flightCostPrice,
					otherExtrasPrice,
					include_per_person_price,
					comments,
					tax_name,
					costing_currency,
					given_currency,
					currency_pairs_obj,
					functional_currency,
					pricing_strategy,
					migrate_to_v2,
					enable_sync_rounding_among_options,
					visas,
					visasPrice,
					missingCabTypeErrors,
				}) => {
					const closeHotelData: Record<string, Date[]> = {}
					const blacoutRoomsData: Record<string, Date[]> = {}
					hotel_options.forEach((hotels) =>
						hotels.hotels?.forEach(({ room_type, hotel, date_wise_prices }) => {
							const label = `${hotel.name} - ${room_type.name}`
							date_wise_prices.forEach(({ room_status, date, given_price }) => {
								if (room_status === "stop_sale") {
									if (!closeHotelData[label]) {
										closeHotelData[label] = []
									}
									closeHotelData[label].push(date)
								}
								if (room_status === "blackout" && given_price === 0) {
									if (!blacoutRoomsData[label]) {
										blacoutRoomsData[label] = []
									}
									blacoutRoomsData[label].push(date)
								}
							})
						})
					)

					const closeServiceData = getClosedService(cabs.daywise_services)
					const currency_pairs = prepareValidCurrencyPairsForPayload(
						currency_pairs_obj || {},
						costing_currency,
						given_currency,
						hotel_options,
						cabPrice,
						transportExtrasPrice,
						flightPrice,
						otherExtrasPrice,
						visasPrice
					)

					// currently we are not supporting per option taxes,
					// but in future we may support that's currently on the frontend for per-component we have synced taxes based on option 1
					// as because backend api needs taxes for each option

					const option1Taxes = options[0].taxes

					const isGstIncluded = options[0].gst_included

					const option1TaxesPayload = isGstIncluded
						? {
								...option1Taxes,
							}
						: undefined

					const option1Margins = options[0].margins
					const option1CostPricesPerComponent =
						options[0].cost_prices_per_component

					// Determine if we should upgrade v1 strategies to v2 based on flight feature and sync rounding among options usage
					const finalPricingStrategy = determineFinalPricingStrategy(
						pricing_strategy,
						isEditing,
						migrate_to_v2
					)
					function mergeRecords(
						data1: Record<string, Date[]>,
						data2: Record<string, Date[]>
					): Record<string, Date[]> {
						const result: Record<string, Date[]> = {}
						const allKeys = new Set([
							...Object.keys(data1),
							...Object.keys(data2),
						])
						for (const key of allKeys) {
							result[key] = [...(data1[key] || []), ...(data2[key] || [])]
						}
						return result
					}
					try {
						const hasClosedHotels = Object.keys(closeHotelData).length > 0
						const hasClosedServices = Object.keys(closeServiceData).length > 0
						if (hasClosedHotels || hasClosedServices) {
							const mergedCloseData =
								hasClosedHotels && hasClosedServices
									? mergeRecords(closeHotelData, closeServiceData)
									: hasClosedHotels
										? closeHotelData
										: closeServiceData
							const alertHeader =
								hasClosedHotels && hasClosedServices
									? "Some of the hotel, activities or services are closed. \n"
									: hasClosedHotels
										? "Some of the hotel rooms are sold out \n"
										: "Some of the activities or services are closed \n"
							const alertFooter =
								hasClosedHotels && hasClosedServices
									? "\n Please update the hotel, activities or services before bookings."
									: hasClosedHotels
										? "\n Please update the rooms before bookings."
										: "\n Please update the activities or services before bookings."
							const alertMessage = showClosedServicesAlert(mergedCloseData)
							alert(alertHeader + alertMessage + alertFooter)
							return
						}

						if (Object.keys(blacoutRoomsData).length > 0) {
							const alertHeader =
								"Some of the hotel rooms are blackout on given dates:\n"
							const alertMessage = showClosedServicesAlert(blacoutRoomsData)
							const alertFooter =
								"\nPlease update the rooms rate before bookings."
							alert(alertHeader + alertMessage + alertFooter)
							return
						}
						// Validate system rate changes - check for undefined given prices for system rate change detection between quotes during (In-Progress Trips)
						const validationPricesErrors = validateQuoteComponentsGivenPrices({
							cabs,
							hotel_options,
						})

						const totalPax = children.length + no_of_adults

						const quoteConfigErrors = validateQuoteConfiguration({
							cabs,
							missingCabTypeErrors,
							visas,
							totalPax,
						})

						const finalComplimentaryCount =
							finalPricingStrategy === "overall_v1" ||
							finalPricingStrategy === "overall_v2"
								? complimentary_count
									? complimentary_count
									: 0
								: 0

						if (Object.keys(quoteConfigErrors).length > 0) {
							throw createValidationError(
								quoteConfigErrors,
								"Quote configuration is incomplete. Please complete the required setup before saving."
							)
						}

						if (Object.keys(validationPricesErrors).length > 0) {
							throw createValidationError(
								validationPricesErrors,
								`Some services in this quote have prices that no longer match the latest system rates.\n\nThis usually happens when a new price sheet is uploaded after the quote was created. As a result, manually entered prices for the affected services have been removed.\n\nPlease review the highlighted services.`
							)
						}

						if (finalComplimentaryCount && finalComplimentaryCount < 0) {
							throw new Error("Total FOC should be a non-negative integer")
						}

						if (
							finalComplimentaryCount &&
							totalPax <= finalComplimentaryCount
						) {
							throw new Error(
								"Total FOC must be less than the number of guests"
							)
						}

						const data = {
							isAIItinerarybuilder,
							generationId,
							startDate,
							refId,
							days,
							no_of_adults,
							complimentary_count: finalComplimentaryCount,
							children,
							cabs,
							transportExtras,
							flights,
							otherExtras,
							cabPrice,
							transportExtrasPrice,
							flightPrice: flightCostPrice,
							otherExtrasPrice,
							comments,
							tax_name,
							gst_included: isGstIncluded,
							include_per_person_price,
							currency_pairs,
							costing_currency,
							given_currency,
							functional_currency,
							pricing_strategy: finalPricingStrategy,
							visas,
							visasPrice,
							minMarkupPercentage,

							// option specific data
							options: options.map(
								({
									given_price,
									unrounded_given_prices,
									per_component_cost_price_components,
									per_person_cost_price_components,
									cost_prices_per_person,
									cost_prices_per_component,
									given_prices_per_person,
									taxes,
									margins,
									per_person_allocation_not_possible,
									name,
									gst_included,
									flight_given_prices_by_id,
									...o
								}) => {
									if (!given_price) {
										throw Error("Please provide a selling price")
									}
									if (per_person_allocation_not_possible) {
										throw Error(
											`Please change the Pricing Strategy to Overall/Per-Component because Per-Person allocation is not possible for (${name}) option`
										)
									}
									const excludeAdultsCount =
										per_person_cost_price_components?.exclude_adults_count_in_cabs

									if (excludeAdultsCount && excludeAdultsCount < 0) {
										throw new Error(
											"Excluded transport adults cannot be negative. Enter a valid positive number"
										)
									}

									//@dev-note - we have to sync margins for all options expect margin for hotels as per current requirement but in future,
									//it can be possible if give user option to add indiviudal markups for options

									//dev-note- Currently, on the quotation page, markups are not synced for old(overall, per_person) stratgies,
									//so we have handle this
									//by sending their independent margins of per option for old strategies. The new stratgies (per_component, per_component_per_person) have the feature of sync markups expect hotels on the qutation form.

									const optionMargins =
										prepareMarginsPayloadForOptionDispatcher(
											margins,
											option1Margins,
											pricing_strategy,
											per_component_cost_price_components?.total || 0,
											enable_sync_rounding_among_options,
											cost_prices_per_component,
											include_per_person_price
												? cost_prices_per_person
												: undefined,
											option1CostPricesPerComponent
										)

									const isGstIncludedForOption = isGstIncluded

									const taxPerComponentAllocation =
										preparePerComponentTaxAllocationPayloadDispatcher(
											pricing_strategy,
											option1Taxes,
											taxes,
											cost_prices_per_component
										)

									const globalTaxAppliedOn =
										prepareGlobalTaxAppliedOnPayloadDispatcher(
											pricing_strategy,
											option1Taxes,
											taxPerComponentAllocation
										)

									const unroundedGivenPrices = unrounded_given_prices
										? {
												...unrounded_given_prices,
												per_person_allocation:
													include_per_person_price && given_price
														? unrounded_given_prices.per_person_allocation
														: undefined,
												per_component_allocation:
													prepareUnroundedGivenPricesPerComponentPayloadDispatcher(
														pricing_strategy,
														unrounded_given_prices
													),
											}
										: undefined

									return {
										...o,
										name: name,
										per_component_cost_price_components,
										cost_prices_per_component,
										per_person_cost_price_components: include_per_person_price
											? per_person_cost_price_components
											: undefined,
										cost_prices_per_person: include_per_person_price
											? cost_prices_per_person
											: undefined,
										given_price: given_price, // selling price
										unrounded_given_prices: unroundedGivenPrices, //given prices (unrounded) in given currency
										given_prices_per_person:
											include_per_person_price && given_price
												? given_prices_per_person
												: undefined,
										taxes: option1TaxesPayload
											? {
													...option1TaxesPayload,
													total: taxes.total,
													total_percentage:
														prepareTaxPercentagePayloadForOptionDispatcher(
															finalPricingStrategy,
															option1Taxes,
															taxes.total,
															per_component_cost_price_components?.total || 0,
															optionMargins?.total || 0,
															globalTaxAppliedOn
														),
													per_component_allocation: taxPerComponentAllocation,
													total_applied_on: globalTaxAppliedOn,
													editing_total:
														prepareTaxEditingTotalPayloadForOptionDispatcher(
															finalPricingStrategy,
															option1Taxes
														),
												}
											: undefined,
										gst_included: isGstIncludedForOption,
										margins: optionMargins,
									}
								}
							),
							missingCabTypeErrors,
							hotel_options,
							ai_autofilled: refId ? 0 : ai_autofilled,
						}
						const quote = await XHR(xhr).saveQuote(tripId, data)
						refreshTrip(tripId.toString())
						onSuccess(quote)
					} catch (e) {
						const error = e as XHRError

						setSubmitError(error)

						if (typeof window !== "undefined" && error.message) {
							window.alert(error.message)
						}

						const document = containerRef.current
						if (document) {
							submitAllForms(document)
							setTimeout(() => {
								showErrorsFromForms(document, error.message)

								const errorDisplay = document.querySelector(
									"[data-error-display]"
								)

								if (
									errorDisplay &&
									typeof errorDisplay.scrollIntoView === "function"
								) {
									errorDisplay.scrollIntoView({
										behavior: "smooth",
										block: "center",
									})
								}
							}, 300)
						}
					} finally {
						//removing the localstorage for the ai - parsing
						try {
							localStorage.removeItem(`ai-feedback-form-${job_uuid}`)
						} catch (error) {
							console.log(error)
						}
					}
				}
			)}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ form, submitting }) => {
				return (
					<Box data-testid="quote_form" ref={containerRef}>
						<TripComments comment={trip.latest_comment || null} />
						<GetFieldValue<string> name="costing_currency">
							{({ value: costingCurrency }) => (
								<BasicDetails
									trip={trip}
									onChange={onChangeBasicInfo}
									baseStartDate={baseStartDate}
									baseNoOfAdults={baseNoOfAdults}
									baseChildren={baseChildren}
									tripQuote={tripQuote}
									defaultCostingCurrency={costingCurrency}
									isEditingQuote={isEditing}
									afterConfirmedQuote={afterConfirmedQuote}
									isEditForbidden={isAIItinerarybuilder}
								/>
							)}
						</GetFieldValue>
						{trip.on_hold_at || trip.converted_at ? null : (
							<GetFieldValue<INewQuoteState["options"]> name="options">
								{({ value: options }) => (
									<Container fluid marginBottom="8">
										<Inline
											gap="4"
											paddingY="2"
											paddingX="4"
											bgColor="default"
											borderWidth="1"
											rounded="md"
										>
											<Stack gap="1">
												<Inline gap="1" fontSize="md" fontWeight="semibold">
													Package Types/Categories:{" "}
													{pluralize("Option", options.length, true)}
												</Inline>
												{options.length > 1 ? (
													<Text>
														{options
															.map((o, index) => `${index + 1}: ${o.name}`)
															.join(" • ")}
													</Text>
												) : null}
											</Stack>
											<Component initialState={false}>
												{({ state, setState }) => (
													<>
														<Button size="sm" onClick={() => setState(true)}>
															<Icons.Pencil />
														</Button>
														<Dialog
															open={state}
															onClose={() => setState(false)}
															title="Package Types/Options"
															sm
														>
															<Dialog.Body>
																<PackageOptionsForm
																	initialOptions={options}
																	hotelOptions={
																		form.getState().values.hotel_options
																	}
																	onCancel={() => setState(false)}
																	onSubmit={async ({
																		options,
																		hotelOptions,
																	}) => {
																		form.batch(() => {
																			form.change("options", options)
																			// reset the hotel options as they are using refs
																			form.change("hotel_options", [])
																		})
																		showSnackbar(
																			"Package Types/Options updated."
																		)
																		// and then set the hotel options
																		setTimeout(() => {
																			form.change(
																				"hotel_options",
																				hotelOptions.length
																					? hotelOptions
																					: [
																							addKeyToFieldArrayItem({
																								option_index: 0,
																								hotels: [],
																								hotelExtras: [],
																								hotelPrice: [],
																								hotelExtrasPrice: [],
																							}),
																						]
																			)
																		}, 1000)
																		setState(false)
																	}}
																/>
															</Dialog.Body>
														</Dialog>
													</>
												)}
											</Component>
										</Inline>
									</Container>
								)}
							</GetFieldValue>
						)}
						<GetFieldValue<Date> name="startDate">
							{({ value: startDate }) => (
								<GetFieldValue<number> name="days">
									{({ value: days }) => (
										<DeferRender>
											<Stack gap="8">
												<Box paddingY="4" bgColor="default" id="quote_hotels">
													<NewQuoteHotelsOptionsArray
														noOfAdults={no_of_adults}
														children={children}
														startDate={bookingFrom}
														endDate={bookingTo}
														tripDestinations={tripDestinations}
														initialValues={initialNewQuoteValues.hotel_options}
													/>
												</Box>
												<Container fluid paddingY="4" bgColor="default">
													<NewQuoteCabs
														startDate={bookingFrom}
														endDate={bookingTo}
														noOfAdults={no_of_adults}
														children={children}
														tripDestinations={tripDestinations}
														cabsInitialValue={initialNewQuoteValues.cabs}
														extrasInitialValue={
															initialNewQuoteValues.transportExtras
														}
													/>
													<TotalCabsAndActivitiesPrice />
												</Container>

												<Container fluid paddingY="4" bgColor="default">
													<NewQuoteFlights
														startDate={bookingFrom}
														endDate={bookingTo}
														tripDestinations={tripDestinations}
														flights={
															form.getFieldState("flights")?.value ||
															initialNewQuoteValues.flights
														}
														no_of_adults={no_of_adults}
														children={children}
														onChange={(services, price, costPrice) => {
															form.batch(() => {
																form.change("flights", services)
																form.change("flightPrice", price)
																form.change("flightCostPrice", costPrice)
															})
														}}
														openFlightDetailForm={
															!!job_uuid && !!hasPackageUploader
														}
													/>
													<TotalFlightsPrice />
												</Container>
												<NewQuoteVisa
													startDate={bookingFrom}
													noOfAdults={no_of_adults}
													childDetails={children}
													tripDestinations={tripDestinations}
													visaInitialValue={initialNewQuoteValues.visas}
													isEditMode={isEditing}
												/>
												<Container fluid paddingY="4" bgColor="default">
													<NewQuoteOtherExtras
														startDate={bookingFrom}
														endDate={bookingTo}
														tripDestinations={tripDestinations}
														otherExtras={
															form.getFieldState("otherExtras")?.value ||
															initialNewQuoteValues.otherExtras
														}
														onChange={(services, price) => {
															form.batch(() => {
																form.change("otherExtras", services)
																form.change("otherExtrasPrice", price)
															})
														}}
													/>
													<TotalOtherExtrasPrice />
												</Container>
												<Container fluid paddingY="4" bgColor="default">
													<TextAreaInputField
														label="Any internal comments for this quote"
														secondaryLabel="optional"
														name="comments"
														placeholder="Any internal comments regarding customer request or anything special about this quote or anything else..."
													/>
												</Container>
											</Stack>
											<DeferRender by={5000}>
												<ErrorBoundary>
													<PreviewData
														startDate={startDate}
														days={days}
														adults={no_of_adults}
														children={children}
													/>
												</ErrorBoundary>
												<DeferRender by={3000}>
													<Container fluid paddingY="6" bgColor="default">
														<ErrorBoundary>
															<CostingPricesBifurcation
																editingExistingQuote={isEditing}
																canModifyQuoteExchangeRate={
																	canModifyQuoteExchangeRate
																}
															/>
														</ErrorBoundary>
													</Container>
													<Container
														bgColor="default"
														paddingY="6"
														borderTopWidth="1"
													>
														<Stack
															borderWidth="2"
															borderColor="primary"
															rounded="md"
															padding="4"
															bgColor="accent"
															gap="4"
														>
															<Inline gap="2" as="header">
																<Box>
																	<StepIcon>
																		<Icons.BankNotes size="6" />
																	</StepIcon>
																</Box>
																<Stack>
																	<Heading as="h4" id="quote_selling_price">
																		Preview Final Package Price
																	</Heading>
																	<Text color="muted">
																		Here are the final prices for this quote.
																	</Text>
																</Stack>
															</Inline>
															<GetFieldValue<
																INewQuoteState["options"]
															> name="options">
																{({ value: options }) => (
																	<Stack gap="6">
																		{options.map((o, index, arr) => (
																			<Grid
																				key={o.__id}
																				gap="4"
																				position="relative"
																			>
																				{arr.length > 1 ? (
																					<Col
																						style={{ position: "sticky" }}
																						xs={12}
																						sm={4}
																						md={3}
																						top="0"
																						zIndex="10"
																					>
																						<Box
																							position="sticky"
																							top="0"
																							zIndex="10"
																							bgColor="emphasis"
																							color="on_emphasis"
																							paddingY="2"
																							paddingX="4"
																							rounded="md"
																						>
																							<Text fontSize="sm">
																								Option {index + 1}
																							</Text>
																							<Heading fontSize="md">
																								{o.name}
																							</Heading>
																						</Box>
																					</Col>
																				) : null}
																				<Col>
																					<Stack gap="2">
																						<PackagePricePreviewForOption
																							optionIndex={index}
																						/>

																						<GetFieldValue<
																							INewQuoteState["options"][number]["given_price_customer_remarks"]
																						>
																							name={`options[${index}].given_price_customer_remarks`}
																						>
																							{({ value: customer_remarks }) =>
																								customer_remarks ? (
																									<Stack
																										fontSize="sm"
																										color="success"
																										maxWidth="3xl"
																										gap="2"
																									>
																										Customer Remarks:{" "}
																										<Markdown>
																											{customer_remarks}
																										</Markdown>
																									</Stack>
																								) : null
																							}
																						</GetFieldValue>
																					</Stack>
																				</Col>
																			</Grid>
																		))}
																	</Stack>
																)}
															</GetFieldValue>
														</Stack>
														<Stack gap="4">
															<Divider sm />

															{/* Custom structured error display for API errors / Custom Frontend ONLY Errors */}
															<NewQuoteErrorDisplay
																error={submitError}
																onDismiss={() => setSubmitError(null)}
															/>

															<GetFieldValue<
																INewQuoteState["fetching_prices"]
															> name="fetching_prices">
																{({ value: isFetchingPrices }) => (
																	<Stack
																		gap="4"
																		cursor={
																			isFetchingPrices ? "wait" : undefined
																		}
																	>
																		{isFetchingPrices ? (
																			<Alert
																				status="warning"
																				title="Please wait... Some configuration is pending."
																			>
																				<Text>
																					System rates are being retrieved.
																					Please wait or preview the quote
																					details.
																				</Text>
																			</Alert>
																		) : null}
																		<Inline>
																			<Inline gap="4">
																				<Button
																					disabled={
																						submitting || isFetchingPrices
																					}
																					type="button"
																					level="primary"
																					status="primary"
																					onClick={() => {
																						form.submit()
																					}}
																					size="lg"
																				>
																					{submitting
																						? "Saving..."
																						: "Save Quote"}
																				</Button>
																				<Button
																					onClick={onCancel}
																					size="lg"
																					disabled={submitting}
																				>
																					Cancel
																				</Button>
																			</Inline>
																			{job_uuid && (
																				<FeatureFlag flag="trip_quote_parsing">
																					<AiParsingFeedbackDialog
																						tripId={tripId}
																						enabled={
																							!!job_uuid && hasPackageUploader
																						}
																					>
																						{({ onClick }) => (
																							<Button
																								onClick={onClick}
																								size="sm"
																								level="tertiary"
																							>
																								<Inline
																									gap={"1"}
																									justifyContent="center"
																									alignItems="center"
																									color="primary"
																									textDecoration={"underline"}
																								>
																									<Icons.SparklesSolid />
																									Give Trip Quote Parsing
																									Feedback
																								</Inline>
																							</Button>
																						)}
																					</AiParsingFeedbackDialog>
																				</FeatureFlag>
																			)}
																		</Inline>
																	</Stack>
																)}
															</GetFieldValue>
														</Stack>
													</Container>
												</DeferRender>
											</DeferRender>
										</DeferRender>
									)}
								</GetFieldValue>
							)}
						</GetFieldValue>
						<SyncIsFetchingPricesField />
					</Box>
				)
			}}
		</Form>
	)
}

function BasicDetails({
	onChange,
	trip,
	tripQuote,
	baseStartDate,
	baseNoOfAdults,
	baseChildren,
	defaultCostingCurrency,
	isEditingQuote,
	afterConfirmedQuote,
	isEditForbidden = false,
}: {
	trip: ITrip
	tripQuote?: ITripQuote
	baseStartDate: Date
	baseNoOfAdults: number
	baseChildren: TChildrenArray
	onChange?: React.ComponentProps<typeof EditBasicInfo>["onChange"]
	defaultCostingCurrency: string
	isEditingQuote: boolean
	afterConfirmedQuote: boolean
	isEditForbidden?: boolean
}) {
	const tenantCurrencies = useCurrenciesOfTenant()
	const { value: startDate } = useFieldValue<Date>("startDate")
	const { value: days } = useFieldValue<number>("days")
	const { value: no_of_adults } = useFieldValue<number>("no_of_adults")
	const { value: children } = useFieldValue<TChildrenArray | undefined>(
		"children"
	)
	const { value: complimentary_count } = useFieldValue<number>(
		"complimentary_count"
	)
	const { value: pricingStrategy } =
		useFieldValue<TGivenQuotePricingStrategy>("pricing_strategy")
	const form = useForm<INewQuoteState>()
	const mismatchBasicDetails = useMemo(() => {
		const diff = getTripQuoteDetailsDiff(trip, {
			start_date: startDate.toISOString(),
			days,
			no_of_adults,
			children: children || [],
			complimentary_count: complimentary_count,
		})
		return diff
	}, [trip, startDate, days, no_of_adults, children, complimentary_count])

	const handleBasicDetailsChange: React.ComponentProps<
		typeof EditBasicInfo
	>["onChange"] = useCallback(
		(newValues) => {
			// Reset the form if we get a change in start_date or days
			if (
				toISOString(startOf(startDate, "day")) !==
					toISOString(startOf(newValues.startDate, "day")) ||
				days !== newValues.days
			) {
				// reset the form
				const initialQuote = getQuoteInitialServicesValues({
					tripQuote,
					baseStartDate,
					baseNoOfAdults,
					baseChildren,
					startDate: newValues.startDate,
					days: newValues.days,
					noOfAdults: newValues.adults,
					children: newValues.children || [],
					complimentaryCount: newValues.complimentary_count || 0,
					defaultCostingCurrency,
					isEditing: isEditingQuote,
					afterConfirmedQuote,
					hasMultiQuoteFeature: true,
					tenantCurrencies,
				})
				form.initialize((values) => {
					return {
						...values,
						...initialQuote,
						startDate: newValues.startDate,
						days: newValues.days,
						no_of_adults: newValues.adults,
						children: newValues.children,
						complimentary_count: newValues.complimentary_count,
					}
				})
			} else {
				// update the no_of_adults,children and complimentary_count values
				form.batch(() => {
					form.change("no_of_adults", newValues.adults)
					form.change("children", newValues.children)
					form.change("complimentary_count", newValues.complimentary_count)
				})
			}
			onChange?.({
				startDate: newValues.startDate,
				days: newValues.days,
				adults: newValues.adults,
				children: newValues.children,
				complimentary_count: newValues.complimentary_count,
			})
		},
		[
			onChange,
			form,
			startDate,
			days,
			baseStartDate,
			tripQuote,
			baseChildren,
			baseNoOfAdults,
			defaultCostingCurrency,
			isEditingQuote,
			afterConfirmedQuote,
			tenantCurrencies,
		]
	)
	const { destinations, profile } = trip

	return (
		<Container fluid paddingY="4">
			<Stack gap="3">
				<Stack gap="px">
					<Heading as="h3">Basic Details</Heading>
					<Text color="muted">
						Please review basic details for this quote. You can edit these
						details to provide a quote with different configuration, without
						changing the trip details.
					</Text>
				</Stack>
				<Inline collapseBelow="md" gap={{ xs: "4", md: "8" }}>
					<Box>
						<Grid gap={{ xs: "4", md: "6" }}>
							<Col>
								<KeyValueDetails label="Destination">
									{destinations.map((d) => d.name).join(", ")}
								</KeyValueDetails>
							</Col>
							<Col>
								<KeyValueDetails label="Start Date">
									{formatDate(startDate, "DD MMM, YYYY")}
								</KeyValueDetails>
							</Col>
							<Col>
								<KeyValueDetails label="Duration">
									{days > 1
										? `${pluralize("Nights", days - 1, true)}, ${pluralize(
												"Day",
												days,
												true
											)}`
										: pluralize("Day", days, true)}
								</KeyValueDetails>
							</Col>
							<Col>
								<KeyValueDetails label="Pax">
									{pluralize("Adult", no_of_adults, true)}
									{children?.length ? (
										<span>
											{" "}
											with {pluralize("Children", children.length, true)} (
											{childrenToQueryParams(children)})
										</span>
									) : null}
									{pricingStrategy === "overall_v1" ||
									pricingStrategy === "overall_v2" ? (
										complimentary_count > 0 ? (
											<span> ({complimentary_count} FOC)</span>
										) : null
									) : null}
								</KeyValueDetails>
							</Col>
						</Grid>
					</Box>
					{!isEditForbidden && onChange ? (
						<Stack gap="1">
							<Box>
								<EditBasicInfo
									destinations={destinations}
									startDate={startDate}
									days={days}
									adults={no_of_adults}
									children={children}
									onChange={handleBasicDetailsChange}
									complimentaryCount={complimentary_count}
									pricingStrategy={pricingStrategy}
								/>
							</Box>
						</Stack>
					) : null}
				</Inline>
				{mismatchBasicDetails ? (
					<Alert status="warning" title="Mismatch details with trip details">
						<Stack gap="2">
							<Text>
								These basic details do not match with the trip's basic details.{" "}
								<b>
									Please ignore if you are providing a quote with different
									details.
								</b>
							</Text>
							<Box>
								<Button
									size="sm"
									status="warning"
									onClick={() => {
										handleBasicDetailsChange({
											startDate: localOrUtcTimestampToLocalDate(
												trip.start_date_local,
												trip.start_date
											),
											days: trip.days,
											adults: trip.no_of_adults,
											children: trip.children,
											complimentary_count: trip.complimentary_count,
										})
									}}
								>
									Match with Trip Details
								</Button>
							</Box>
						</Stack>
					</Alert>
				) : null}
			</Stack>
			{profile ? (
				<>
					<Divider sm />
					<Stack gap="3">
						<Stack gap="px">
							<Heading as="h3">Primary Tourist Profile Details</Heading>
							<Text color="muted">
								Please review primary tourist profile details for this quote.
							</Text>
						</Stack>
						<Inline collapseBelow="md" gap={{ xs: "4", md: "8" }}>
							<Inline gap="3" alignItems="center">
								<Stack>
									<Inline
										size="12"
										alignItems="center"
										justifyContent="center"
										rounded="full"
										boxShadow="base"
										bgColor="primary"
										fontWeight="semibold"
										borderWidth="1"
										color="primary"
										letterSpacing="wider"
									>
										<Icons.User size="6" />
									</Inline>
								</Stack>
								<Stack gap="1">
									<Text fontWeight="semibold" fontSize="md">
										{profile.name}
									</Text>
									<Box fontSize="sm" color="muted">
										{joinAttributes(
											[
												profile.phone_numbers?.length,
												<PhoneNumber value={profile.phone_numbers} />,
											],
											[profile.email, <Email value={profile.email} />]
										)}
									</Box>
								</Stack>
							</Inline>
							<Inline gap="10" flexWrap="wrap" justifyContent="start">
								<KeyValueDetails label="Meal Pref.">
									{profile.meal_preference ? (
										profile.meal_preference
									) : (
										<Text color="muted">N/A</Text>
									)}
								</KeyValueDetails>
								<KeyValueDetails label="Stay Pref.">
									{profile.stay_preference ? (
										profile.stay_preference
									) : (
										<Text color="muted">N/A</Text>
									)}
								</KeyValueDetails>
								<KeyValueDetails label="Smoking">
									{profile.is_smoker === null ? (
										<Text color="muted">N/A</Text>
									) : profile.is_smoker ? (
										"Yes"
									) : (
										"No"
									)}
								</KeyValueDetails>
								<KeyValueDetails label="Origin City/State">
									{profile.address ? (
										profile.address.location?.full_name
									) : (
										<Text color="muted">N/A</Text>
									)}
								</KeyValueDetails>
							</Inline>
						</Inline>
					</Stack>
				</>
			) : (
				<></>
			)}
		</Container>
	)
}

function getNewOptions(
	{
		options,
		hotelOptions,
	}: {
		options: INewQuoteState["options"]
		hotelOptions: INewQuoteState["hotel_options"]
	},
	params: { options: INewQuoteState["options"] }
) {
	const oldOptionsById = collect(options).keyBy((_, index) => String(index))
	const oldOptionNewOptionIdMapping: {
		[oldId: string]: number
	} = {}
	const newOptions: typeof options = params.options.map((o, index) => {
		const oldOption = oldOptionsById[String(o.id)]
		const newId = index
		if (oldOption) {
			oldOptionNewOptionIdMapping[o.id] = newId
		}
		return {
			...(oldOption || options[0]),
			...o,
			id: newId,
		}
	})
	const newHotelOptions = hotelOptions
		.filter((ho) => oldOptionNewOptionIdMapping[ho.option_index] !== undefined)
		.map((ho) => ({
			...ho,
			option_index: oldOptionNewOptionIdMapping[ho.option_index], // update the id
		}))

	return {
		options: newOptions,
		hotelOptions: newHotelOptions,
	}
}

const MAX_QUOTE_OPTIONS = 6

function PackageOptionsForm({
	initialOptions: options,
	hotelOptions,
	onCancel,
	onSubmit,
}: {
	initialOptions: INewQuoteState["options"]
	hotelOptions: INewQuoteState["hotel_options"]
	onCancel: () => void
	onSubmit: (payload: {
		options: INewQuoteState["options"]
		hotelOptions: INewQuoteState["hotel_options"]
	}) => Promise<void>
}) {
	type TPackageOptionFormData = {
		options: Array<{
			__id: number
			/**
			 * ID for the option,
			 * During editing, we will set it to the index of an option
			 * and on add new, we will generate a new id. this way we
			 * will track the changes in the existing options
			 */
			id: number
			name: string
			gst_included: boolean
			taxes: INewQuoteState["options"][number]["taxes"]
		}>
	}
	const initialValues: TPackageOptionFormData = {
		options: options.map((o, index) =>
			addKeyToFieldArrayItem({
				id: index,
				name: o.name,
				gst_included: o.gst_included,
				taxes: o.taxes,
			})
		),
	}
	return (
		<Form<TPackageOptionFormData>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					options: Validator.array()
						.min(1, "Options should be atleast one")
						.max(MAX_QUOTE_OPTIONS, "Options can not be greater than four")
						.of(
							Validator.object().shape({
								name: Validator.string()
									.required("Please provide a name/title for the option")
									.max(50, "Please use 50 or fewer characters"),
							})
						),
				})
			)}
			onSubmit={withServerErrors(async (params) => {
				const options_count = params.options.length
				if (
					options_count < options.length &&
					!window.confirm(
						`Are you sure you want to reduce the options from ${options.length} to ${options_count}? Data associated with removed options will be removed.`
					)
				) {
					return
				}
				await onSubmit(
					getNewOptions({ options, hotelOptions }, { options: params.options })
				)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Stack gap="4">
						<FieldArray<
							TPackageOptionFormData["options"][number]
						> name="options">
							{({ fields }) => (
								<Stack gap="2">
									<Table
										headers={["#", "Name", ""]}
										bordered
										hover
										alignCols={{ 2: "right" }}
										rows={fields.map((name, index) => [
											<Box>{index + 1}</Box>,
											<TextInputField
												name={`${name}.name`}
												type="text"
												placeholder="e.g. Premium Package"
											/>,
											<Button
												onClick={() => fields.remove(index)}
												level="tertiary"
											>
												<Icons.Cancel />
											</Button>,
										])}
									/>
									{(fields.length || 0) < MAX_QUOTE_OPTIONS ? (
										<Box>
											<Button
												size="sm"
												level="secondary"
												onClick={() =>
													fields.push(
														addKeyToFieldArrayItem({
															id: Math.random() * 1000, // generate a new ID
															name: "",
															gst_included: true,
															taxes: {
																editing_total: 1,
																total_percentage: 0,
																total: 0,
																total_applied_on: "cost_plus_markup",

																per_component_allocation: {
																	hotels_applied_on: "cost_plus_markup",
																	hotels_percentage: 0,
																	hotels: 0,
																	cabs_applied_on: "cost_plus_markup",
																	cabs_percentage: 0,
																	cabs: 0,
																	activities_applied_on: "cost_plus_markup",
																	activities_percentage: 0,
																	activities: 0,
																	extras_applied_on: "cost_plus_markup",
																	extras_percentage: 0,
																	extras: 0,
																	flights_applied_on: "cost_plus_markup",
																	flights_percentage: 0,
																	flights: 0,
																	visas_applied_on: "cost_plus_markup",
																	visas_percentage: 0,
																	visas: 0,
																},
															},
														})
													)
												}
											>
												Add More
											</Button>
										</Box>
									) : null}
								</Stack>
							)}
						</FieldArray>
						<Divider marginY="0" />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								Save
							</Button>
							<Button onClick={() => onCancel()} disabled={submitting}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function NewQuoteHotelsOptionsArray({
	initialValues,
	startDate,
	...props
}: {
	initialValues: INewQuoteState["hotel_options"]
} & Pick<
	React.ComponentProps<typeof NewQuoteHotelsOption>,
	"noOfAdults" | "children" | "startDate" | "endDate" | "tripDestinations"
>) {
	const form = useForm<INewQuoteState>()
	return (
		<Stack gap="4">
			<Container fluid>
				<Inline as="header" gap="2">
					<Box>
						<StepIcon>
							<Icons.Bed size="6" />
						</StepIcon>
					</Box>
					<Stack gap="1">
						<Heading as="h4">Hotels</Heading>
						<Text color="muted">
							Please add hotels details (if included in package) with services
							provided for each hotels and the selling cost price.
						</Text>
						<Text fontSize="sm" color="muted">
							<Icons.LightBulb /> Tip: To speed up the process of adding
							multiple hotels, use <strong>Next Night</strong> or{" "}
							<strong>Duplicate</strong> actions.
						</Text>
					</Stack>
				</Inline>
			</Container>
			<FieldArray<INewQuoteState["hotel_options"][number]> name="hotel_options">
				{({ fields }) => (
					<Stack gap="4" position="relative">
						{fields.map(function (name, index) {
							const key = getFieldArrayItemKey(form as never, name)
							return (
								<Stack gap="4" key={key}>
									<GetFieldValue<INewQuoteState["options"]> name="options">
										{({ value: quoteOptions }) =>
											quoteOptions.length > 1 ? (
												<Box
													paddingY="2"
													bgColor="emphasis"
													position="sticky"
													zIndex="10"
													top="0"
												>
													<Container fluid>
														<Inline
															gap="4"
															alignItems="center"
															justifyContent="between"
														>
															<Inline>
																<SelectInputField name={`${name}.option_index`}>
																	{quoteOptions.map((o, index) => (
																		<option key={o.__id} value={index}>
																			Option {index + 1}: {o.name}
																		</option>
																	))}
																</SelectInputField>
															</Inline>
															<Inline gap="4">
																<GetFieldValue<
																	INewQuoteState["hotel_options"][number]
																>
																	name={name}
																>
																	{({ value }) =>
																		value.hotels?.length ||
																		value.hotelExtras?.length ? (
																			<Component initialState={false}>
																				{({ state, setState }) => (
																					<>
																						<Button
																							size="sm"
																							onClick={() => {
																								setState(true)
																							}}
																						>
																							<Icons.Duplicate /> Copy
																						</Button>
																						<Dialog
																							title="Copy Hotels for new Option"
																							open={state}
																							onClose={() => setState(false)}
																						>
																							<Dialog.Body>
																								<DuplicateHotelsToNewOption
																									startDate={startDate}
																									hotelOption={value}
																									quoteOptions={quoteOptions}
																									tripDestinations={
																										props.tripDestinations
																									}
																									onCancel={() => {
																										setState(false)
																									}}
																									onSave={(hotelOption) => {
																										const existingHotelOptions =
																											form.getState().values
																												.hotel_options
																										const existingCopy =
																											existingHotelOptions.find(
																												(o) =>
																													String(
																														o.option_index
																													) ===
																													String(
																														hotelOption.option_index
																													)
																											)
																										if (!existingCopy) {
																											// simply push it to the end
																											fields.push(hotelOption)
																										} else {
																											form.change(
																												"hotel_options",
																												[]
																											)
																											setTimeout(() => {
																												// replace the existing one(s)
																												form.change(
																													"hotel_options",
																													existingHotelOptions.map(
																														(h) =>
																															String(
																																h.option_index
																															) ===
																															String(
																																hotelOption.option_index
																															)
																																? hotelOption
																																: h
																													)
																												)
																											}, 300)
																										}
																										setState(false)
																									}}
																								/>
																							</Dialog.Body>
																						</Dialog>
																					</>
																				)}
																			</Component>
																		) : null
																	}
																</GetFieldValue>
																{Number(fields.length || 0) > 1 ? (
																	<Button
																		size="sm"
																		onClick={() => {
																			if (
																				window.confirm(
																					"Are you sure you want to remove this hotel option?"
																				)
																			) {
																				const hotel_options =
																					form.getState().values.hotel_options
																				const hotel_option =
																					hotel_options[index]
																				form.change("hotel_options", [])
																				setTimeout(() => {
																					const newData = getNewOptions(
																						{
																							options: quoteOptions,
																							hotelOptions: hotel_options,
																						},
																						{
																							// remove the option option_index
																							options: quoteOptions.filter(
																								(_, index) =>
																									String(index) !==
																									String(
																										hotel_option.option_index
																									)
																							),
																						}
																					)
																					// now set the data back
																					form.batch(() => {
																						form.change(
																							"options",
																							newData.options
																						)
																						form.change(
																							"hotel_options",
																							newData.hotelOptions
																						)
																					})
																				}, 300)
																			}
																		}}
																	>
																		<Icons.Cancel />
																	</Button>
																) : null}
															</Inline>
														</Inline>
													</Container>
												</Box>
											) : null
										}
									</GetFieldValue>
									<Container fluid>
										<NewQuoteHotelsOption
											{...props}
											startDate={startDate}
											optionName={name}
											hotelsInitialValue={initialValues[index]?.hotels}
											hotelExtrasInitialValue={
												initialValues[index]?.hotelExtras
											}
										/>
									</Container>
								</Stack>
							)
						})}
						<GetFieldValue<INewQuoteState["options"]> name="options">
							{({ value: quoteOptions }) =>
								fields.length !== quoteOptions.length ? (
									<DeferRender by={500}>
										<Container>
											<Alert
												status="error"
												title="Some options are missing hotels details!!"
											>
												Please use the copy/duplicate [<Icons.Duplicate />]
												button from an option to add hotels for other option(s).
											</Alert>
										</Container>
									</DeferRender>
								) : null
							}
						</GetFieldValue>
					</Stack>
				)}
			</FieldArray>
		</Stack>
	)
}

function DuplicateHotelsToNewOption({
	startDate,
	hotelOption,
	quoteOptions,
	onSave,
	onCancel,
	tripDestinations,
}: {
	startDate: string
	hotelOption: INewQuoteState["hotel_options"][number]
	quoteOptions: INewQuoteState["options"]
	tripDestinations: Array<TTripDestination>
	onCancel: () => void
	onSave: (newOption: INewQuoteState["hotel_options"][number]) => void
}) {
	const uniqueQuoteHotelsIndexedByHotel = useMemo(() => {
		return collect(hotelOption.hotels)
			.groupBy((h) => h.hotel.id)
			.toArray()
	}, [hotelOption])

	const copyFromOption = quoteOptions[Number(hotelOption.option_index)]

	const commonMealPlans = useMemo(() => {
		const hotels = uniqueQuoteHotelsIndexedByHotel.map((h) => h[0].hotel)
		if (!hotels.length) return []
		const firstHotelMealPlans = hotels[0].meal_plans
		return firstHotelMealPlans.filter((mealPlan) =>
			hotels.every((hotel) =>
				hotel.meal_plans.find((m) => m.id === mealPlan.id)
			)
		)
	}, [uniqueQuoteHotelsIndexedByHotel])
	return (
		<Form<{
			option_index?: string
			change_type?: "meal_plan" | "hotels" | "none"
			meal_plan?: IHotelMealPlan
			hotels_replacements?: { [hotelId: string]: IHotel | undefined }
		}>
			initialValues={{
				option_index: undefined,
				change_type: "hotels",
				meal_plan: undefined,
				hotels_replacements: undefined,
			}}
			validate={validateFormValues(
				Validator.object().shape({
					option_index: EmptyNumberValidator().required(
						"Please select the Target Option to copy to."
					),
				})
			)}
			onSubmit={withServerErrors(
				async ({ meal_plan, hotels_replacements, ...values }) => {
					const option_index = Number(String(values.option_index))
					if (String(option_index) === String(hotelOption.option_index)) {
						throw new Error("Please change the Target Option to copy.")
					}
					const newHotelOption: INewQuoteState["hotel_options"][number] =
						addKeyToFieldArrayItem({
							...hotelOption,
							option_index,
						})
					switch (values.change_type) {
						case "meal_plan":
							if (meal_plan) {
								newHotelOption.hotels = newHotelOption.hotels.map((h) => ({
									...h,
									meal_plan,
									date_wise_prices: h.date_wise_prices.map((p) => ({
										...p,
										edited_given_price: false,
									})),
									comments: "",
									similar_hotel_options: h.similar_hotel_options?.map((h) => ({
										...h,
										meal_plan:
											h.hotel.meal_plans.find((m) => m.id === meal_plan.id) ||
											h.hotel.meal_plans[0],
										comments: "",
										date_wise_prices: h.date_wise_prices.map((p) => ({
											...p,
											given_price: 0,
											edited_given_price: false,
										})),
									})),
								}))
							} else {
								throw new Error("Please select a meal plan for Target Option.")
							}
							break
						case "hotels":
							newHotelOption.hotels = newHotelOption.hotels.map((h) => {
								const mappedHotel = hotels_replacements?.[h.hotel.id]
								if (!mappedHotel) return h
								const aweb =
									mappedHotel.room_types[0].allowed_adults_with_extra_bed *
										(h.no_of_rooms || 0) >=
									h.adults_with_extra_bed
										? h.adults_with_extra_bed
										: mappedHotel.room_types[0].allowed_adults_with_extra_bed *
											(h.no_of_rooms || 0)
								const cweb =
									mappedHotel.room_types[0].allowed_children_with_extra_bed *
										(h.no_of_rooms || 0) >=
									h.children_with_extra_bed
										? h.children_with_extra_bed
										: mappedHotel.room_types[0]
												.allowed_children_with_extra_bed * (h.no_of_rooms || 0)
								return {
									...h,
									adults_with_extra_bed: aweb,
									children_with_extra_bed: cweb,
									hotel: mappedHotel,
									room_type: mappedHotel.room_types[0], // base category
									meal_plan:
										mappedHotel.meal_plans.find(
											(m) => m.id === h.meal_plan.id
										) || mappedHotel.meal_plans[0],
									comments: "",
									date_wise_prices: h.date_wise_prices.map((p) => ({
										...p,
										edited_given_price: false,
									})),
									similar_hotel_options: undefined,
								}
							})
							newHotelOption.hotelExtras = newHotelOption.hotelExtras.map(
								(h) => {
									const mappedHotel = hotels_replacements?.[h.hotel.id]
									if (!mappedHotel) return h
									return {
										...h,
										hotel: mappedHotel,
									}
								}
							)
							break
						default:
							break
					}
					onSave(newHotelOption)
				}
			)}
			subscription={{
				submitting: true,
			}}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Stack gap="4">
						<Inline
							gap="2"
							padding="4"
							borderWidth="1"
							bgColor="subtle"
							rounded="md"
						>
							<Heading fontSize="md">Source Option:</Heading>
							<Text fontSize="md" fontWeight="semibold">
								{copyFromOption.name}
							</Text>
						</Inline>
						<Divider sm />
						<Inline
							gap="4"
							collapseBelow="sm"
							padding="4"
							borderWidth="1"
							rounded="md"
							bgColor="primary"
							borderColor="primary"
						>
							<Stack gap="1">
								<Heading fontSize="md">Target Option</Heading>
								<Text color="muted">The option where you want to copy to.</Text>
							</Stack>
							<Inline>
								<SelectInputField name="option_index">
									<option>---</option>
									{quoteOptions.map((o, index) => (
										<option
											key={o.id}
											value={index}
											disabled={
												index.toString() === hotelOption.option_index.toString()
											}
										>
											Option {index + 1}: {o.name}
										</option>
									))}
								</SelectInputField>
							</Inline>
						</Inline>
						<Stack borderWidth="1" rounded="md">
							<Box paddingX="4" paddingY="2" bgColor="inset">
								<Text fontWeight="semibold">
									What would you like to change in Hotels for the Target Option
									?
								</Text>
							</Box>
							<Stack padding="4" gap="4" borderTopWidth="1">
								<RadioInputField
									name="change_type"
									value="hotels"
									label="Hotels"
									help="Add replacements for hotels to copy to Target Option"
								/>
								<GetFieldValue<string> name="change_type">
									{({ value }) =>
										value !== "hotels" ? null : (
											<Table
												headers={["Exisiting Hotel", "Replacement Hotel"]}
												bordered
												hover
												rows={uniqueQuoteHotelsIndexedByHotel.map(
													(quoteHotels) => {
														const hotel = quoteHotels[0].hotel
														return [
															<Stack>
																<Text fontWeight="semibold">{hotel.name}</Text>
																<Text color="muted">
																	{joinAttributes(
																		hotel?.location?.short_name,
																		hotel.stars_string,
																		quoteHotels
																			.flatMap((qh) =>
																				qh.dates.map(
																					(d) =>
																						getDiff(
																							d,
																							parseDate(startDate),
																							"days"
																						) + 1
																				)
																			)
																			.join(", ") + " N"
																	)}
																</Text>
															</Stack>,
															<SelectField
																select={SelectHotels}
																multiple={false}
																name={`hotels_replacements.${hotel.id}`}
																tripDestinations={tripDestinations}
																showGlobalHotelsNotes
																creatable
															/>,
														]
													}
												)}
											/>
										)
									}
								</GetFieldValue>
							</Stack>
							{commonMealPlans.length ? (
								<Stack padding="4" borderTopWidth="1" gap="4">
									<RadioInputField
										name="change_type"
										value="meal_plan"
										label="Meal Plan"
										help="Change the meal plan and copy"
									/>
									<GetFieldValue<string> name="change_type">
										{({ value }) =>
											value !== "meal_plan" ? null : (
												<SelectField
													select={Select}
													multiple={false}
													options={commonMealPlans}
													name="meal_plan"
													label="Select Meal Plan for Target Option"
												/>
											)
										}
									</GetFieldValue>
								</Stack>
							) : null}
							<Stack padding="4" borderTopWidth="1" gap="4">
								<RadioInputField
									name="change_type"
									value="none"
									label="Custom Change (Occupancy, Room Type etc.)"
									help="You should update details after copying."
								/>
							</Stack>
						</Stack>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								Copy Over
							</Button>
							<Button disabled={submitting} onClick={() => onCancel()}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function NewQuoteHotelsOption({
	startDate,
	endDate,
	tripDestinations,
	noOfAdults,
	children,
	optionName,
	hotelsInitialValue: propHotelsInitialValues,
	hotelExtrasInitialValue: propHotelExtrasInitialValues,
}: {
	startDate: string
	endDate: string
	tripDestinations: Array<TTripDestination>
	noOfAdults: number
	children: TChildrenArray
	optionName: string
	hotelsInitialValue?: IQuoteHotelParams
	hotelExtrasInitialValue?: IQuoteHotelExtraParams
}) {
	const bookingFrom = startDate
	const bookingTo = useMemo(
		() => formatDate(subtractUnit(endDate, 1, "day"), "YYYY-MM-DD HH:mm:ss"),
		[endDate]
	)
	const form = useForm<INewQuoteState>()

	const [hotelsInitialValue] = useState(() => {
		const hotels =
			getFormValueForKey(form.getState().values, `${optionName}.hotels`) ||
			propHotelsInitialValues
		return hotels?.length ? { hotels } : undefined
	})

	const [extrasInitialValue] = useState(() => {
		const extras =
			getFormValueForKey(form.getState().values, `${optionName}.hotelExtras`) ||
			propHotelExtrasInitialValues
		return { hotel_extras: extras || [] }
	})
	return (
		<Stack>
			<Box>
				<CalculateHotelPrice
					noOfAdults={noOfAdults}
					children={children}
					bookingFrom={bookingFrom}
					bookingTo={bookingTo}
					initialValues={hotelsInitialValue}
					tripDestinations={tripDestinations}
					onChange={(services) => {
						form.batch(() => {
							form.change(`${optionName}.hotels` as never, services as never)
							form.change(
								`${optionName}.hotelPrice` as never,
								getTotalPriceForServices(services) as never
							)
						})
					}}
					shouldEmptyInitialValues
					canAddSimilarOptions
					canAddMoreOption={false}
					canAddRoomType
					canAddMealPlan
				/>
			</Box>
			<Divider sm />
			<Stack gap="4">
				<Stack gap="1">
					<Heading as="h5" id="quote_hotel_extras" fontSize="md">
						Any special inclusions in hotels
					</Heading>
					<Text color="muted">
						Add any extra services for hotels e.g. special dinner, honeymoon
						cake etc.
					</Text>
				</Stack>
				<ExtraHotelServices
					bookingFrom={bookingFrom}
					bookingTo={bookingTo}
					initialValues={extrasInitialValue}
					tripDestinations={tripDestinations}
					onChange={(services) => {
						form.batch(() => {
							form.change(
								`${optionName}.hotelExtras` as never,
								services as never
							)
							form.change(
								`${optionName}.hotelExtrasPrice` as never,
								getTotalPriceForExtras(services) as never
							)
						})
					}}
				/>
			</Stack>
			<TotalHotelsPrice optionName={optionName} />
		</Stack>
	)
}

function useHotelsOption(optionIndex: number) {
	const { value: hotelOptions } =
		useFieldValue<INewQuoteState["hotel_options"]>(`hotel_options`)
	const hotelOption = hotelOptions.find(
		(o) => Number(o.option_index) === optionIndex
	)
	return hotelOption
}

function useTotalHotelPrice(optionIndex: number) {
	const hotelOption = useHotelsOption(optionIndex)
	if (!hotelOption) return []
	return addMoneyFromDifferentCurrencies([
		...hotelOption.hotelPrice,
		...hotelOption.hotelExtrasPrice,
	])
}

function TotalHotelsPrice({ optionName }: { optionName: string }) {
	const { value: hotelPrice } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotelPrice"]
	>(`${optionName}.hotelPrice`)
	const { value: hotelExtrasPrice } = useFieldValue<
		INewQuoteState["hotel_options"][number]["hotelExtrasPrice"]
	>(`${optionName}.hotelExtrasPrice`)
	const totalPrice = addMoneyFromDifferentCurrencies([
		...hotelPrice,
		...hotelExtrasPrice,
	])
	return (
		<Box as="footer" textAlign="right">
			<Box
				bgColor="warning"
				display="inlineBlock"
				paddingX="4"
				paddingY="2"
				fontWeight="semibold"
				borderWidth="1"
				borderColor="warning"
				color="warning"
				rounded="md"
			>
				Accommodations Total: <MoneySum money={totalPrice} />
			</Box>
		</Box>
	)
}

function NewQuoteCabs({
	startDate,
	endDate,
	cabsInitialValue: propCabsInitialValue,
	extrasInitialValue: propExtrasInitialValue,
	tripDestinations,
	noOfAdults,
	children,
}: {
	startDate: string
	endDate: string
	noOfAdults: number
	children: TChildrenArray
	cabsInitialValue: INewQuoteState["cabs"]
	extrasInitialValue: INewQuoteState["transportExtras"]
	tripDestinations: Array<TTripDestination>
}) {
	const form = useForm<INewQuoteState>()

	const [cabsInitialValue] = useState(() => {
		const cabs =
			getFormValueForKey(form.getState(), "cabs") || propCabsInitialValue
		return cabs.daywise_services.length ? cabs : undefined
	})

	const [extrasInitialValue] = useState(() => {
		const extras =
			getFormValueForKey(form.getState(), "transportExtras") ||
			propExtrasInitialValue
		return { transport_extras: extras }
	})

	return (
		<Stack>
			<Stack gap="4">
				<Inline as="header" gap="2">
					<Box>
						<StepIcon>
							<Icons.Bus size="6" />
						</StepIcon>
					</Box>
					<Stack gap="1">
						<Heading as="h4" id="quote_cabs">
							Transports and Activities
						</Heading>
						<Text color="muted">
							Please add the transportation services and Activites (if included)
							details and the selling cost price for each service.
						</Text>
						<Text fontSize="sm" color="muted">
							<Icons.LightBulb /> Tip: To speed up the process of adding
							multiple services, use <strong>Next Day</strong> or{" "}
							<strong>Duplicate</strong> actions.
						</Text>
					</Stack>
				</Inline>
				<Box>
					<CalculateCabPrice
						noOfAdults={noOfAdults}
						children={children}
						bookingFrom={startDate}
						bookingTo={endDate}
						initialValues={cabsInitialValue}
						onChange={(services, errors) => {
							form.batch(() => {
								form.change("cabs", services)
								form.change("cabPrice", services.given_price)
								form.change("missingCabTypeErrors", errors)
							})
						}}
						shouldEmptyInitialValues
						tripDestinations={tripDestinations}
					/>
				</Box>
			</Stack>
			<Divider sm />
			<Stack gap="4">
				<Stack gap="1">
					<Heading as="h5" id="quote_cab_extras" fontSize="md">
						Any extra or sightseeing in transportation
					</Heading>
					<Text color="muted">
						Add any extra services like any side destination trip that is
						provided only per customer request
					</Text>
				</Stack>
				<ExtraTransportServices
					bookingFrom={startDate}
					bookingTo={endDate}
					initialValues={extrasInitialValue}
					tripDestinations={tripDestinations}
					onChange={(extras) =>
						form.batch(() => {
							form.change("transportExtras", extras as never)
							form.change(
								"transportExtrasPrice",
								getTotalPriceForExtras(extras)
							)
						})
					}
				/>
			</Stack>
		</Stack>
	)
}

function NewQuoteVisa({
	startDate,
	visaInitialValue: propVisasInitialValue,
	tripDestinations,
	noOfAdults,
	childDetails,
	isEditMode,
}: {
	startDate: string
	noOfAdults: number
	childDetails: TChildrenArray
	visaInitialValue: INewQuoteState["visas"]
	tripDestinations: Array<TTripDestination>
	isEditMode?: boolean
}) {
	const form = useForm<INewQuoteState>()
	const totalPax = noOfAdults + countChildrenAboveComplimentary(childDetails)
	const [visasInitialValue] = useState<TVisaServiceInQuote>(() => {
		const visas: TVisaServiceInQuote =
			getFormValueForKey(form.getState(), "visas") || propVisasInitialValue
		return visas
	})

	const visaFeatureEnabled = useHasFeatureFlag("visa")

	const hasVisaData = Boolean(
		visasInitialValue?.services && visasInitialValue.services.length > 0
	)
	// Don't render anything if visa flag is disabled AND no visa data exists
	if (!visaFeatureEnabled && !hasVisaData) {
		return null
	}

	return (
		<Container fluid paddingY="4" bgColor="default">
			<Stack gap="4">
				<Stack gap="4">
					<Inline as="header" gap="2">
						<Box>
							<StepIcon>
								<Icons.GlobeWithBottomLine size="6" />
							</StepIcon>
						</Box>
						<Stack gap="1">
							<Inline gap="2" alignItems="center">
								<Heading as="h4" id="quote_visas">
									Visa Details
								</Heading>{" "}
								<Icons.NewBadge />
							</Inline>

							<Text color="muted">
								Please provide visa details for this quote if included.
							</Text>
						</Stack>
					</Inline>
					<Box>
						<CalculateVisaPrice
							initialData={visasInitialValue}
							tripDestinations={tripDestinations}
							totalPax={totalPax}
							isEditMode={isEditMode}
							startDate={startDate}
							onChange={(services) => {
								const servicesList = Array.isArray(services?.services)
									? services.services.filter(
											(s) =>
												s.country &&
												s.visa &&
												s.visa_type &&
												s.pax &&
												s.given_price
										)
									: []
								const totalPrices: TMoney[] = []
								servicesList.forEach((vs) => {
									const currency = vs.visa?.currency
									const givenPrice = vs.given_price
									if (givenPrice && currency) {
										totalPrices.push(
											moneyParseByDecimal(Number(givenPrice) || 0, currency)
										)
									}
								})
								form.batch(() => {
									form.change("visas", {
										services: servicesList,
									})
									form.change(
										"visasPrice",
										addMoneyFromDifferentCurrencies(totalPrices)
									)
								})
							}}
						/>
					</Box>
				</Stack>
				<TotalVisasPrice startDate={startDate} />
			</Stack>
		</Container>
	)
}

function useTotalCabsAndTravelActivitesWithExtrasPrice() {
	const { value: cabsAndActivitiesPrices } =
		useFieldValue<INewQuoteState["cabPrice"]>("cabPrice")
	const { value: cabsPrice } = useFieldValue<
		INewQuoteState["cabs"]["given_price_of_cabs_only"]
	>("cabs.given_price_of_cabs_only")
	const { value: travelActivitiesPrice } = useFieldValue<
		INewQuoteState["cabs"]["given_price_of_cabs_only"]
	>("cabs.given_price_of_activities_only")
	const { value: transportExtrasPrice } = useFieldValue<
		INewQuoteState["transportExtrasPrice"]
	>("transportExtrasPrice")
	return [
		addMoneyFromDifferentCurrencies([
			...cabsAndActivitiesPrices,
			...transportExtrasPrice,
		]),
		{
			cabsPrice,
			travelActivitiesPrice,
			extrasPrice: transportExtrasPrice,
		},
	] as const
}

function TotalCabsAndActivitiesPrice() {
	const [total, { cabsPrice, travelActivitiesPrice, extrasPrice }] =
		useTotalCabsAndTravelActivitesWithExtrasPrice()
	const hasPrices =
		[cabsPrice, travelActivitiesPrice, extrasPrice].filter((t) => t.length)
			.length > 0
	if (!hasPrices) {
		return null
	}
	const showTotal =
		[cabsPrice, travelActivitiesPrice, extrasPrice].filter((t) => t.length)
			.length > 1
	return (
		<Box as="footer" textAlign="right">
			<Box
				bgColor="warning"
				display="inlineBlock"
				paddingX="4"
				paddingY="2"
				fontWeight="semibold"
				borderWidth="1"
				borderColor="warning"
				color="warning"
				rounded="md"
			>
				<Stack gap="2">
					{showTotal ? (
						<Box>
							Total: <MoneySum money={total} />
						</Box>
					) : null}
					<Text fontSize={showTotal ? "sm" : undefined}>
						{joinAttributes(
							cabsPrice?.length ? (
								<>
									{" "}
									Transports: <MoneySum money={cabsPrice} />
								</>
							) : null,
							travelActivitiesPrice?.length ? (
								<>
									Activity/Tickets: <MoneySum money={travelActivitiesPrice} />
								</>
							) : null,
							extrasPrice?.length ? (
								<>
									Others: <MoneySum money={extrasPrice} />
								</>
							) : null
						)}
					</Text>
				</Stack>
			</Box>
		</Box>
	)
}

function NewQuoteFlights({
	startDate,
	endDate,
	flights,
	no_of_adults,
	children,
	tripDestinations,
	onChange,
	openFlightDetailForm,
}: {
	startDate: string
	endDate: string
	flights: INewQuoteState["flights"]
	onChange: (
		flights: INewQuoteState["flights"],
		price: Array<TMoney>,
		costPrice: Array<TMoney>
	) => void
	openFlightDetailForm?: boolean
} & Pick<
	React.ComponentProps<typeof FlightService>,
	"no_of_adults" | "children" | "tripDestinations"
>) {
	const initialValue = useRef(
		flights?.flight_form_fill_mode ? flights : undefined
	)

	const handleChange = (s: IFlightServicesParams | undefined) => {
		const flightsList = s?.flights || []
		const pricingList =
			flightsList.length > 0
				? flightsList.map((f) => f.pricing)
				: [{ given_price: 0 }]

		const calculatedList =
			flightsList.length > 0
				? flightsList.map((f) => ({
						price: f.pricing.calculated_price,
						currency: f.pricing.currency,
					}))
				: [{ price: 0 }]

		onChange(
			s as INewQuoteState["flights"],
			getTotalPriceForExtras(pricingList),
			getTotalPriceForExtras(calculatedList)
		)
	}

	return (
		<Stack>
			<Stack gap="4">
				<Inline as="header" gap="2">
					<Box>
						<StepIcon>
							<Icons.Airplane size="4" />
						</StepIcon>
					</Box>
					<Stack gap="1">
						<Heading as="h4" id="quote_flights">
							Flight Details
						</Heading>
						<Text color="muted">
							Please provide flight details for this quote if included.
						</Text>
					</Stack>
				</Inline>

				<FlightService
					bookingFrom={startDate}
					bookingTo={endDate}
					onChange={handleChange}
					initialValues={initialValue.current}
					no_of_adults={no_of_adults}
					children={children}
					tripDestinations={tripDestinations}
					flightPreview={openFlightDetailForm ? 0 : 1}
				/>
			</Stack>
		</Stack>
	)
}

function TotalFlightsPrice() {
	const { value: flightPrice } =
		useFieldValue<INewQuoteState["flightPrice"]>("flightPrice")

	const { value: flightCostPrice } =
		useFieldValue<INewQuoteState["flightCostPrice"]>("flightCostPrice")

	return (
		<Box as="footer" textAlign="right">
			<Box
				bgColor="warning"
				display="inlineBlock"
				paddingX="4"
				paddingY="2"
				fontWeight="semibold"
				borderWidth="1"
				borderColor="warning"
				color="warning"
				rounded="md"
			>
				<Stack gap="1">
					<Inline gap="1" justifyContent={"end"}>
						Flights Cost Total:
						<MoneySum money={flightCostPrice} />
					</Inline>

					<Inline gap="1" justifyContent={"end"}>
						Flights Selling Total:
						<MoneySum money={flightPrice} />
					</Inline>
				</Stack>
			</Box>
		</Box>
	)
}

function TotalVisasPrice({ startDate }: { startDate: string }) {
	const { value: visasPrice } =
		useFieldValue<INewQuoteState["visasPrice"]>("visasPrice")

	const { value: visas } = useFieldValue<INewQuoteState["visas"]>("visas")
	const visaCostPrice = Array.isArray(visas?.services)
		? visas.services
				.map((s) => {
					const isRateExpired = s.visa_type
						? isVisaRateExpired({
								rateValidityDate: s.visa_type.price_valid_till,
								startDate,
							})
						: false
					if (!s?.visa_type?.cost_price || !s.pax || isRateExpired) return null
					return multipleMoneyBy(s?.visa_type?.cost_price, s.pax)
				})
				.filter((price): price is TFormattedMoney => !!price)
		: []

	return (
		<Box as="footer" textAlign="right">
			<Box
				bgColor="warning"
				display="inlineBlock"
				paddingX="4"
				paddingY="2"
				fontWeight="semibold"
				borderWidth="1"
				borderColor="warning"
				color="warning"
				rounded="md"
			>
				<Stack gap="1">
					<Inline gap="1" justifyContent={"end"}>
						Visa Cost Total:
						<MoneySum money={addMoneyFromDifferentCurrencies(visaCostPrice)} />
					</Inline>

					<Inline gap="1" justifyContent={"end"}>
						Visa Given/Selling Total:
						<MoneySum money={addMoneyFromDifferentCurrencies(visasPrice)} />
					</Inline>
				</Stack>
			</Box>
		</Box>
	)
}

function NewQuoteOtherExtras({
	startDate,
	endDate,
	otherExtras,
	onChange,
	tripDestinations,
}: {
	startDate: string
	endDate: string
	otherExtras: INewQuoteState["otherExtras"]
	onChange: (extras: INewQuoteState["otherExtras"], price: TMoney[]) => void
	tripDestinations: Array<TTripDestination>
}) {
	const initialValue = useRef({ other_extras: otherExtras }).current
	return (
		<Stack gap="4">
			<Inline as="header" gap="2">
				<Box>
					<StepIcon>
						<Icons.Star size="6" />
					</StepIcon>
				</Box>
				<Stack gap="1">
					<Heading as="h4" id="quote_other_extras">
						Any other special service for this trip
					</Heading>
					<Text color="muted">
						Add any extra services like off road dinner, side treking etc that
						are associated with overall trip package
					</Text>
				</Stack>
			</Inline>
			<ExtraQuoteServices
				bookingFrom={startDate}
				bookingTo={endDate}
				initialValues={initialValue}
				tripDestinations={tripDestinations}
				onChange={(extras) =>
					onChange(extras as never, getTotalPriceForExtras(extras))
				}
			/>
		</Stack>
	)
}

function TotalOtherExtrasPrice() {
	const { value: otherExtras } =
		useFieldValue<INewQuoteState["otherExtras"]>("otherExtras")
	const { value: otherExtrasPrice } =
		useFieldValue<INewQuoteState["otherExtrasPrice"]>("otherExtrasPrice")
	return otherExtras.length ? (
		<Box as="footer" textAlign="right">
			<Box
				bgColor="warning"
				display="inlineBlock"
				paddingX="4"
				paddingY="2"
				fontWeight="semibold"
				borderWidth="1"
				borderColor="warning"
				color="warning"
				rounded="md"
			>
				Extra Services Total: <MoneySum money={otherExtrasPrice} />
			</Box>
		</Box>
	) : null
}

function SellingPrice({
	optionIndex,
	enableFlightRounding = false,
	enableSyncRoundingAmongOptions = false,
	quoteComponentPrices,
}: {
	optionIndex: number
	quoteComponentPrices: IQuoteComponentsCostPricesDistributed
	enableFlightRounding: boolean
	enableSyncRoundingAmongOptions: boolean
}) {
	const { totalPrice, flightPrice, flightCostPrice } =
		useTotalQuotePriceInCostingCurrency(optionIndex)
	const { value: perPersonCostPriceAllocation } = useFieldValue<
		INewQuoteState["options"][number]["cost_prices_per_person"]
	>(`options[${optionIndex}].cost_prices_per_person`)

	const { value: includePerPersonPrice } = useFieldValue<
		INewQuoteState["include_per_person_price"]
	>(`include_per_person_price`)

	return (
		<Stack gap="4">
			<Box>
				<MarginsAndTaxInputField
					optionIndex={optionIndex}
					quoteComponentPrices={quoteComponentPrices}
					costPriceWithoutFlights={Number(
						formatMoneyByDecimal(subtractMoney(totalPrice, flightPrice))
					)}
					flightPriceInCostingCurrency={
						Number(formatMoneyByDecimal(flightPrice)) || 0
					}
					flightCostPriceInCostingCurrency={
						Number(formatMoneyByDecimal(flightCostPrice)) || 0
					}
					perPersonCostPriceAllocation={
						perPersonCostPriceAllocation || undefined
					}
					includePerPersonPrice={isTruthy(includePerPersonPrice)}
					enableFlightRounding={enableFlightRounding}
					enableSyncRoundingAmongOptions={enableSyncRoundingAmongOptions}
				/>
			</Box>
		</Stack>
	)
}

function QuoteCommentsRemarksInputField({
	optionIndex,
}: {
	optionIndex: number
}) {
	return (
		<Grid gap="4">
			<Col sm={12} md>
				<TextAreaInputField
					label="Any internal comments regarding selling price"
					secondaryLabel="optional"
					name={`options[${optionIndex}].given_price_comments`}
				/>
			</Col>

			<Col sm={12} md>
				<Stack gap="2">
					<MarkdownInputField
						label="Remarks for Agent/Customer"
						placeholder="Any special remarks for the customer."
						secondaryLabel="optional"
						name={`options[${optionIndex}].given_price_customer_remarks`}
					/>
					<Text fontSize="sm" color="muted">
						These remarks will be shared with the customer.
					</Text>
				</Stack>
			</Col>
		</Grid>
	)
}

function allocatePax({
	noOfAdults,
	children,
	hotels,
	cabs,
	includeAdultsWithExtraBedInCabs,
	includeChildrenInCabsStartAge,
	// flights,
	totalExtrasPrice,
	includeChildrenInExtras,
	costingCurrency,
	currencyExchangePairs,
	excludeAdultsCountInCabs,
	visas,
}: {
	noOfAdults: number
	children: TChildrenArray
	hotels: IQuoteHotelParams
	cabs: INewQuoteState["cabs"]
	includeAdultsWithExtraBedInCabs?: boolean
	includeChildrenInCabsStartAge: false | number
	flights: INewQuoteState["flights"]
	totalExtrasPrice?: Array<TMoney>
	includeChildrenInExtras?: boolean
	costingCurrency: string
	currencyExchangePairs: Array<TCurrencyPair>
	excludeAdultsCountInCabs: number
	visas: INewQuoteState["visas"]
}) {
	const pax: Array<{
		id: number
		service: "accommodation"
		type: "adult" | "child"
		accommodation?: "base" | "extra_bed" | "no_extra_bed" | "complimentary"
		room_sharing_type?: number
		price: TMoney[]
		hotels_price: TMoney[]
		cabs_price: TMoney[]
		travel_activities_price: TMoney[]
		flights_price: TMoney[]
		extras_price: TMoney[]
		visas_price: TMoney[]
		age?: number
	}> = []

	const converter = makeCurrencyConverter(currencyExchangePairs, true)

	function convertMoneyToCostingCurrencyAndGetDecimalAmount(
		money: TMoney | Array<TMoney>
	): number {
		return Number(formatMoneyByDecimal(converter(costingCurrency, money)))
	}

	function addPax(type: "adult" | "child", age?: number) {
		pax.push({
			id: pax.length,
			service: "accommodation",
			type: type,
			price: [createMoney(0, costingCurrency)],
			age,
			hotels_price: [createMoney(0, costingCurrency)],
			cabs_price: [createMoney(0, costingCurrency)],
			travel_activities_price: [createMoney(0, costingCurrency)],
			flights_price: [createMoney(0, costingCurrency)],
			extras_price: [createMoney(0, costingCurrency)],
			visas_price: [createMoney(0, costingCurrency)],
		})
	}
	for (let i = 0; i < noOfAdults; i++) {
		addPax("adult")
	}
	// sort the children in descending order of age
	children = children
		.concat([])
		.sort((a, b) => (a.age > b.age ? -1 : a.age === b.age ? 0 : 1))
	for (const child of children) {
		for (let i = 0; i < child.count; i++) {
			addPax("child", child.age)
		}
	}

	function addPriceToPax(
		p: (typeof pax)[0],
		type: "hotels" | "cabs" | "activities" | "flights" | "extras" | "visas",
		amount: TMoney[] | undefined | null
	) {
		const price = amount ? amount : []
		p.price = addMoneyFromDifferentCurrencies(p.price.concat(price))
		switch (type) {
			case "hotels":
				p.hotels_price = addMoneyFromDifferentCurrencies(
					p.hotels_price.concat(price)
				)
				break
			case "cabs":
				p.cabs_price = addMoneyFromDifferentCurrencies(
					p.cabs_price.concat(price)
				)
				break
			case "activities":
				p.travel_activities_price = addMoneyFromDifferentCurrencies(
					p.travel_activities_price.concat(price)
				)
				break
			case "flights":
				p.flights_price = addMoneyFromDifferentCurrencies(
					p.flights_price.concat(price)
				)
				break
			case "extras":
				p.extras_price = addMoneyFromDifferentCurrencies(
					p.extras_price.concat(price)
				)
				break
			case "visas":
				p.visas_price = addMoneyFromDifferentCurrencies(
					p.visas_price.concat(price)
				)
				break
		}
		return p
	}

	let warnings: Array<string> = []

	// group the hotels by nights
	collect(normalizeHotelsForQuote(hotels))
		.groupBy((h) => h.date)
		.toArray()
		.forEach((hotels) => {
			const { warnings: warns } = allocatePaxInHotels(pax, hotels, {
				defaultCurrency: costingCurrency,
				addPriceToPax: (pax, price) => addPriceToPax(pax, "hotels", price),
			})
			warnings = warnings.concat(warns)
		})

	const includedPaxInCabs = pax
		.filter((p) => {
			if (
				p.type === "child" &&
				(includeChildrenInCabsStartAge === false ||
					(p.age || 0) < includeChildrenInCabsStartAge)
			) {
				return false
			}
			if (
				p.type === "adult" &&
				p.accommodation === "extra_bed" &&
				!includeAdultsWithExtraBedInCabs
			) {
				return false
			}
			return true
		})
		.sort((a, b) => {
			if (a.type === "adult" && b.type === "adult") {
				if (a.accommodation === "extra_bed" && b.accommodation !== "extra_bed")
					return -1
				if (a.accommodation !== "extra_bed" && b.accommodation === "extra_bed")
					return 1
			}
			return 0
		})
		.slice(excludeAdultsCountInCabs)
	const normalizedDaywiseServices = normalizeDaywiseServices(cabs)
	if (isTruthy(cabs.add_cab_prices_at_once)) {
		const totalCabsPrice = cabs.given_price_of_cabs_only
		const perPaxPrice = totalCabsPrice.map((price) =>
			divideMoneyBy(price, includedPaxInCabs.length || 1, "ceil")
		)
		includedPaxInCabs.forEach((p) => {
			addPriceToPax(p, "cabs", perPaxPrice)
		})
	} else {
		collect(normalizedDaywiseServices.cabs)
			.groupBy((d) => d.date)
			.toArray()
			.forEach((cabs) => {
				const totalCabsPriceForThisDay = cabs.reduce<Array<TMoney>>(
					(total, { currency, given_price }) =>
						addMoneyFromDifferentCurrencies(
							total.concat([moneyParseByDecimal(given_price, currency)])
						),
					[]
				)
				const perPaxPrice = totalCabsPriceForThisDay.map((m) =>
					divideMoneyBy(m, includedPaxInCabs.length || 1, "ceil")
				)
				includedPaxInCabs.forEach((p) => {
					addPriceToPax(p, "cabs", perPaxPrice)
				})
			})
	}

	collect(normalizedDaywiseServices.travel_activities)
		.groupBy((d) => d.date)
		.toArray()
		.forEach((travelActivities) => {
			travelActivities.forEach((travelActivity) => {
				const allocatedPaxId: {
					[
						key: number
					]: (typeof travelActivity.ticket_tourist_configurations)[number]
				} = {}
				travelActivity.ticket_tourist_configurations.forEach((ticket) => {
					let assignedTo: (typeof pax)[0] | undefined = undefined
					const { configuration } = ticket
					const { age_start, age_end } = configuration
					let perPaxPriceForGroup = 0
					let availabePaxForGroup: typeof pax = []
					switch (ticket.configuration.tourist_type) {
						case "adult":
							{
								const totalQuantity = ticket.quantity || 0
								const complementary = ticket.complementary || 0
								const chargeableQuantity = totalQuantity - complementary
								for (let i = 0; i < totalQuantity; i++) {
									assignedTo = pax.find(
										(p) => !allocatedPaxId[p.id] && p.type === "adult"
									)
									if (assignedTo) {
										if (i < chargeableQuantity) {
											addPriceToPax(assignedTo, "activities", [
												moneyParseByDecimal(
													ticket.per_quantity_given_price || 0,
													ticket.currency
												),
											])
										}
										allocatedPaxId[assignedTo.id] = ticket
									}
								}
							}
							break
						case "child":
							{
								const totalQuantity = ticket.quantity || 0
								const complementary = ticket.complementary || 0
								const chargeableQuantity = totalQuantity - complementary
								for (let i = 0; i < totalQuantity; i++) {
									if (
										age_start !== undefined &&
										age_start !== null &&
										age_end !== undefined &&
										age_end !== null
									) {
										assignedTo = pax.find(
											(p) =>
												!allocatedPaxId[p.id] &&
												p.type === "child" &&
												p.age &&
												p.age >= age_start &&
												p.age <= age_end
										)
										if (assignedTo) {
											if (i < chargeableQuantity) {
												addPriceToPax(assignedTo, "activities", [
													moneyParseByDecimal(
														ticket.per_quantity_given_price || 0,
														ticket.currency
													),
												])
											}
											allocatedPaxId[assignedTo.id] = ticket
										}
									}
								}
							}
							break
						case "group":
							availabePaxForGroup = pax.filter((p) => {
								if (allocatedPaxId[p.id]) return false
								if (!p.age) return true
								if (!age_start && !age_end) {
									return !travelActivity.activity.complimentary_till_age
										? true
										: p.age > travelActivity.activity.complimentary_till_age
								}
								return (
									p.age >=
										(age_start ||
											travelActivity.activity.complimentary_till_age ||
											0) && p.age <= (age_end || Infinity)
								)
							})
							perPaxPriceForGroup = Math.ceil(
								(ticket.given_price || 0) /
									Math.min(
										availabePaxForGroup.length || 1,
										ticket.configuration.quantity * ticket.quantity
									)
							)
							for (
								let i = 0;
								i < ticket.quantity * ticket.configuration.quantity;
								i++
							) {
								assignedTo = availabePaxForGroup.find(
									(p) => !allocatedPaxId[p.id]
								)
								if (assignedTo) {
									addPriceToPax(assignedTo, "activities", [
										moneyParseByDecimal(perPaxPriceForGroup, ticket.currency),
									])
									allocatedPaxId[assignedTo.id] = ticket
								}
							}
							break
					}
				})
				const allocatedTickets = Object.values(allocatedPaxId)
				let unallocatedTickets = travelActivity.ticket_tourist_configurations
					.map((ticket) => {
						let allocatedQuantity = allocatedTickets.filter(
							(t) => t === ticket
						).length
						const perTicketQuantity = ticket.configuration.quantity
						const availableQuantity = ticket.quantity * perTicketQuantity
						// incase of group, convert the allocatedQuantity to allocated tickets
						const tickets = allocatedQuantity
							? Math.ceil(
									Math.max(allocatedQuantity, perTicketQuantity) /
										perTicketQuantity
								)
							: 0
						allocatedQuantity = tickets * perTicketQuantity
						return {
							ticket,
							unallocatedQuantity: Math.ceil(
								(availableQuantity - allocatedQuantity) / perTicketQuantity
							),
						}
					})
					.filter((ticket) => ticket.unallocatedQuantity > 0)
				if (unallocatedTickets.length) {
					// some tickets were configured but have not been allocated to pax
					// This happens if when children have been assigned the tickets of adults
					const unallocatedChildren = pax.filter(
						(p) => !allocatedPaxId[p.id] && p.type === "child"
					)
					unallocatedChildren.sort((a, b) =>
						!a.age || !b.age ? 0 : a.age > b.age ? -1 : 1
					)
					const unallocatedTicketsForAdults = unallocatedTickets.filter(
						({ ticket }) => ticket.configuration.tourist_type === "adult"
					)
					unallocatedChildren.forEach((assignedTo) => {
						const ticket = unallocatedTicketsForAdults.find(
							({ unallocatedQuantity }) => unallocatedQuantity > 0
						)
						if (ticket) {
							addPriceToPax(assignedTo, "activities", [
								moneyParseByDecimal(
									ticket.ticket.per_quantity_given_price || 0,
									ticket.ticket.currency
								),
							])
							allocatedPaxId[assignedTo.id] = ticket.ticket
							ticket.unallocatedQuantity = ticket.unallocatedQuantity - 1
						}
					})
					unallocatedTickets = unallocatedTickets.filter(
						({ unallocatedQuantity }) => {
							return unallocatedQuantity
						}
					)
					if (unallocatedTickets.length) {
						throw new Error(
							`Unable to allocate activity tickets: ${unallocatedTickets
								.map(
									({ ticket, unallocatedQuantity }) =>
										`${unallocatedQuantity} ${ticket.configuration.name}`
								)
								.join(", ")}`
						)
					}
				}
			})
		})

	if (totalExtrasPrice?.length) {
		const includeChildrenInExtrasStartAge = includeChildrenInExtras
			? 6
			: undefined
		const includedPaxInCabs = pax.filter((p) => {
			if (
				p.type === "child" &&
				(!includeChildrenInExtrasStartAge ||
					(p.age || 0) < includeChildrenInExtrasStartAge)
			) {
				return false
			}
			return true
		})
		const perPaxPrice = totalExtrasPrice.map((m) =>
			divideMoneyBy(m, includedPaxInCabs.length || 1, "ceil")
		)
		includedPaxInCabs.forEach((p) => {
			addPriceToPax(p, "extras", perPaxPrice)
		})
	}
	if (visas?.services.length) {
		visas.services.forEach((service) => {
			const allocatedPaxId: Record<number, boolean> = {}
			const result = processVisasServiceForPax({
				service,
				pax,
				allocatedPaxId,
			})

			if (!result) return

			if ("error" in result && result.error) {
				warnings.push(result.error)
				return
			}
			if (result.usedPaxIds) {
				result.usedPaxIds.forEach((id) => {
					allocatedPaxId[id] = true
				})
			}

			if (result.allocations) {
				result.allocations.forEach(({ pax: paxAllocation, price }) => {
					const p = pax.find((person) => person.id === paxAllocation.id)
					// Add visas price to the pax
					if (p) {
						addPriceToPax(p, "visas", price)
					}
				})
			}
		})
	}

	function getLabelForSharing(sharing: number) {
		switch (sharing) {
			case 1:
				return "Single Sharing"
			case 2:
				return "Double Sharing"
			case 3:
				return "Triple Sharing"
			case 4:
				return "Quad Sharing"
			default:
				return sharing + " pax/room"
		}
	}

	const allocation = collect(pax)
		.groupBy(
			(p) =>
				(p.accommodation || "") +
				"-" +
				p.price.map((t) => `${t.currency}${t.amount}`).join("+") +
				"-" +
				p.type +
				"-" +
				p.room_sharing_type
		)
		.toArray()
		.map((pax, index) => {
			const { type, accommodation, room_sharing_type } = pax[0]
			let perPaxLabel = type === "adult" ? "Adult" : "Child"
			switch (accommodation) {
				case "extra_bed":
					perPaxLabel += " with Extra Bed/Mattress"
					break
				case "no_extra_bed":
					perPaxLabel += " without Extra Bed/Mattress"
					break
				case "base":
					perPaxLabel =
						(type === "adult" ? "Person" : "Child") +
						(room_sharing_type
							? ` (${getLabelForSharing(room_sharing_type)})`
							: ``)
			}
			let totalPaxLabel = ""
			switch (type) {
				case "adult":
					totalPaxLabel = pax.length + " Pax"
					break
				case "child":
					totalPaxLabel = `${pluralize("Child", pax.length, true)} (${pax
						.map((p) => p.age)
						.filter(Boolean)
						.join("y, ")}y)`
					break
			}
			return {
				...pax[0],
				id: `${index}_` + perPaxLabel.replace(/[\W]/gi, "_"),
				count: pax.length,
				per_pax_label: perPaxLabel,
				total_pax_label: totalPaxLabel,
				individuals: pax,
			}
		})
		.map(
			({
				price,
				hotels_price,
				cabs_price,
				travel_activities_price,
				flights_price,
				extras_price,
				visas_price,
				...others
			}) => {
				const hotels_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(hotels_price)
				const cabs_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(cabs_price)
				const travel_activities_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(
						travel_activities_price
					)
				const flights_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(flights_price)
				const extras_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(extras_price)
				const visas_price_decimal =
					convertMoneyToCostingCurrencyAndGetDecimalAmount(visas_price)
				return {
					...others,
					currency: costingCurrency,
					price: addMoney(
						...[
							moneyParseByDecimal(hotels_price_decimal, costingCurrency),
							moneyParseByDecimal(cabs_price_decimal, costingCurrency),
							moneyParseByDecimal(
								travel_activities_price_decimal,
								costingCurrency
							),
							moneyParseByDecimal(flights_price_decimal, costingCurrency),
							moneyParseByDecimal(extras_price_decimal, costingCurrency),
							moneyParseByDecimal(visas_price_decimal, costingCurrency),
						]
					),
					visas_price: visas_price_decimal,
					hotels_price: hotels_price_decimal,
					cabs_price: cabs_price_decimal,
					travel_activities_price: travel_activities_price_decimal,
					flights_price: flights_price_decimal,
					extras_price: extras_price_decimal,
					pax_count: others.individuals.length,
					ages: others.individuals
						.map((p) => p.age)
						.filter((age): age is number => Boolean(age)),
				}
			}
		)

	return {
		allocation,
		warnings,
	}
}

function useCurrenciesFromCosting() {
	const { value: hotelOptions } =
		useFieldValue<INewQuoteState["hotel_options"]>("hotel_options")
	const [totalCabsPrice] = useTotalCabsAndTravelActivitesWithExtrasPrice()
	const { value: flightPrice } =
		useFieldValue<INewQuoteState["flightPrice"]>("flightPrice")
	const { value: otherExtrasPrice } =
		useFieldValue<INewQuoteState["otherExtrasPrice"]>("otherExtrasPrice")
	const { value: visasPrice } =
		useFieldValue<INewQuoteState["visasPrice"]>("visasPrice")

	return useMemo(
		() =>
			collect(
				[
					...hotelOptions.flatMap((h) => [
						...h.hotelPrice,
						...h.hotelExtrasPrice,
					]),
					...visasPrice,
					...totalCabsPrice,
					...otherExtrasPrice,
					...flightPrice,
				]
					.filter((t) => t.amount)
					.map((t) => t.currency)
			)
				.unique()
				.toArray(),
		[hotelOptions, totalCabsPrice, otherExtrasPrice, flightPrice, visasPrice]
	)
}

function SelectCostingCurrency({
	canModifyQuoteExchangeRate,
}: {
	canModifyQuoteExchangeRate: boolean
}) {
	const currencies = useCurrenciesFromCosting()
	const { value: costingCurrency, onChange: changeCostingCurrency } =
		useFieldValue<INewQuoteState["costing_currency"]>("costing_currency")
	// ensure that the costing is from the list of all currencies
	useEffect(() => {
		if (currencies?.length && currencies.indexOf(costingCurrency) === -1) {
			setTimeout(() => {
				changeCostingCurrency(currencies[0])
			}, 0)
		}
	}, [currencies, costingCurrency, changeCostingCurrency])
	const form = useForm<INewQuoteState>()
	if (currencies.length <= 1) return null
	return (
		<Grid gap="4" borderBottomWidth="1" paddingBottom="4">
			<Col sm={12} md={4}>
				<Stack gap="1">
					<Heading fontSize="md">Costing Currency and Exchange Rates</Heading>
					<Text color="muted">
						The quote has multiple costing currencies. Please select a costing
						currency and provide the exchange rates.
					</Text>
				</Stack>
			</Col>
			<Col>
				<Stack gap="4">
					<Inline gap="6" flexWrap="wrap">
						<SelectInputField
							label="Costing Currency"
							name="costing_currency"
							onChange={(e) => {
								form.change("costing_currency", e.currentTarget.value)
								form.change("given_currency", e.currentTarget.value)
							}}
							size="sm"
						>
							{currencies.map((c) => (
								<option key={c} value={c}>
									{c}
								</option>
							))}
						</SelectInputField>
						<Inline gap="2">
							<Stack>
								<Heading fontSize="md" color="primary">
									Exchange Rates
								</Heading>
								<Box>
									<Icons.SwitchHorizontal />
								</Box>
							</Stack>
							<Inline gap="4" flexWrap="wrap">
								{currencies.map((baseCurrency) =>
									baseCurrency === costingCurrency ? null : (
										<CurrencyPairInputFieldWithRates
											canNotCustomize={!canModifyQuoteExchangeRate}
											key={`${baseCurrency}-${costingCurrency}`}
											baseCurrency={baseCurrency}
											counterCurrency={costingCurrency}
											name={`currency_pairs_obj`}
											size="sm"
										/>
									)
								)}
							</Inline>
						</Inline>
					</Inline>
				</Stack>
			</Col>
		</Grid>
	)
}

function PerComponentCostPriceMarkupTaxPricing({
	optionIndex,
}: {
	optionIndex: number
}) {
	const quoteComponentPrices =
		useQuoteComponentPricesInCostingCurrency(optionIndex)

	return (
		<Stack>
			<QuotePriceInCostingCurrency optionIndex={optionIndex} />
			<ComponentMarkupTaxPricingBifurcation
				optionIndex={optionIndex}
				quoteComponentPrices={quoteComponentPrices}
			/>
		</Stack>
	)
}

export type TPricingStrategyGroup =
	| "overall"
	| "per_person"
	| "per_component"
	| "per_component_per_person"

function CostingPricesBifurcation({
	editingExistingQuote,
	canModifyQuoteExchangeRate,
}: {
	editingExistingQuote: boolean
	canModifyQuoteExchangeRate: boolean
}) {
	const form = useForm<INewQuoteState>()
	const { value: options } = useFieldValue<INewQuoteState["options"]>("options")
	const { value: children } = useFieldValue<TChildrenArray | undefined>(
		"children"
	)
	const { value: noOfAdults } = useFieldValue<number>("no_of_adults")
	const totalPax = noOfAdults + (children?.length ?? 0)
	const { value: pricingStrategy, onChange: onChangePricingStrategy } =
		useFieldValue<TGivenQuotePricingStrategy>("pricing_strategy")
	const {
		value: includePerPersonPrice,
		onChange: onChangeIncludePerPersonPrice,
	} = useFieldValue<0 | 1 | boolean | "0" | "1">("include_per_person_price")

	const { enableFlightRounding, enableSyncRoundingAmongOptions } =
		usePricingStrategyMigrationSync({
			pricingStrategy,
			editingExistingQuote,
		})
	//We need to store the initial pricing strategy value to determine the pricing
	//strategies base values for options when user toggle between pricing stratgies, this is critical because the determination of "baseVariantForGroup"
	//should be done once only the component quotation form loads with default pricing strategies(avaible inside the form inital state (which will be coming from a exisitng quote and "overall_v2" by default for new quotes))
	//We will use the ref stored "initialPricingStrategy" to compute "baseVariantForGroup" only once.
	const initialPricingStrategy =
		useRef<INewQuoteState["pricing_strategy"]>(pricingStrategy).current

	const getRenderComponentByPricingStrategy = useCallback(
		(pricingStrategy: TGivenQuotePricingStrategy, optionIndex: number) => {
			if (
				pricingStrategy === "overall_v1" ||
				pricingStrategy === "overall_v2"
			) {
				return (
					<PerPersonCostPrice
						optionIndex={optionIndex}
						enableFlightRounding={enableFlightRounding}
						enableSyncRoundingAmongOptions={enableSyncRoundingAmongOptions}
					/>
				)
			}
			if (
				pricingStrategy === "per_person_v1" ||
				pricingStrategy === "per_person_v2"
			) {
				return (
					<PerPersonCostPrice
						optionIndex={optionIndex}
						enableFlightRounding={enableFlightRounding}
						enableSyncRoundingAmongOptions={enableSyncRoundingAmongOptions}
					/>
				)
			}
			if (pricingStrategy === "per_component_v2") {
				return (
					<PerComponentCostPriceMarkupTaxPricing optionIndex={optionIndex} />
				)
			}
			if (pricingStrategy === "per_component_per_person_v2") {
				return (
					<PerComponentPerPersonCostPriceMarkupTaxPricing
						optionIndex={optionIndex}
					/>
				)
			}

			return (
				<PerPersonCostPrice
					optionIndex={optionIndex}
					enableFlightRounding={enableFlightRounding}
					enableSyncRoundingAmongOptions={enableSyncRoundingAmongOptions}
				/>
			)
		},
		[enableFlightRounding, enableSyncRoundingAmongOptions]
	)

	const collapseStrategyToGroup = useCallback(
		(s: TGivenQuotePricingStrategy): TPricingStrategyGroup => {
			if (s === "overall_v1" || s === "overall_v2") return "overall"
			if (s === "per_person_v1" || s === "per_person_v2") return "per_person"
			if (s === "per_component_v2") return "per_component"
			return "per_component_per_person"
		},
		[]
	)

	const baseVariantForGroup: Record<
		TPricingStrategyGroup,
		TGivenQuotePricingStrategy
	> = useMemo(() => {
		const isOverallPerPersonV2PricingStrategy =
			initialPricingStrategy === "overall_v2" ||
			initialPricingStrategy === "per_person_v2"

		const isPerComponentV2PricingStrategy =
			initialPricingStrategy === "per_component_v2" ||
			initialPricingStrategy === "per_component_per_person_v2"

		return {
			overall:
				isOverallPerPersonV2PricingStrategy || isPerComponentV2PricingStrategy
					? "overall_v2"
					: editingExistingQuote
						? "overall_v1"
						: "overall_v2",
			per_person:
				isOverallPerPersonV2PricingStrategy || isPerComponentV2PricingStrategy
					? "per_person_v2"
					: editingExistingQuote
						? "per_person_v1"
						: "per_person_v2",
			per_component: "per_component_v2",
			per_component_per_person: "per_component_per_person_v2",
		}
	}, [editingExistingQuote, initialPricingStrategy])

	return (
		<Stack gap="8">
			<SelectCostingCurrency
				canModifyQuoteExchangeRate={canModifyQuoteExchangeRate}
			/>
			<Inline
				gap="8"
				flexWrap="wrap"
				padding="4"
				bgColor="primary"
				borderWidth="1"
				borderColor="primary"
				rounded="default"
			>
				<Stack gap="1">
					<Inline gap={"2"}>
						<Box as="label" alignItems="center" gap="1">
							Pricing Strategy
						</Box>
					</Inline>

					<SelectInput
						label="Pricing Strategy"
						style={{ width: "220px" }}
						value={collapseStrategyToGroup(pricingStrategy)}
						onChange={(e) => {
							const group = e.currentTarget.value as TPricingStrategyGroup
							const strategy = baseVariantForGroup[group]

							onChangePricingStrategy(strategy)

							if (
								group === "per_person" ||
								group === "per_component_per_person"
							) {
								onChangeIncludePerPersonPrice(1)
							} else if (isTruthy(includePerPersonPrice)) {
								onChangeIncludePerPersonPrice(0)
							}
						}}
					>
						<AllowedPricingStrategyOptions />
					</SelectInput>
				</Stack>
				<SelectGivenCurrency
					canModifyQuoteExchangeRate={canModifyQuoteExchangeRate}
				/>
				{pricingStrategy === "overall_v1" ||
				pricingStrategy === "overall_v2" ? (
					<Box style={{ maxWidth: "135px" }} title="Free of cost">
						<TextInputField
							name="complimentary_count"
							label={
								<Inline alignItems="center" gap="1">
									<Text>Total FOC</Text>
									<Tooltip
										interactive
										content="Total number of free-of-cost pax in the trip, generally used for group tours."
									>
										<Box>
											<Icons.Info />
										</Box>
									</Tooltip>
								</Inline>
							}
							type="number"
							placeholder="e.g. 1"
							min={0}
							max={totalPax - 1}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								const value = e.currentTarget.value
								if (value) {
									if (Number(value || 0) <= totalPax - 1) {
										form.change("complimentary_count", Number(value))
									} else {
										alert("Total Foc must be less than no. of pax")
									}
								} else {
									form.change("complimentary_count", undefined)
								}
							}}
						/>
					</Box>
				) : null}
			</Inline>
			{options.map((o, index, arr) => (
				<Stack key={o.__id} gap="4" position="relative">
					{arr.length > 1 ? (
						<Box style={{ position: "sticky" }} top="0" zIndex="10">
							<Box
								position="sticky"
								top="0"
								zIndex="10"
								bgColor="emphasis"
								color="on_emphasis"
								paddingY="2"
								paddingX="4"
								rounded="md"
							>
								<Inline
									justifyContent="between"
									gap="1"
									alignItems="baseline"
									collapseBelow="md"
								>
									<Heading fontSize="md">{o.name}</Heading>
									<Text fontSize="sm">Option {index + 1}</Text>
								</Inline>
							</Box>
						</Box>
					) : null}

					{getRenderComponentByPricingStrategy(pricingStrategy, index)}

					<QuoteCommentsRemarksInputField optionIndex={index} />
				</Stack>
			))}
		</Stack>
	)
}

function QuotePriceInCostingCurrency({ optionIndex }: { optionIndex: number }) {
	const {
		totalCostPrice: total,
		totalHotelPrice: hotels,
		totalCabsPrice: cabs,
		otherExtrasPrice: otherExtras,
		flightCostPrice: flights,
		totalVisasPrice: visas,
	} = useTotalQuotePriceInCostingCurrency(optionIndex)

	const currencies = useCurrenciesFromCosting()

	if (currencies.length <= 1) return null

	return (
		<Grid gap="6">
			<Col sm={12} md={3} lg={2}>
				<Heading fontSize="md">Prices in Costing Currency</Heading>
				<Text color="accent" fontWeight="semibold">
					{total.currency}
				</Text>
			</Col>
			<Col>
				<Inline gap="4" collapseBelow="md">
					<Inline
						gap="2"
						paddingX="4"
						paddingY="2"
						rounded="md"
						bgColor="primary"
						borderWidth="1"
						borderColor="primary"
					>
						<Text fontWeight="semibold" color="primary">
							Total Cost Price:
						</Text>
						<Text fontSize="md">
							<Money money={total} showCurrency />
						</Text>
					</Inline>
					{total.amount ? (
						<Box
							paddingX="4"
							paddingY="2"
							rounded="md"
							borderWidth="1"
							overflow="auto"
						>
							<Inline gap="2">
								{([] as Array<[string, TMoney]>)
									.concat([
										["Hotels", hotels],
										["Transports/Activities", cabs],
										["Other Extras", otherExtras],
										["Flights", flights],
										["Visa", visas],
									])
									.filter(([_, money]) => money.amount)
									.map(([label, money], i) => (
										<Fragment key={label}>
											{i > 0 ? (
												<Stack
													padding="1"
													alignItems="center"
													justifyContent="center"
												>
													<Icons.Plus />
												</Stack>
											) : null}
											<Inline flexWrap="wrap" gap="1" whiteSpace="preserve">
												<Text fontSize="sm" color="muted">
													{label}
												</Text>
												<Text>
													<Money money={money} />
												</Text>
											</Inline>
										</Fragment>
									))}
							</Inline>
						</Box>
					) : null}
				</Inline>
			</Col>
		</Grid>
	)
}

function useCurrencyPairsArrayFieldValue() {
	const { value } =
		useFieldValue<INewQuoteState["currency_pairs_obj"]>("currency_pairs_obj")
	return useMemo(() => {
		if (!value) return []
		return currencyPairsObjectToArray(value)
	}, [value])
}

/**
 * Hook for fetching quotes components total and per component cost prices
 */
function useQuoteComponentPricesInCostingCurrency(optionIndex: number) {
	const quotePrices = useTotalQuotePriceInCostingCurrency(optionIndex)

	const quoteComponentPrices = useMemo(
		() => ({
			totalPrice: quotePrices.totalPrice,
			flightPrice: quotePrices.flightPrice,
			flightCostPrice: quotePrices.flightCostPrice,
			totalHotelPrice: quotePrices.totalHotelPrice,
			totalCabsOnlyPrice: quotePrices.totalCabsOnlyPrice,
			totalTravelActivitiesOnlyPrice:
				quotePrices.totalTravelActivitiesOnlyPrice,
			totalExtrasPrice: quotePrices.totalExtrasPrice,
			totalVisasPrice: quotePrices.totalVisasPrice,
		}),
		[quotePrices]
	)

	return quoteComponentPrices
}

export function AllowedPricingStrategyOptions() {
	const hasAdvancedStrategies = useHasFeatureFlag("component_wise_pricing")
	return (
		<>
			<option value="overall">Overall</option>
			<option value="per_person">Per-Person</option>
			{hasAdvancedStrategies ? (
				<>
					<option value="per_component">Per-Component</option>
					<option value="per_component_per_person">
						Per-Component Per-Person
					</option>
				</>
			) : null}
		</>
	)
}

export function PerPersonInclusionConfiguration({
	paxAllocation,
	optionIndex,
	totalExtrasPrice,
	hasCabs,
	children,
}: {
	paxAllocation: ReturnType<typeof allocatePax>["allocation"]
	optionIndex: number
	totalExtrasPrice: Array<TMoney>
	hasCabs: boolean
	children: TChildrenArray
}) {
	const hasAdultWithExtraBed = paxAllocation.some(
		(p) => p.type === "adult" && p.accommodation === "extra_bed"
	)

	const { value: include_children_in_cabs_start_age } = useFieldValue<boolean>(
		`options[${optionIndex}].per_person_cost_price_components.include_children_in_cabs_start_age`
	)
	const { value: exclude_adults_count_in_cabs } = useFieldValue<number>(
		`options[${optionIndex}].per_person_cost_price_components.exclude_adults_count_in_cabs`
	)
	const { value: no_of_adults } = useFieldValue<number>("no_of_adults")

	const includeChildrenInCabsStartAge = !isNaN(
		parseInt(String(include_children_in_cabs_start_age))
	)
		? Number(include_children_in_cabs_start_age)
		: false
	const excludeAdultsCountInCabs = !isNaN(
		parseInt(String(exclude_adults_count_in_cabs))
	)
		? exclude_adults_count_in_cabs
		: false

	const form = useForm()

	const childrenCountAboveFive = useMemo(
		() => children.filter((c) => c.age > 5).length,
		[children]
	)

	let adultCountToShow = 0
	let childCountShow = 0

	paxAllocation.forEach((p) => {
		if (p.type === "adult" && p.cabs_price > 0) {
			adultCountToShow += p.pax_count
		}
		if (p.type === "child" && p.cabs_price > 0) {
			childCountShow += p.pax_count
		}
	})

	return (
		<>
			<Text fontWeight="semibold">
				[{pluralize("Adult", no_of_adults, true)}
				{children?.length ? (
					<span>
						{" "}
						with {pluralize("Child", children.length, true)} (
						{childrenToQueryParams(children)})
					</span>
				) : null}
				]
			</Text>
			{hasCabs ? (
				<Stack gap="2">
					<Text>Transports Inclusions</Text>
					<Inline gap="2" alignItems="center">
						<SwitchInput
							checked={excludeAdultsCountInCabs !== false}
							onChange={(checked) => {
								if (checked) {
									form.batch(() => {
										form.change(
											`options[${optionIndex}].per_person_cost_price_components.exclude_adults_count_in_cabs`,
											1
										)
										form.change(
											`options[${optionIndex}].per_person_cost_price_components.include_adults_with_extra_bed_in_cabs`,
											1
										)
									})
								} else {
									form.change(
										`options[${optionIndex}].per_person_cost_price_components.exclude_adults_count_in_cabs`,
										undefined
									)
								}
							}}
						/>
						<Text fontWeight="semibold">Exc. Transport charges for:</Text>
						<Inline gap="1" alignItems="center">
							<TextInputField
								size="sm"
								type="number"
								style={{ maxWidth: "60px" }}
								placeholder="1"
								min={0}
								max={no_of_adults - 1}
								name={`options[${optionIndex}].per_person_cost_price_components.exclude_adults_count_in_cabs`}
								onChange={(e) => {
									form.change(
										`options[${optionIndex}].per_person_cost_price_components.include_adults_with_extra_bed_in_cabs`,
										1
									)
									form.change(
										`options[${optionIndex}].per_person_cost_price_components.exclude_adults_count_in_cabs`,
										Number(e.currentTarget.value)
									)
								}}
							/>
							<Text fontWeight="semibold">Adults</Text>
						</Inline>
					</Inline>
					{hasAdultWithExtraBed ? (
						<SwitchInputField
							name={`options[${optionIndex}].per_person_cost_price_components.include_adults_with_extra_bed_in_cabs`}
							label="Inc. AwEB in Transports"
							disabled={exclude_adults_count_in_cabs > 0}
						/>
					) : null}
					{children?.length ? (
						<Inline gap="2" alignItems="center">
							<SwitchInput
								checked={includeChildrenInCabsStartAge !== false}
								onChange={(checked) => {
									if (checked) {
										form.change(
											`options[${optionIndex}].per_person_cost_price_components.include_children_in_cabs_start_age`,
											6
										)
									} else {
										form.change(
											`options[${optionIndex}].per_person_cost_price_components.include_children_in_cabs_start_age`,
											undefined
										)
									}
								}}
							/>
							<Text fontWeight="semibold">Inc. Child:</Text>
							<Inline gap="1" alignItems="center">
								<TextInputField
									name={`options[${optionIndex}].per_person_cost_price_components.include_children_in_cabs_start_age`}
									size="sm"
									type="number"
									style={{ maxWidth: "60px" }}
									placeholder="6"
									min={0}
								/>
								<Text fontWeight="semibold">yrs and onwards</Text>
							</Inline>
						</Inline>
					) : null}
					{adultCountToShow > 0 || childCountShow > 0 ? (
						<Text fontWeight="semibold">
							[Transport cost for{" "}
							{[
								adultCountToShow > 0 &&
									pluralize("Adult", adultCountToShow, true),
								childCountShow > 0 && pluralize("Child", childCountShow, true),
							]
								.filter(Boolean)
								.join(" ")}
							]
						</Text>
					) : null}
				</Stack>
			) : null}
			{totalExtrasPrice?.length && childrenCountAboveFive ? (
				<Stack gap="2">
					<Text>Extras Inclusions</Text>
					<SwitchInputField
						name={`options[${optionIndex}].per_person_cost_price_components.include_children_in_extras`}
						label="Inc. Child (6yr and above)"
					/>
				</Stack>
			) : null}
		</>
	)
}

function usePerPersonAllocation(optionIndex: number) {
	const { value: costPriceCurrency } =
		useFieldValue<INewQuoteState["costing_currency"]>("costing_currency")
	const currencyPairs = useCurrencyPairsArrayFieldValue()
	const { hotels, hotelExtrasPrice } = useHotelsOption(optionIndex) || {}
	const { value: noOfAdults } = useFieldValue<number>("no_of_adults")
	const { value: children } = useFieldValue<TChildrenArray>("children")
	const { value: transportExtrasPrice } = useFieldValue<
		INewQuoteState["transportExtrasPrice"]
	>("transportExtrasPrice")
	const { value: otherExtrasPrice } =
		useFieldValue<INewQuoteState["otherExtrasPrice"]>("otherExtrasPrice")
	let { value: includePerPersonPrice } = useFieldValue<boolean>(
		"include_per_person_price"
	)
	const { value: visas } = useFieldValue<INewQuoteState["visas"]>("visas")

	includePerPersonPrice = isTruthy(includePerPersonPrice)

	const { value: include_children_in_cabs_start_age } = useFieldValue<boolean>(
		`options[${optionIndex}].per_person_cost_price_components.include_children_in_cabs_start_age`
	)
	const { value: include_adults_with_extra_bed_in_cabs } =
		useFieldValue<boolean>(
			`options[${optionIndex}].per_person_cost_price_components.include_adults_with_extra_bed_in_cabs`
		)
	const { value: include_children_in_extras } = useFieldValue<boolean>(
		`options[${optionIndex}].per_person_cost_price_components.include_children_in_extras`
	)
	const { value: exclude_adults_count_in_cabs } = useFieldValue<number>(
		`options[${optionIndex}].per_person_cost_price_components.exclude_adults_count_in_cabs`
	)
	const { value: priceMismatchBetweenPerPersonAndTotal } = useFieldValue<
		boolean | undefined
	>(`options[${optionIndex}].price_mismatch_between_per_person_and_total`)
	const { value: perPersonAllocationNotPossible } = useFieldValue<
		boolean | undefined
	>(`options[${optionIndex}].per_person_allocation_not_possible`)

	const includeChildrenInCabsStartAge = !isNaN(
		parseInt(String(include_children_in_cabs_start_age))
	)
		? Number(include_children_in_cabs_start_age)
		: false

	const includeAdultsWithExtraBedInCabs = isTruthy(
		include_adults_with_extra_bed_in_cabs
	)
	const includeChildrenInExtras = isTruthy(include_children_in_extras)
	const { totalCostPrice, flightCostPrice: flightCostPriceInCostingCurrency } =
		useTotalQuotePriceInCostingCurrency(optionIndex)
	const totalCostPriceExcludingFlights = Number(
		formatMoneyByDecimal(
			subtractMoney(totalCostPrice, flightCostPriceInCostingCurrency)
		)
	)
	const form = useForm()
	const { value: cabs } = useFieldValue<INewQuoteState["cabs"]>("cabs")
	const { value: flights } = useFieldValue<INewQuoteState["flights"]>("flights")

	// Flight allocations including cost and given price per pax.
	const flightAllocations: TFlightPaxAllocation[] = useMemo(() => {
		return flightPaxAllocation({
			flightService: flights,
			children,
			costingCurrency: costPriceCurrency,
			currencyPairs,
		})
	}, [flights, children, costPriceCurrency, currencyPairs])

	// only flight allocations are used for per-person costing, so we convert
	// the flight allocations to TPricePerPersonAllocation format for use in the
	// per-person costing allocation.
	const flightCostAllocations: TPricePerPersonAllocation[] = useMemo(
		() =>
			flightAllocations.map((flight) => ({
				id: flight.id,
				service: "flight",
				// TPricePerPersonAllocation has no "infant" type, so infants are stored as "child" with ages
				type: flight.paxType === "infant" ? "child" : flight.paxType,
				per_pax_label: flight.label,
				total_pax_label: flight.totalPaxLabel,
				pax_count: flight.paxCount,
				currency: costPriceCurrency,
				price: formatMoneyByDecimal(
					moneyParseByDecimal(flight.costPrice, costPriceCurrency)
				),
				ages: flight.ages,
			})),
		[flightAllocations, costPriceCurrency]
	)

	const [
		{
			allocation: paxAllocation,
			error: paxAllocationError,
			warnings: paxAllocationWarnings,
		},
		setPaxAllocation,
	] = useState<{
		allocation: ReturnType<typeof allocatePax>["allocation"] | undefined
		warnings: Array<string> | undefined
		error: Error | undefined
	}>({ allocation: undefined, error: undefined, warnings: undefined })
	const totalExtrasPrice = useMemo(
		() =>
			addMoneyFromDifferentCurrencies(
				([] as Array<TMoney>)
					.concat(hotelExtrasPrice || [])
					.concat(transportExtrasPrice)
					.concat(otherExtrasPrice)
			),
		[hotelExtrasPrice, transportExtrasPrice, otherExtrasPrice]
	)

	useEffect(() => {
		try {
			const { allocation, warnings } = allocatePax({
				noOfAdults,
				children,
				hotels: hotels || [],
				cabs,
				includeAdultsWithExtraBedInCabs,
				includeChildrenInCabsStartAge,
				flights,
				totalExtrasPrice,
				includeChildrenInExtras,
				costingCurrency: costPriceCurrency,
				currencyExchangePairs: currencyPairs,
				visas,
				excludeAdultsCountInCabs:
					exclude_adults_count_in_cabs > 0 ? exclude_adults_count_in_cabs : 0,
			})
			setPaxAllocation({ allocation, error: undefined, warnings })
		} catch (_e) {
			setPaxAllocation({
				error: _e as Error,
				allocation: undefined,
				warnings: undefined,
			})
		}
	}, [
		hotels,
		cabs,
		flights,
		noOfAdults,
		children,
		setPaxAllocation,
		includeAdultsWithExtraBedInCabs,
		includeChildrenInCabsStartAge,
		totalExtrasPrice,
		includeChildrenInExtras,
		costPriceCurrency,
		currencyPairs,
		visas,
		exclude_adults_count_in_cabs,
	])
	const totalPriceUsingAllocation = (paxAllocation || []).reduce<number>(
		(total, { price, pax_count }) =>
			total + Number(formatMoneyByDecimal(multipleMoneyBy(price, pax_count))),
		0
	)

	const resetPerPersonAllocation = useCallback(() => {
		form.batch(() => {
			form.change("per_person_cost_price", undefined)
			form.change("per_person_given_price", undefined)
			form.change(
				`options[${optionIndex}].price_mismatch_between_per_person_and_total`,
				undefined
			)
			form.change(
				`options[${optionIndex}].per_person_allocation_not_possible`,
				undefined
			)
			form.change(
				`options[${optionIndex}].per_person_cost_price_components.total`,
				undefined
			)
			form.change(`options[${optionIndex}].cost_prices_per_person`, undefined)
			form.change(
				`options[${optionIndex}].flight_given_prices_by_id`,
				undefined
			)
		})
	}, [form, optionIndex])

	useEffect(() => {
		if (!includePerPersonPrice) {
			const h = setTimeout(() => {
				resetPerPersonAllocation()
			}, 100)
			return () => {
				clearTimeout(h)
			}
		}

		return () => {
			resetPerPersonAllocation()
		}
	}, [includePerPersonPrice, resetPerPersonAllocation])

	// allocations only for accomodations (-flights) only costing
	const accommodationAllocations: TPricePerPersonAllocation[] = useMemo(
		() =>
			(paxAllocation ?? [])?.map(
				({
					id,
					price,
					type,
					per_pax_label,
					total_pax_label,
					accommodation,
					room_sharing_type,
					pax_count,
					currency,
					ages,
				}) => {
					return {
						id,
						service: "accommodation",
						currency,
						price: formatMoneyByDecimal(price),
						type,
						per_pax_label,
						total_pax_label,
						pax_count,
						ages,
						accommodation,
						room_sharing_type,
					}
				}
			),
		[paxAllocation]
	)

	// synchronise the total cost and selling per-person price
	useEffect(() => {
		if (includePerPersonPrice) {
			const h = setTimeout(() => {
				form.batch(() => {
					form.change(
						`options[${optionIndex}].per_person_cost_price_components.total`,
						!paxAllocation
							? undefined
							: formatMoneyByDecimal(
									moneyParseByDecimal(
										totalPriceUsingAllocation,
										costPriceCurrency
									)
								)
					)
					const priceMismatch = !paxAllocation
						? false
						: Number(
								formatMoneyByDecimal(
									moneyParseByDecimal(
										totalCostPriceExcludingFlights,
										costPriceCurrency
									)
								)
							) >
							Number(
								formatMoneyByDecimal(
									moneyParseByDecimal(
										totalPriceUsingAllocation,
										costPriceCurrency
									)
								)
							)

					const perPersonAllocationNotPossible =
						!paxAllocation || paxAllocationError || priceMismatch ? true : false
					form.change(
						`options[${optionIndex}].price_mismatch_between_per_person_and_total`,
						priceMismatch
					)
					form.change(
						`options[${optionIndex}].per_person_allocation_not_possible`,
						perPersonAllocationNotPossible
					)
					form.change(
						`options[${optionIndex}].cost_prices_per_person`,
						!paxAllocation || priceMismatch
							? undefined
							: [...accommodationAllocations, ...flightCostAllocations]
					)

					// this is for given price per pax for flights to persist, so we can use it to calculate the total given price for flights
					form.change(
						`options[${optionIndex}].flight_given_prices_by_id`,
						!paxAllocation || priceMismatch
							? undefined
							: flightAllocations.reduce<Record<string, number>>(
									(acc, flight) => {
										acc[flight.id] = flight.givenPrice
										return acc
									},
									{}
								)
					)
				})
			}, 100)

			return () => clearTimeout(h)
		}
	}, [
		optionIndex,
		paxAllocation,
		includePerPersonPrice,
		form,
		costPriceCurrency,
		totalPriceUsingAllocation,
		totalCostPriceExcludingFlights,
		paxAllocationError,
		flightAllocations,
		flightCostAllocations,
		accommodationAllocations,
	])

	return {
		includePerPersonPrice,
		paxAllocation,
		paxAllocationError,
		paxAllocationWarnings,
		priceMismatchBetweenPerPersonAndTotal,
		totalCostPriceExcludingFlights,
		totalPriceUsingAllocation,
		perPersonAllocationNotPossible,
		flightAllocations,
	}
}

export type TPerPersonAllocationResult = ReturnType<
	typeof usePerPersonAllocation
>

function PerPersonCostPrice({
	optionIndex,
	enableFlightRounding,
	enableSyncRoundingAmongOptions,
}: {
	optionIndex: number
	enableFlightRounding: boolean
	enableSyncRoundingAmongOptions: boolean
}) {
	const { value: children } = useFieldValue<TChildrenArray>("children")
	const { value: costPriceCurrency } =
		useFieldValue<INewQuoteState["costing_currency"]>("costing_currency")
	const { hotelExtrasPrice } = useHotelsOption(optionIndex) || {}
	const { value: transportExtrasPrice } = useFieldValue<
		INewQuoteState["transportExtrasPrice"]
	>("transportExtrasPrice")
	const { value: otherExtrasPrice } =
		useFieldValue<INewQuoteState["otherExtrasPrice"]>("otherExtrasPrice")
	const { value: cabs } = useFieldValue<INewQuoteState["cabs"]>("cabs")
	const quoteComponentPrices =
		useQuoteComponentPricesInCostingCurrency(optionIndex)

	const {
		includePerPersonPrice,
		paxAllocation,
		paxAllocationError,
		paxAllocationWarnings,
		priceMismatchBetweenPerPersonAndTotal,
		totalCostPriceExcludingFlights,
		totalPriceUsingAllocation,
		flightAllocations,
	} = usePerPersonAllocation(optionIndex)

	const totalExtrasPrice = useMemo(
		() =>
			addMoneyFromDifferentCurrencies(
				([] as Array<TMoney>)
					.concat(hotelExtrasPrice || [])
					.concat(transportExtrasPrice)
					.concat(otherExtrasPrice)
			),
		[hotelExtrasPrice, transportExtrasPrice, otherExtrasPrice]
	)

	const hasCabs = useMemo(() => {
		return Boolean(
			cabs?.daywise_services?.some((s) =>
				s.services?.some((s) => s.type === "transport_service" && s.service)
			)
		)
	}, [cabs])

	return (
		<Stack gap="6">
			<QuotePriceInCostingCurrency optionIndex={optionIndex} />
			{includePerPersonPrice ? (
				<Grid gap="6">
					{paxAllocation?.length ? (
						<Col sm={12} lg={4} xl={3}>
							<Stack gap="4">
								<Heading fontSize="md" color="accent">
									Per-Person Settings
								</Heading>
								<PerPersonInclusionConfiguration
									children={children}
									paxAllocation={paxAllocation}
									optionIndex={optionIndex}
									totalExtrasPrice={totalExtrasPrice}
									hasCabs={hasCabs}
								/>
							</Stack>
						</Col>
					) : null}
					<Col>
						<Stack gap="4">
							{!paxAllocation ? (
								<PerPersonAllocationNotPossibleError
									paxAllocationError={paxAllocationError}
								/>
							) : (
								<>
									<PerPersonAllocationWarnings
										paxAllocationWarnings={paxAllocationWarnings}
									/>
									<PerPersonCostPriceSegregationTable
										costPriceCurrency={costPriceCurrency}
										paxAllocation={paxAllocation}
										flightAllocations={flightAllocations}
									/>
								</>
							)}
							{priceMismatchBetweenPerPersonAndTotal ? (
								<PerPersonAllocationPriceMismatchError
									costPriceCurrency={costPriceCurrency}
									totalCostPriceExcludingFlights={
										totalCostPriceExcludingFlights
									}
									totalPriceUsingAllocation={totalPriceUsingAllocation}
								/>
							) : null}
						</Stack>
					</Col>
				</Grid>
			) : null}
			<Stack gap="4">
				<Heading fontSize="md" color="accent">
					Set Markup, Tax and Rounding
				</Heading>
				<SellingPrice
					optionIndex={optionIndex}
					quoteComponentPrices={quoteComponentPrices}
					enableSyncRoundingAmongOptions={enableSyncRoundingAmongOptions}
					enableFlightRounding={enableFlightRounding}
				/>
			</Stack>
		</Stack>
	)
}

function PerComponentPerPersonCostPriceMarkupTaxPricing({
	optionIndex,
}: {
	optionIndex: number
}) {
	const { value: costPriceCurrency } = useFieldValue<string>("costing_currency")
	const { value: children } = useFieldValue<TChildrenArray>("children")
	const { value: transportExtrasPrice } = useFieldValue<
		INewQuoteState["transportExtrasPrice"]
	>("transportExtrasPrice")
	const { value: otherExtrasPrice } =
		useFieldValue<INewQuoteState["otherExtrasPrice"]>("otherExtrasPrice")
	const { hotelExtrasPrice } = useHotelsOption(optionIndex) || {}
	const { value: cabs } = useFieldValue<INewQuoteState["cabs"]>("cabs")

	const quotePrices = useTotalQuotePriceInCostingCurrency(optionIndex)
	const perPersonAllocationResult = usePerPersonAllocation(optionIndex)

	const totalExtrasPrice = useMemo(
		() =>
			addMoneyFromDifferentCurrencies(
				([] as Array<TMoney>)
					.concat(hotelExtrasPrice || [])
					.concat(transportExtrasPrice)
					.concat(otherExtrasPrice)
			),
		[hotelExtrasPrice, transportExtrasPrice, otherExtrasPrice]
	)

	const hasCabs = useMemo(() => {
		return Boolean(
			cabs?.daywise_services?.some((s) =>
				s.services?.some((s) => s.type === "transport_service" && s.service)
			)
		)
	}, [cabs])

	const quoteComponentPrices = useMemo(() => {
		return {
			totalPrice: quotePrices.totalPrice,
			flightPrice: quotePrices.flightPrice,
			flightCostPrice: quotePrices.flightCostPrice,
			totalHotelPrice: quotePrices.totalHotelPrice,
			totalCabsOnlyPrice: quotePrices.totalCabsOnlyPrice,
			totalTravelActivitiesOnlyPrice:
				quotePrices.totalTravelActivitiesOnlyPrice,
			totalExtrasPrice: quotePrices.totalExtrasPrice,
			totalVisasPrice: quotePrices.totalVisasPrice,
		}
	}, [quotePrices])

	return (
		<ComponentMarkupTaxPerPersonPricingBifurcationWithRounding
			optionIndex={optionIndex}
			costPriceCurrency={costPriceCurrency}
			quoteComponentPrices={quoteComponentPrices}
			children={children}
			totalExtrasPrice={totalExtrasPrice}
			hasCabs={hasCabs}
			perPersonAllocationResult={perPersonAllocationResult}
		/>
	)
}

export function PerPersonAllocationNotPossibleError({
	paxAllocationError,
}: {
	paxAllocationError?: Error
}) {
	return (
		<Alert status="error">
			<Stack gap="1">
				<Text>
					Per-person allocation is not possible with the given services and
					prices combination.
				</Text>
				{paxAllocationError?.message ? (
					<Text fontSize="sm">
						<b>Reason:</b> {paxAllocationError.message}
					</Text>
				) : null}
			</Stack>
		</Alert>
	)
}

export function PerPersonAllocationWarnings({
	paxAllocationWarnings,
}: {
	paxAllocationWarnings?: string[]
}) {
	if (!paxAllocationWarnings?.length) {
		return null
	}
	return (
		<Alert
			status="warning"
			title="Below are the warnings when distributing prices amount pax"
		>
			<Stack gap="1" as="ul" listStyleType="decimal" marginLeft="4">
				{paxAllocationWarnings.map((m, index) => (
					<Box as="li" key={index}>
						<Text fontSize="sm">{m}</Text>
					</Box>
				))}
			</Stack>
		</Alert>
	)
}

export function PerPersonAllocationPriceMismatchError({
	costPriceCurrency,
	totalCostPriceExcludingFlights,
	totalPriceUsingAllocation,
}: {
	costPriceCurrency: string
	totalCostPriceExcludingFlights: number
	totalPriceUsingAllocation: number
}) {
	return (
		<Alert title="Price Mismatch" status="error">
			<Stack gap="2">
				<Text>
					When adding per-person prices, the total is not equal to the Total of
					all cost prices. Total using Given Prices:{" "}
					<Money
						currency={costPriceCurrency}
						fontWeight="semibold"
						amount={totalCostPriceExcludingFlights}
						showCurrency
					/>
					. Total using Per Person:{" "}
					<Money
						currency={costPriceCurrency}
						fontWeight="semibold"
						amount={totalPriceUsingAllocation}
						showCurrency
					/>
				</Text>
				<Text>
					<Icons.LightBulb /> This generally happens when there are more
					rooms/beds than required.
				</Text>
				<Text>
					<Icons.LightBulb /> This may also occur if excluded transport adults
					are equal to or greater than the total adults.
				</Text>
			</Stack>
		</Alert>
	)
}

export function PerPersonCostPriceSegregationTable({
	costPriceCurrency,
	paxAllocation,
	flightAllocations,
}: {
	costPriceCurrency: string
	paxAllocation: ReturnType<typeof usePerPersonAllocation>["paxAllocation"]
	flightAllocations: TFlightPaxAllocation[]
}) {
	if (!paxAllocation?.length && !flightAllocations?.length) {
		return null
	}

	// rows for both accommodation and flight allocations
	const rows = [
		...(paxAllocation ?? []).map((p) => [
			<Text fontWeight="semibold">
				{p.per_pax_label} x {p.total_pax_label}
			</Text>,
			p.price ? <Money money={p.price} fontWeight="semibold" /> : null,
			<Inline gap="4">
				{[
					p.hotels_price
						? joinAttributes(
								<Text>
									<Text as="span" color="muted" fontSize="sm">
										Hotels
									</Text>
									:{" "}
									<Money amount={p.hotels_price} currency={costPriceCurrency} />
								</Text>
							)
						: null,
					p.cabs_price
						? joinAttributes(
								<Text>
									<Text as="span" color="muted" fontSize="sm">
										Transports
									</Text>
									: <Money amount={p.cabs_price} currency={costPriceCurrency} />
								</Text>
							)
						: null,
					p.travel_activities_price
						? joinAttributes(
								<Text>
									<Text as="span" color="muted" fontSize="sm">
										Activities
									</Text>
									:{" "}
									<Money
										amount={p.travel_activities_price}
										currency={costPriceCurrency}
									/>
								</Text>
							)
						: null,
					p.visas_price
						? joinAttributes(
								<Text>
									<Text as="span" color="muted" fontSize="sm">
										Visa
									</Text>
									:{" "}
									<Money amount={p.visas_price} currency={costPriceCurrency} />
								</Text>
							)
						: null,
					p.extras_price
						? joinAttributes(
								<Text>
									<Text as="span" color="muted" fontSize="sm">
										Extras
									</Text>
									:{" "}
									<Money amount={p.extras_price} currency={costPriceCurrency} />
								</Text>
							)
						: null,
				]
					.filter(Boolean)
					.map((t, i) => (
						<Box key={i}>{t}</Box>
					))}
			</Inline>,
		]),
		...(flightAllocations ?? []).map((f) => [
			<Text fontWeight="semibold">
				{f.label} x {f.totalPaxLabel}
			</Text>,
			<Money
				amount={f.costPrice}
				currency={costPriceCurrency}
				fontWeight="semibold"
			/>,
			<Text>
				<Text as="span" color="muted" fontSize="sm">
					Flights
				</Text>
				: <Money amount={f.costPrice} currency={costPriceCurrency} />
			</Text>,
		]),
	]
	return (
		<Table
			headers={["For", `Price (${costPriceCurrency})`, "Segregation"]}
			bordered
			responsive
			alignCols={{ 1: "center" }}
			rows={rows}
		/>
	)
}

function KeyValueDetails({
	label,
	children,
}: {
	label: React.ReactNode
	children: React.ReactNode
}) {
	return (
		<Stack gap="1">
			<Box
				color="muted"
				textTransform="uppercase"
				letterSpacing="wider"
				fontSize="sm"
				fontWeight="semibold"
			>
				{label}
			</Box>
			<Text fontSize="md" fontWeight="semibold" whiteSpace="preserve">
				{children}
			</Text>
		</Stack>
	)
}

export function StepIcon(props: React.ComponentProps<typeof Box>) {
	return (
		<Box
			display="inlineFlex"
			size="12"
			alignItems="center"
			justifyContent="center"
			bgColor="primary_emphasis"
			color="on_emphasis"
			rounded="full"
			{...props}
		/>
	)
}

function useTotalQuotePrice(optionIndex: number) {
	const totalHotelPrice = useTotalHotelPrice(optionIndex)
	const [
		totalCabsAndTravelActivitiesWithExtrasPrice,
		{ cabsPrice, travelActivitiesPrice, extrasPrice },
	] = useTotalCabsAndTravelActivitesWithExtrasPrice()
	const { value: flightPrice } =
		useFieldValue<INewQuoteState["flightPrice"]>("flightPrice")
	const { value: flightCostPrice } =
		useFieldValue<INewQuoteState["flightCostPrice"]>("flightCostPrice")
	const { value: otherExtrasPrice } =
		useFieldValue<INewQuoteState["otherExtrasPrice"]>("otherExtrasPrice")
	const { value: visasPrice } =
		useFieldValue<INewQuoteState["visasPrice"]>("visasPrice")

	const totalPrice = useMemo(
		() =>
			addMoneyFromDifferentCurrencies([
				...totalHotelPrice,
				...totalCabsAndTravelActivitiesWithExtrasPrice,
				...otherExtrasPrice,
				...flightCostPrice,
				...visasPrice,
			]),
		[
			totalHotelPrice,
			totalCabsAndTravelActivitiesWithExtrasPrice,
			otherExtrasPrice,
			flightCostPrice,
			visasPrice,
		]
	)
	return {
		totalPrice,
		flightPrice,
		flightCostPrice,
		totalHotelPrice,
		totalCabsAndTravelActivitiesWithExtrasPrice:
			totalCabsAndTravelActivitiesWithExtrasPrice,
		cabsPrice,
		travelActivitiesPrice,
		extrasPrice,
		otherExtrasPrice,
		visasPrice,
	}
}

function useTotalQuotePriceInCostingCurrency(optionIndex: number) {
	const {
		flightPrice,
		flightCostPrice,
		totalHotelPrice,
		totalCabsAndTravelActivitiesWithExtrasPrice,
		cabsPrice,
		travelActivitiesPrice,
		otherExtrasPrice,
		extrasPrice,
		visasPrice,
	} = useTotalQuotePrice(optionIndex)
	const { value: costingCurrency } =
		useFieldValue<INewQuoteState["costing_currency"]>("costing_currency")
	const currencyPairs = useCurrencyPairsArrayFieldValue()
	return useMemo(() => {
		const converter = makeCurrencyConverter(currencyPairs, true)

		const totalHotelPriceInCostingCurrency = converter(
			costingCurrency,
			totalHotelPrice
		)
		const totalCabsPriceInCostingCurrency = converter(
			costingCurrency,
			totalCabsAndTravelActivitiesWithExtrasPrice
		)
		const otherExtrasPriceInCostingCurrency = converter(
			costingCurrency,
			otherExtrasPrice
		)

		const extrasPriceInCostingCurrency = converter(costingCurrency, extrasPrice)

		const totalTravelActivitiesOnlyPriceInCostingCurrency = converter(
			costingCurrency,
			travelActivitiesPrice
		)

		const totalCabsOnlyPriceInCostingCurrency = converter(
			costingCurrency,
			cabsPrice
		)

		const flightPriceInCostingCurrency = converter(costingCurrency, flightPrice)

		const flightCostPriceInCostingCurrency = converter(
			costingCurrency,
			flightCostPrice
		)

		const visasServicesPriceInCostingCurrency = converter(
			costingCurrency,
			visasPrice
		)
		return {
			totalPrice: addMoney(
				totalHotelPriceInCostingCurrency,
				totalCabsPriceInCostingCurrency,
				otherExtrasPriceInCostingCurrency,
				flightPriceInCostingCurrency,
				visasServicesPriceInCostingCurrency
			), //This is the total price in costing currency with flight Given Price in Costing Currency
			totalCostPrice: addMoney(
				totalHotelPriceInCostingCurrency,
				totalCabsPriceInCostingCurrency,
				otherExtrasPriceInCostingCurrency,
				flightCostPriceInCostingCurrency,
				visasServicesPriceInCostingCurrency
			), //This is the total cost price in costing currency with flight Cost Price in Costing Currency
			flightPrice: flightPriceInCostingCurrency,
			flightCostPrice: flightCostPriceInCostingCurrency,
			totalHotelPrice: totalHotelPriceInCostingCurrency,
			totalCabsPrice: totalCabsPriceInCostingCurrency,
			otherExtrasPrice: otherExtrasPriceInCostingCurrency,
			extrasPrice: extrasPriceInCostingCurrency,
			totalTravelActivitiesOnlyPrice:
				totalTravelActivitiesOnlyPriceInCostingCurrency,
			totalCabsOnlyPrice: totalCabsOnlyPriceInCostingCurrency,
			totalExtrasPrice: addMoney(
				otherExtrasPriceInCostingCurrency,
				extrasPriceInCostingCurrency
			),
			totalVisasPrice: visasServicesPriceInCostingCurrency,
		}
	}, [
		flightPrice,
		currencyPairs,
		costingCurrency,
		totalHotelPrice,
		totalCabsAndTravelActivitiesWithExtrasPrice,
		otherExtrasPrice,
		cabsPrice,
		travelActivitiesPrice,
		extrasPrice,
		flightCostPrice,
		visasPrice,
	])
}

function PreviewData({
	startDate,
	days,
	adults,
	children,
}: {
	startDate: Date
	days: number
	adults: number
	children: TChildrenArray
}) {
	const { value: options } = useFieldValue<INewQuoteState["options"]>("options")
	// services
	const { value: cabs } = useFieldValue<INewQuoteState["cabs"]>("cabs")
	const { value: transportExtras } =
		useFieldValue<INewQuoteState["transportExtras"]>("transportExtras")
	const { value: otherExtras } =
		useFieldValue<INewQuoteState["otherExtras"]>("otherExtras")
	const { value: flights } = useFieldValue<INewQuoteState["flights"]>("flights")
	const { value: complimentaryCount } = useFieldValue<number>(
		"complimentary_count"
	)
	const { value: pricingStrategy } =
		useFieldValue<TGivenQuotePricingStrategy>("pricing_strategy")
	const id = useRef<number>(Math.random())
	const normalizedCabs = useMemo(() => normalizeDaywiseServices(cabs), [cabs])
	const quoteCabs = useMemo(
		() =>
			normalizedCabs.cabs
				.map((i) => ({
					...i,
					quote_id: id.current,
				}))
				.sort((a, b) => {
					return a.date > b.date ? 1 : a.date < b.date ? -1 : 0
				}),

		[normalizedCabs, id]
	)
	const givenPriceForCabs = normalizedCabs.given_price_of_cabs_only
	const givenPriceForActivities = normalizedCabs.given_price_of_activities_only

	const quoteTravelActivities = useMemo(
		() =>
			normalizeDaywiseServices(cabs)
				.travel_activities.map((i) => ({
					...i,
					quote_id: id.current,
				}))
				.sort((a, b) => {
					return a.date > b.date ? 1 : a.date < b.date ? -1 : 0
				}),

		[cabs, id]
	)

	const quoteCabExtras = useMemo(
		() =>
			normalizeCabExtras(transportExtras)
				.map((i) => ({
					...i,
					quote_id: id.current,
				}))
				.sort((a, b) => {
					return a.date && b.date ? (a.date > b.date ? 1 : -1) : 0
				}),
		[transportExtras, id]
	)
	const quoteOtherExtras = useMemo(
		() =>
			normalizeOtherExtras(otherExtras)
				.map((i) => ({
					...i,
					quote_id: id.current,
				}))
				.sort((a, b) => {
					return a.date && b.date ? (a.date > b.date ? 1 : -1) : 0
				}),
		[otherExtras, id]
	)
	const errorsInCabs = useMemo(() => {
		const errorsInCabs: Array<{
			message: React.ReactNode
		}> = []
		if (
			quoteCabs.length &&
			(!givenPriceForCabs || !givenPriceForCabs.filter((t) => t.amount).length)
		) {
			errorsInCabs.push({
				message: "Total Transportation price is ZERO.",
			})
		}
		// now we will find if there is any misconfiguration with cab types
		// Here are some mis-configurations
		// - 1 Innova vs 2 Innova
		// - 1 Innova + 1 WagonR vs 2 Innova + 1 WagonR
		// Here are some valid configuration
		// - 1 Innova vs 2 WagonR
		// - 2 Innova vs 1 Innova + 1 WagonR

		// we will start by creating a group of cab types provided per tsp and date
		const cabConfigurationPerTransportService = quoteCabs.reduce<{
			[transportServiceIdPerDate: string]: {
				date: string
				transport_service: (typeof quoteCabs)[number]["transport_service"]
				cabs: Array<string>
				countPerCab: { [key: string]: number }
				noOfCabs: 0
			}
		}>((config, cab) => {
			if (cab.date && cab.transport_service && cab.cab_type && cab.no_of_cabs) {
				const key = `${cab.transport_service.id}_${String(cab.date)}`
				if (!config[key]) {
					config[key] = {
						date: cab.date,
						transport_service: cab.transport_service,
						cabs: [],
						countPerCab: {},
						noOfCabs: 0,
					}
				}
				config[key].cabs.push(cab.cab_type.name)
				config[key].countPerCab[cab.cab_type.name] = cab.no_of_cabs
				config[key].noOfCabs += cab.no_of_cabs
			}
			return config
		}, {})
		// now we will get unique cab types for each configuration
		// This will element these (possibly) valid cases
		// - 1 Innova vs 2 WagonR
		// - 2 Innova vs 1 Innova + 1 WagonR
		const cabTypesConfig = Object.values(
			Object.values(cabConfigurationPerTransportService).reduce<{
				[key: string]: Array<
					$Values<typeof cabConfigurationPerTransportService>
				>
			}>((u, config) => {
				const cabs = config.cabs.concat([])
				cabs.sort()
				const key = cabs.join("###")
				if (!u[key]) {
					u[key] = []
				}
				u[key].push({
					...config,
					cabs,
				})
				return u
			}, {})
		)
		// And we will find of we have some of these cases
		// - 1 Innova vs 2 Innova
		// - 1 Innova + 1 WagonR vs 2 Innova + 1 WagonR
		for (const cabs of cabTypesConfig) {
			// something might be wrong here
			// let create possible groups
			const groups = Object.values(
				cabs.reduce<{
					[key: string]: (typeof cabs)[number] & { services: typeof cabs }
				}>((groups, cab) => {
					const key = cab.cabs
						.map((c) => `${c}##${cab.countPerCab[c]}`)
						.join(",")
					if (!groups[key]) {
						groups[key] = { ...cab, services: [] }
					}
					groups[key].services.push(cab)
					return groups
				}, {})
			)
			if (groups.length > 1) {
				// now we will add misconfiguration messages
				const issues = []
				for (const cab of groups) {
					issues.push(
						<Text>
							Day:{" "}
							{cab.services
								.map(
									({ date }) =>
										getDiff(utcTimestampToLocalDate(date), startDate, "day") + 1
								)
								.join(",")}
							{" - "}
							<Text as="span" fontWeight="semibold">
								{cab.cabs.map((c) => `${cab.countPerCab[c]} ${c}`).join(" + ")}
							</Text>
						</Text>
					)
				}
				errorsInCabs.push({
					message: (
						<Box>
							<Text fontWeight="semibold">Possible Issue</Text>
							<Stack paddingLeft="4" as="ul" listStyleType="disc">
								{issues.map((issue, index) => (
									<Box key={index} as="li">
										{issue}
									</Box>
								))}
							</Stack>
						</Box>
					),
				})
			}
		}
		return errorsInCabs
	}, [quoteCabs, startDate, givenPriceForCabs])

	const errorsInTravelActivities = useMemo(() => {
		if (!quoteTravelActivities.length) return []

		const hasValidPrice = givenPriceForActivities?.some((t) => t.amount)
		const hasInvalidFOC = quoteTravelActivities.some((activity) =>
			activity.ticket_tourist_configurations?.some(
				(config) => config.complementary >= config.quantity
			)
		)
		if (!hasInvalidFOC && hasValidPrice) return []
		const message: { message: string }[] = []

		if (!hasValidPrice) {
			message.push({
				message: "All travel activities prices are missing or zero",
			})
		}
		if (hasInvalidFOC) {
			message.push({
				message:
					"There is a misconfiguration in FOC for some travel activities.",
			})
		}
		return message
	}, [quoteTravelActivities, givenPriceForActivities])

	const errorsInFlight = useMemo(() => {
		// by default flights contains one flight with given price 0 and if user filled the data there should be at least one group
		if (!flights.flights?.[0]?.groups?.length) return []

		const errors: Array<{ message: string }> = []

		// Check each flight for invalid prices
		flights.flights?.forEach((flight, index) => {
			const calculatedPrice = Number(flight.pricing.calculated_price)
			const givenPrice = Number(flight.pricing.given_price)

			// Check if calculated price is invalid
			const isCalculatedPriceInvalid =
				isNaN(calculatedPrice) || calculatedPrice <= 0
			// Check if given price is invalid
			const isGivenPriceInvalid = isNaN(givenPrice) || givenPrice <= 0

			// Add separate errors for each price type
			if (isCalculatedPriceInvalid) {
				errors.push({
					message: `Flight ${index + 1}: Cost price is missing or ZERO`,
				})
			}

			if (isGivenPriceInvalid) {
				errors.push({
					message: `Flight ${index + 1}: Given/Selling price is missing or ZERO`,
				})
			}
		})

		return errors
	}, [flights])

	return (
		<Container fluid paddingY="8" bgColor="accent">
			<Stack gap="1">
				<Heading as="h3" id="quote_summary">
					Summary
				</Heading>
				<Text color="muted">
					Please review the quote's data before creating.
				</Text>
			</Stack>
			<Box>
				<Box
					padding="4"
					marginY="4"
					marginBottom="8"
					borderWidth="1"
					rounded="md"
					bgColor="default"
				>
					<Inline gap="8" flexWrap="wrap">
						<KeyValueDetails label="Start Date">
							{formatDate(startDate, "DD MMM, YYYY")}
						</KeyValueDetails>
						<KeyValueDetails label="Duration">
							{days > 1
								? `${pluralize("Nights", days - 1, true)}, ${pluralize(
										"Day",
										days,
										true
									)}`
								: pluralize("Day", days, true)}
						</KeyValueDetails>
						<KeyValueDetails label="Pax">
							{pluralize("Adult", adults, true)}
							{children.length ? (
								<span> with {childrenToQueryParams(children)} Children</span>
							) : null}

							{pricingStrategy === "overall_v1" ||
							pricingStrategy === "overall_v2" ? (
								complimentaryCount > 0 ? (
									<span> ({complimentaryCount} FOC)</span>
								) : null
							) : null}
						</KeyValueDetails>
					</Inline>
				</Box>
				<Box marginBottom="8">
					<Stack gap="4">
						{options.map((o, index, arr) => (
							<Stack gap="2" key={o.__id}>
								{arr.length > 1 ? (
									<Heading color="accent">
										Option {index + 1}: {o.name}
									</Heading>
								) : null}
								<QuoteHotelsOptionPreview
									key={o.__id}
									optionIndex={index}
									id={id.current}
									startDate={startDate}
									nights={days - 1}
									adults={adults}
									children={children}
								/>
							</Stack>
						))}
					</Stack>
				</Box>
				<Stack marginBottom="8" gap="8">
					<QuoteTransportsAndActivityTickets
						transports={quoteCabs}
						transportExtras={quoteCabExtras}
						travelActivities={quoteTravelActivities}
						startDateUTC={dateToUTCString(startDate)}
						usedCabTypes={normalizedCabs.used_cab_types}
						cabsPriceCalculations={normalizedCabs.cabs_price_calculations}
						taxPercentage={normalizedCabs.cabs_tax_percentage}
						children={children}
						noOfAdults={adults}
					/>
					{errorsInCabs.length ? (
						<Box>
							<Alert
								status="warning"
								title="Misconfiguration in Transportation Services."
							>
								<Box
									as="ul"
									listStyleType="disc"
									marginLeft="4"
									marginBottom="4"
								>
									{errorsInCabs.map(({ message }, i) => (
										<Box as="li" key={i}>
											{message}
										</Box>
									))}
								</Box>
								<Box>
									<Button as="a" href="#quote_cabs" level="tertiary">
										<Icons.ChevronDown rotate="180" /> Back to transportation
									</Button>
								</Box>
								<Box fontSize="sm" marginTop="4">
									<Icons.Info /> You can skip these warnings if everything is as
									expected.
								</Box>
							</Alert>
						</Box>
					) : null}
					{errorsInTravelActivities.length ? (
						<Box>
							<Alert
								status="warning"
								title="Misconfiguration in Travel Activities."
							>
								<Box
									as="ul"
									listStyleType="disc"
									marginLeft="4"
									marginBottom="4"
								>
									{errorsInTravelActivities.map(({ message }, i) => (
										<Box key={i}>{message}</Box>
									))}
								</Box>
								<Box>
									<Button as="a" href="#quote_cabs" level="tertiary">
										<Icons.ChevronDown rotate="180" /> Back to travel activities
									</Button>
								</Box>
								<Box fontSize="sm" marginTop="4">
									<Icons.Info /> You can skip these warnings if everything is as
									expected.
								</Box>
							</Alert>
						</Box>
					) : null}
				</Stack>
				<Box marginBottom="8">
					<QuoteExtras
						extras={quoteOtherExtras}
						startDateUTC={dateToUTCString(startDate)}
					/>
				</Box>
				<Stack gap="8">
					<QuoteFlights
						flightsDetails={transformFlightsFormDataToPreviewData(
							flights["flights"]
						)}
						flightPrice={(flights["flights"] || [])?.map((f) => f.pricing)}
						startDateUTC={dateToUTCString(startDate)}
					/>
					{errorsInFlight.length ? (
						<Box>
							<Alert
								status="warning"
								title="Misconfiguration in Flight Services."
							>
								<Box
									as="ul"
									listStyleType="disc"
									marginLeft="4"
									marginBottom="4"
								>
									{errorsInFlight.map(({ message }, i) => (
										<Box key={i} as="li">
											{message}
										</Box>
									))}
								</Box>
								<Box>
									<Button as="a" href="#quote_flights" level="tertiary">
										<Icons.ChevronDown rotate="180" /> Back to flights
									</Button>
								</Box>
								<Box fontSize="sm" marginTop="4">
									<Icons.Info /> You can skip these warnings if everything is as
									expected.
								</Box>
							</Alert>
						</Box>
					) : null}
				</Stack>
				<VisaServicesPreview startDate={startDate} />
			</Box>
			<Stack gap="2" alignItems="end" marginTop="4">
				<Inline gap="6" flexWrap="wrap">
					{options.map((o, index, arr) => (
						<Stack key={o.__id} gap="3">
							{arr.length > 1 ? (
								<Box>
									<Box
										display="inlineBlock"
										borderBottomWidth="2"
										borderColor="emphasis"
										paddingBottom="1"
									>
										<Text fontWeight="semibold">
											Option {index + 1}: {o.name}
										</Text>
									</Box>
								</Box>
							) : null}
							<TotalCostPrice optionIndex={index} />
						</Stack>
					))}
				</Inline>
				<Text fontSize="sm">
					It is the total cost price for all the provided services. This is NOT
					the selling price.
				</Text>
			</Stack>
		</Container>
	)
}

function QuoteHotelsOptionPreview({
	id,
	startDate,
	nights,
	adults,
	children,
	optionIndex,
}: {
	id: number
	startDate: Date
	nights: number
	adults: number
	children: TChildrenArray
	optionIndex: number
}) {
	const { hotels, hotelExtras } = useHotelsOption(optionIndex) || {}

	const quoteHotels = useMemo(
		() =>
			normalizeHotelsForQuote(hotels || [])
				.map((i) => ({
					...i,
					quote_id: id,
					similar_hotel_options: i.similar_hotel_options?.length
						? i.similar_hotel_options.map((s) => ({
								...s,
								quote_id: id + 10000,
							}))
						: undefined,
				}))
				.sort((a, b) => {
					return a.date > b.date ? 1 : a.date < b.date ? -1 : 0
				}),
		[hotels, id]
	)
	const quoteHotelExtras = useMemo(
		() =>
			normalizeHotelExtrasForQuote(hotelExtras || [])
				.map((i) => ({
					...i,
					quote_id: id,
				}))
				.sort((a, b) => {
					return a.date && b.date ? (a.date > b.date ? 1 : -1) : 0
				}),
		[hotelExtras, id]
	)

	const expectedStayNights = useMemo(() => {
		const expectedStayNights: Array<Date> = []
		for (let i = 0; i < nights; i++) {
			expectedStayNights.push(addUnit(startDate, i, "day"))
		}
		return expectedStayNights
	}, [startDate, nights])

	const errorsInHotels = useMemo(() => {
		const expectedAdultsCount = adults
		const errorsInHotels: Array<{
			date: Date
			messages: Array<string>
			isSimilarOption?: boolean
		}> = []
		const stayNights: Array<Date> = []
		collect(quoteHotels)
			.groupBy((h) => h.date)
			.toArray()
			.forEach((quoteHotelsForDate) => {
				function checkForErrors(
					quoteHotels: Array<
						Omit<(typeof quoteHotelsForDate)[number], "similar_hotel_options">
					>
				) {
					const messages: Array<string> = []
					const { totalAdultsInHotels, totalChildrenInHotels } =
						quoteHotels.reduce(
							(
								{ totalAdultsInHotels, totalChildrenInHotels },
								{
									no_of_rooms,
									persons_per_room,
									adults_with_extra_bed,
									children_with_extra_bed,
									children_without_extra_bed,
								}
							) => ({
								totalAdultsInHotels:
									totalAdultsInHotels +
									Number(no_of_rooms || 0) * Number(persons_per_room) +
									Number(adults_with_extra_bed || 0),
								totalChildrenInHotels:
									totalChildrenInHotels +
									Number(children_with_extra_bed || 0) +
									Number(children_without_extra_bed || 0),
							}),
							{ totalAdultsInHotels: 0, totalChildrenInHotels: 0 }
						)

					const complementaryChildren = children.filter((c) =>
						quoteHotels.find(
							(h) => Number(c.age) < Number(h.hotel.extra_bed_child_age_start)
						)
					)
					const expectedChildrenCount =
						children.length - complementaryChildren.length
					if (totalAdultsInHotels !== expectedAdultsCount) {
						messages.push(
							`Expected ${pluralize(
								"Adult",
								expectedAdultsCount,
								true
							)} but ${pluralize("Adult", totalAdultsInHotels, true)} provided`
						)
					}
					if (totalChildrenInHotels !== expectedChildrenCount) {
						messages.push(
							`Expected ${pluralize(
								"Child",
								expectedChildrenCount,
								true
							)} but ${pluralize(
								"Child",
								totalChildrenInHotels,
								true
							)} provided ${
								complementaryChildren.length > 0
									? `. ${pluralize(
											"Child",
											complementaryChildren.length,
											true
										)} (${childrenToQueryParams(
											complementaryChildren
										)}) counted as complementary`
									: ``
							}.`
						)
					}
					quoteHotels.forEach((h) => {
						if (
							h.room_type.no_of_rooms &&
							h.no_of_rooms &&
							h.no_of_rooms > h.room_type.no_of_rooms
						) {
							messages.push(
								`Only ${pluralize(
									"Room",
									h.room_type.no_of_rooms,
									true
								)} available but ${h.no_of_rooms} provided/required.`
							)
						}
						if (!h.given_price) {
							messages.push(`Missing given prices for ${h.hotel.name}`)
						}
					})
					return messages
				}
				const messages = checkForErrors(quoteHotelsForDate)
				const date = utcTimestampToLocalDate(quoteHotelsForDate[0].date)
				stayNights.push(date)
				if (messages.length) {
					errorsInHotels.push({
						date,
						messages,
					})
				}
				const similar_hotel_options = quoteHotelsForDate.flatMap((h) =>
					h.similar_hotel_options?.filter((s) => s.date === h.date)
				)
				similar_hotel_options.forEach((options) => {
					if (options) {
						const messages = checkForErrors([options])
						if (messages.length) {
							errorsInHotels.push({
								date,
								messages,
								isSimilarOption: true,
							})
						}
					}
				})
			})

		if (stayNights.length) {
			expectedStayNights.forEach((night) => {
				if (!stayNights.find((n) => isSame(n, night, "day"))) {
					errorsInHotels.push({
						date: night,
						messages: ["No hotels/accommodation details provided."],
					})
				}
			})
		}
		return errorsInHotels
	}, [quoteHotels, adults, children, expectedStayNights])
	return (
		<>
			<QuoteHotels
				hotels={quoteHotels}
				hotelExtras={quoteHotelExtras}
				startDateUTC={dateToUTCString(startDate)}
			/>
			{errorsInHotels.length ? (
				<Box>
					<Alert status="warning" title="Misconfiguration in Hotel Services.">
						<Box as="p" marginBottom="2">
							There are some misconfiguration in hotel services e.g. more pax,
							less rooms etc. Please review them
						</Box>
						<Stack as="ul" listStyleType="disc" marginBottom="4" gap="2">
							{errorsInHotels.map(({ date, messages, isSimilarOption }, i) => (
								<Inline gap="2" as="li" key={i}>
									<Text fontWeight="semibold">
										{formatDate(date, "DD MMM")}:
									</Text>
									<Stack>
										{isSimilarOption ? (
											<Text fontSize="sm" fontWeight="semibold">
												Similar Hotel/Option
											</Text>
										) : null}
										{messages.map((m, i) => (
											<Text key={i}>{m}</Text>
										))}
									</Stack>
								</Inline>
							))}
						</Stack>
						<Box>
							<Button as="a" href="#quote_hotels" level="tertiary">
								<Icons.ChevronDown rotate="180" /> Back to Hotels
							</Button>
						</Box>
						<Box fontSize="sm" marginTop="4">
							<Icons.Info /> You can skip these warnings if everything is as
							expected.
						</Box>
					</Alert>
				</Box>
			) : null}
		</>
	)
}

function TotalCostPrice({ optionIndex }: { optionIndex: number }) {
	const {
		totalPrice,
		flightCostPrice,
		totalHotelPrice,
		cabsPrice,
		travelActivitiesPrice,
		extrasPrice,
		otherExtrasPrice,
		visasPrice,
	} = useTotalQuotePrice(optionIndex)

	const sectionWiseTotalCostPrice = ([] as Array<[string, TMoney[]]>)
		.concat([
			["Hotels", totalHotelPrice],
			["Transports", cabsPrice],
			["Activities/Tickets", travelActivitiesPrice],
			["Extras", extrasPrice],
			["Other Specials", otherExtrasPrice],
			["Flights", flightCostPrice],
			["Visa", visasPrice],
		])
		.filter(([_label, money]) => money.length)
	return (
		<Inline gap="6" flexWrap="wrap" collapseBelow="sm">
			<Stack
				gap="1"
				padding="4"
				rounded="md"
				bgColor="primary"
				borderWidth="1"
				borderColor="primary"
			>
				<Text fontWeight="semibold" color="primary">
					Total Cost
				</Text>
				<Text fontSize="md">
					<MoneySum money={totalPrice} />
				</Text>
			</Stack>
			{sectionWiseTotalCostPrice.length ? (
				<Box overflow="auto" padding="4" rounded="md" borderWidth="1">
					<Inline gap="2" flexWrap="nowrap">
						{sectionWiseTotalCostPrice.map(([label, money], i) => (
							<Fragment key={label}>
								{i > 0 ? (
									<Stack
										padding="1"
										alignItems="center"
										justifyContent="center"
									>
										<Icons.Plus />
									</Stack>
								) : null}
								<Stack gap="1" whiteSpace="preserve">
									<Text fontWeight="semibold">{label}</Text>
									<Text fontSize="md">
										<MoneySum money={money} />
									</Text>
								</Stack>
							</Fragment>
						))}
					</Inline>
				</Box>
			) : null}
		</Inline>
	)
}

function PackagePricePreviewForOption({
	optionIndex,
}: {
	optionIndex: number
}) {
	const { flightPrice, flightCostPrice } =
		useTotalQuotePriceInCostingCurrency(optionIndex)
	return (
		<PackagePricePreview
			optionIndex={optionIndex}
			flightGivenPriceInCostingCurrency={Number(
				formatMoneyByDecimal(flightPrice)
			)}
			flightCostPriceInCostingCurrency={Number(
				formatMoneyByDecimal(flightCostPrice)
			)}
		/>
	)
}

export type INewQuoteState = {
	options: Array<{
		__id: number
		id: number
		name: string
		per_person_cost_price_components?: {
			include_children_in_cabs_start_age?: number
			include_children_in_cabs?: boolean | number
			include_adults_with_extra_bed_in_cabs?: boolean
			include_children_in_extras?: boolean
			exclude_adults_count_in_cabs?: number
			total?: number
		}
		per_component_cost_price_components?: {
			total: number
		}
		total_price?: number
		cost_prices_per_person?: Array<TPricePerPersonAllocation>
		flight_given_prices_by_id?: Record<string, number>
		cost_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		price_mismatch_between_per_person_and_total?: boolean
		per_person_allocation_not_possible?: boolean
		given_price?: number
		unrounded_given_prices?: TUnroundedGivenPrice
		given_prices_per_person?: Array<TPricePerPersonAllocation>
		given_prices_per_component?: Array<TPricePerTravelComponentAllocation>
		// given_price_in_costing_currency?: number //This has been depricated after release of new advance pricing strategies(Per Component & Per Component Per Person)
		margins?: IGivenQuote["margins"]
		given_price_comments?: string
		given_price_customer_remarks?: string
		existing_given_price_currency?: string
		existing_given_price?: number
		gst_included: IGivenQuote["gst_included"]
		taxes: {
			editing_total: 0 | 1
			total_percentage: number
			total: number
			total_applied_on?: TTaxAppliedOn
			per_component_allocation: TTaxPerComponentAllocation
		}
	}>
	hotel_options: Array<{
		__id: number
		option_index: number | string
		hotels: IQuoteHotelParams
		hotelPrice: TMoney[]
		hotelExtras: IQuoteHotelExtraParams
		hotelExtrasPrice: TMoney[]
	}>
	cabs: IQuoteCabParams
	cabPrice: TMoney[]
	visas: TVisaServiceInQuote
	visasPrice: TMoney[]
	transportExtras: IQuoteCabExtraParams
	transportExtrasPrice: TMoney[]
	flights: IQuoteFlightParams
	flightPrice: TMoney[]
	flightCostPrice: TMoney[]
	otherExtras: ReturnType<typeof getEditableOtherExtras>
	otherExtrasPrice: TMoney[]
	comments: string
	isCreating: boolean
	errors?: null | { [key: string]: string | Array<string> | undefined }
	startDate: Date
	days: number
	no_of_adults: number
	complimentary_count?: number
	children: TChildrenArray
	include_per_person_price?: 0 | 1 | "0" | "1" | boolean
	functional_currency: string
	currency_pairs: Array<TCurrencyPair>
	currency_pairs_obj?: TCurrencyPairsObject
	given_currency: string
	costing_currency: string
	// tax_percentage: number //Depricated - Tax percentage is now handled per-option in options[].taxes.total_percentage
	// gst_included: IGivenQuote["gst_included"] //Depricated - Gst Included is now handled per-option in options[].gst_included
	tax_name: IGivenQuote["tax_name"]
	pricing_strategy: ITripQuote["pricing_strategy"]
	migrate_to_v2?: 0 | 1 //v1→v2 migration tracking
	enable_flight_rounding?: 0 | 1
	enable_sync_rounding_among_options?: 0 | 1
	fetching_prices: boolean
	missingCabTypeErrors: ICabTypeMissingError[]
}

/**
 * Creates an AxiosError-compatible validation error for display
 * Compatible with NewQuoteErrorDisplay component
 */
function createValidationError(
	errors: {
		[key: string]: Array<string>
	},
	message: string
): XHRError {
	return {
		isAxiosError: true,
		name: "ValidationError",
		message,
		response: {
			data: {
				message,
				errors,
			},
		},
	} as XHRError
}

/**
 * Validates that all hotels, transport services, cabs metrics, activities to have valid given prices
 * @returns validation errors if any service has undefined given prices due to system rate changes or any other reason.
 */
function validateQuoteComponentsGivenPrices(formData: {
	cabs?: INewQuoteState["cabs"]
	hotel_options?: INewQuoteState["hotel_options"]
}): {
	[key: string]: Array<string>
} {
	const errors: { [key: string]: Array<string> } = {}

	// Checking both transport services and activities (both are in cabs.daywise_services)
	const daywiseServices = formData.cabs?.daywise_services || []
	daywiseServices.forEach((daywiseService, daywiseIndex) => {
		const services = daywiseService.services || []
		services.forEach((serviceItem, serviceIndex) => {
			if (serviceItem.type === "transport_service") {
				const service = serviceItem.service
				const cabs = service.cabs || []
				const serviceName = service.transport_service.name

				cabs.forEach((cab, cabIndex) => {
					const cabTypeName = cab.cab_type.name
					const datePrices = cab.date_wise_prices || []
					datePrices.forEach((datePrice, datePriceIndex) => {
						if (
							datePrice.given_price === undefined ||
							datePrice.given_price === null
						) {
							const fieldKey = `cabs.daywise_services.${daywiseIndex}.services.${serviceIndex}.service.cabs.${cabIndex}.date_wise_prices.${datePriceIndex}.per_quantity_given_price`

							const formattedDate = formatDate(datePrice.date, "MMM DD, YYYY")

							const noOfCabs = cab.no_of_cabs

							const errorMessage = [
								`(${formattedDate}) → ${serviceName} → ${cabTypeName}${noOfCabs > 1 ? ` (${noOfCabs} units)` : ""}`,
								`Please re-enter price. System rate has changed since last save.`,
							].join("\n")

							errors[fieldKey] = [errorMessage]
						}
					})
				})
			} else if (serviceItem.type === "travel_activity") {
				const service = serviceItem.service
				const activityName = service.activity.name
				const ticketTypeName = service.ticket_type?.name
				const touristConfigurations =
					service.ticket_tourist_configurations || []

				touristConfigurations.forEach((config, configIndex) => {
					const configName = config.configuration.name
					const datePrices = config.date_wise_prices || []

					datePrices.forEach((datePrice, datePriceIndex) => {
						if (
							datePrice.given_price === undefined ||
							datePrice.given_price === null
						) {
							const fieldKey = `cabs.daywise_services.${daywiseIndex}.services.${serviceIndex}.service.ticket_tourist_configurations.${configIndex}.date_wise_prices.${datePriceIndex}.per_quantity_given_price`

							const formattedDate = formatDate(datePrice.date, "MMM DD, YYYY")

							const configDetails = config.configuration
							const paxInfo = [
								configDetails.tourist_type === "adult"
									? `${configDetails.quantity} Adults`
									: null,
								configDetails.tourist_type === "child"
									? `${configDetails.quantity} Children`
									: null,
							]
								.filter(Boolean)
								.join(", ")

							const errorMessage = [
								`(${formattedDate}) → ${activityName} → ${ticketTypeName ? ticketTypeName : null} → ${configName}${paxInfo ? ` (${paxInfo})` : ""}`,
								`Please re-enter price. System rate has changed since last save.`,
							].join("\n")

							errors[fieldKey] = [errorMessage]
						}
					})
				})
			}
		})
	})

	// Check cumulative pricing (add_cab_prices_at_once mode)
	const cabsCalculations = formData.cabs?.cabs_price_calculations || []
	cabsCalculations.forEach((cab, cabIndex) => {
		const cabTypeName = cab.cab_type.name
		const metricPrices = cab.metric_wise_prices || []

		const services = cab.services || []
		const serviceNames = services
			.map((s) => s.transport_service?.name)
			.filter(Boolean)
			.join(", ")
		const servicesInfo = serviceNames ? ` (Used in: ${serviceNames})` : ""

		metricPrices.forEach((metricPrice, metricIndex) => {
			const metricName = metricPrice.metric.name

			// Check price
			if (
				metricPrice.per_quantity_given_price === undefined ||
				metricPrice.per_quantity_given_price === null
			) {
				const fieldKey = `cabs.cabs_price_calculations.${cabIndex}.metric_wise_prices.${metricIndex}.per_quantity_given_price`

				const priceErrorMessage = [
					`${cabTypeName} → ${metricName}-${servicesInfo}`,
					`Please re-enter price. System rate has changed since last save.`,
				].join("\n")

				errors[fieldKey] = [priceErrorMessage]
			}

			// Check quantity
			if (
				metricPrice.given_quantity === undefined ||
				metricPrice.given_quantity === null
			) {
				const fieldKey = `cabs.cabs_price_calculations.${cabIndex}.metric_wise_prices.${metricIndex}.given_quantity`

				const quantityErrorMessage = [
					`${cabTypeName} → ${metricName}-${servicesInfo}`,
					`Please re-enter quantity. System quantity has changed since last save.`,
				].join("\n")

				errors[fieldKey] = [quantityErrorMessage]
			}
		})
	})

	const hotelOptions = formData.hotel_options || []
	hotelOptions.forEach((hotelOption, optionIndex) => {
		const hotels = hotelOption.hotels || []
		hotels.forEach((hotel, hotelIndex) => {
			const hotelName = hotel.hotel.name

			const datePrices = hotel.date_wise_prices || []
			datePrices.forEach((datePrice, dateIndex) => {
				if (
					datePrice.given_price === undefined ||
					datePrice.given_price === null
				) {
					const fieldKey = `hotel_options.${optionIndex}.hotels.${hotelIndex}.date_wise_prices.${dateIndex}.given_price`

					const formattedDate = formatDate(datePrice.date, "MMM DD, YYYY")

					const roomType = hotel.room_type.name
					const mealPlan = hotel.meal_plan.name
					const noOfRooms = hotel.no_of_rooms

					const optionNumber = optionIndex + 1

					const errorMessage = [
						`Option ${optionNumber} → (${formattedDate}) → ${hotelName}`,
						`${roomType} | ${mealPlan}${noOfRooms > 1 ? ` | ${noOfRooms} Rooms` : ""}`,
						`Please re-enter price. System rate has changed since last save.`,
					].join("\n")

					errors[fieldKey] = [errorMessage]
				}
			})

			const similarHotels = hotel.similar_hotel_options || []
			similarHotels.forEach((similarHotel, similarIndex) => {
				const similarHotelName = similarHotel.hotel.name
				const similarDatePrices = similarHotel.date_wise_prices || []

				similarDatePrices.forEach((datePrice, dateIndex) => {
					if (
						datePrice.given_price === undefined ||
						datePrice.given_price === null
					) {
						const fieldKey = `hotel_options.${optionIndex}.hotels.${hotelIndex}.similar_hotel_options.${similarIndex}.date_wise_prices.${dateIndex}.given_price`

						const formattedDate = formatDate(datePrice.date, "MMM DD, YYYY")

						const roomType = similarHotel.room_type.name
						const mealPlan = similarHotel.meal_plan.name
						const noOfRooms = similarHotel.no_of_rooms

						const optionNumber = optionIndex + 1
						const alternativeHotelNumber = similarIndex + 1

						const errorMessage = [
							`Option ${optionNumber} → Alternative Similar Hotel #${alternativeHotelNumber} → (${formattedDate}) → ${similarHotelName}`,
							`${roomType} | ${mealPlan}${noOfRooms > 1 ? ` | ${noOfRooms} Rooms` : ""}`,
							`Please re-enter price. System rate has changed since last save.`,
						].join("\n")

						errors[fieldKey] = [errorMessage]
					}
				})
			})
		})
	})

	return errors
}

/**
 * Validates quote configuration and setup completeness.
 *
 * PURPOSE:
 * Validates that all quote components are properly configured before saving.
 *
 */
function validateQuoteConfiguration(formData: {
	cabs?: INewQuoteState["cabs"]
	missingCabTypeErrors?: INewQuoteState["missingCabTypeErrors"]
	visas?: INewQuoteState["visas"]
	totalPax?: number
}): {
	[key: string]: Array<string>
} {
	let errors: { [key: string]: Array<string> } = {}

	const cabPricingErrors = validateCabCumulativePricingSetup(formData.cabs)

	errors = {
		...cabPricingErrors,
	}

	if (
		formData.missingCabTypeErrors &&
		formData.missingCabTypeErrors.length > 0
	) {
		const errorMessages = formData.missingCabTypeErrors.map(
			(e) =>
				`Day ${e.dayIndex + 1} • ${e?.serviceName} • Cab ${e.cabIndex + 1}: ${e.reason}`
		)

		const fieldKey = "cabs.missing_cab_types"

		errors[fieldKey] = [
			"Please select a cab type for the following services before entering prices:",
			...errorMessages.map((msg, i) => `${i + 1}. ${msg}`),
		]
	}

	if (formData.visas && formData.totalPax !== undefined) {
		const visaPaxErrors = validateVisaServicesPax(
			formData.visas,
			formData.totalPax
		)
		errors = {
			...errors,
			...visaPaxErrors,
		}
	}

	return errors
}

/**
 * Validates cab cumulative pricing mode configuration.
 *
 * When "Add Cab Prices at Once" is enabled, user must click "Add Cumulative Prices"
 * to configure pricing calculations. This validation ensures the configuration is complete.
 *
 */
function validateCabCumulativePricingSetup(cabs?: INewQuoteState["cabs"]): {
	[key: string]: Array<string>
} {
	const errors: { [key: string]: Array<string> } = {}

	if (!cabs) {
		return errors
	}

	const isCumulativePricingEnabled = Boolean(cabs.add_cab_prices_at_once)

	const cabsCalculations = cabs.cabs_price_calculations || []
	const hasPricingCalculations = cabsCalculations.length > 0

	if (isCumulativePricingEnabled && !hasPricingCalculations) {
		const fieldKey = "cabs.cabs_price_calculations"

		errors[fieldKey] = [
			"Cumulative cab pricing is enabled but not configured.",
			"Please click 'Add Cumulative Prices' button to configure cab pricing before saving the quote.",
		]
	}

	return errors
}

function getQuoteInitialServicesValues({
	tripQuote,
	startDate,
	days,
	baseStartDate,
	baseNoOfAdults,
	baseChildren,
	noOfAdults,
	children,
	defaultCostingCurrency,
	isEditing,
	afterConfirmedQuote,
	hasMultiQuoteFeature,
	isUsingExistingQuote = false,
	tenantCurrencies,
	complimentaryCount,
}: {
	tripQuote?: ITripQuote
	baseStartDate: Date
	baseNoOfAdults: number
	baseChildren: TChildrenArray
	startDate: Date
	days: number
	noOfAdults: number
	children: TChildrenArray
	defaultCostingCurrency: string
	isEditing: boolean
	afterConfirmedQuote: boolean
	hasMultiQuoteFeature: boolean
	isUsingExistingQuote?: boolean
	tenantCurrencies?: Array<TCurrency>
	complimentaryCount: number
}): INewQuoteState {
	const tripQuotes: Array<Omit<ITripQuote, "options"> | undefined> = tripQuote
		? tripQuote.can_share_options && hasMultiQuoteFeature
			? [tripQuote].concat(tripQuote.options || [])
			: [tripQuote]
		: [undefined]
	const firstQuote = tripQuote?.quote
	const costingCurrency = tripQuote?.currency || defaultCostingCurrency

	// Find the currency object for the quotation currency (costing currency) to get its default rounding
	const costingCurrencyObject = tenantCurrencies?.find(
		(c) => c.currency === costingCurrency
	)
	const defaultRoundingPoint = costingCurrencyObject?.rounding
		? Number(costingCurrencyObject.rounding)
		: 10

	const hasPaxChanged =
		Number(noOfAdults) !== Number(baseNoOfAdults) ||
		childrenToQueryParams(baseChildren) !== childrenToQueryParams(children)
	const hotelOptions = tripQuotes.reduce<INewQuoteState["hotel_options"]>(
		(hotelOptions, tripQuote, index) => {
			const { quote } = tripQuote || {}
			const hotels = quote
				? getEditableHotelsFromQuote(
						quote.hotels,
						clone(baseStartDate),
						startDate,
						days,
						{
							disablePriceResetsUsingRates: afterConfirmedQuote,
						}
					)
				: []
			const hotel_extras = quote
				? getEditableHotelExtrasFromQuote(
						quote.hotel_extras,
						clone(baseStartDate),
						startDate,
						days
					)
				: []

			const hotelPrice = getTotalPriceForServices(hotels)

			const hotelExtrasPrice = getTotalPriceForExtras(hotel_extras)
			return hotelOptions.concat([
				addKeyToFieldArrayItem({
					option_index: index,
					hotels,
					hotelPrice,
					hotelExtras: hotel_extras,
					hotelExtrasPrice,
				}),
			])
		},
		[]
	)
	const cabs = firstQuote
		? getEditableCabs(
				firstQuote,
				clone(baseStartDate),
				startDate,
				days,
				hasPaxChanged,
				isUsingExistingQuote,
				{
					disablePriceResetsUsingRates: afterConfirmedQuote,
				}
			)
		: ({
				cabs: [],
				daywise_services: [],
				given_price: [moneyParseByDecimal(0, costingCurrency)],
				given_price_of_cabs_only: [moneyParseByDecimal(0, costingCurrency)],
				given_price_of_activities_only: [
					moneyParseByDecimal(0, costingCurrency),
				],
			} as IQuoteCabParams)
	const transport_extras = firstQuote
		? getEditableCabExtras(
				firstQuote.transport_extras,
				clone(baseStartDate),
				startDate,
				days
			)
		: []

	const visas: TVisaServiceInQuote =
		firstQuote && !isUsingExistingQuote
			? getEditableVisaServices(firstQuote.visas ?? [])
			: { services: [] }
	const visaPrice =
		firstQuote && !isUsingExistingQuote
			? (firstQuote.visas ?? []).map((v) => v.given_price_money)
			: []

	const flights = firstQuote
		? getEditableFlights(
				firstQuote.flight_form_fill_mode,
				firstQuote.flight_type,
				firstQuote.flights,
				clone(baseStartDate),
				startDate,
				isUsingExistingQuote,
				noOfAdults,
				children,
				defaultCostingCurrency
			)
		: {}
	const other_extras = firstQuote
		? getEditableOtherExtras(
				firstQuote.other_extras,
				clone(baseStartDate),
				startDate,
				days
			)
		: []

	const cabPrice = cabs.given_price
	const transportExtrasPrice = getTotalPriceForExtras(transport_extras)
	const flightPrice = getTotalPriceForExtras(
		(flights.flights || []).map((f) => f.pricing)
	)
	const flightCostPrice = getTotalPriceForExtras(
		(flights?.flights || []).map((f) => ({
			...f.pricing,
			price: f.pricing.calculated_price,
		}))
	)

	const otherExtrasPrice = getTotalPriceForExtras(other_extras)

	const tax_name = tripQuote?.given_quote ? tripQuote.given_quote.tax_name : ""

	const gstIncluded = tripQuote?.given_quote
		? tripQuote.given_quote.gst_included
			? "1"
			: "0"
		: "1"

	/**
	 * DEV NOTE: Tax Percentage Migration (Critical Change) - tax_percentage has been migrated to options[].taxes.total_percentage, Backend now backfills this data for old quotes
	 *
	 * REMOVED: INewQuoteState.tax_percentage (root level)
	 * REPLACED WITH: options[].taxes.total_percentage (per-option level)
	 *
	 * MIGRATION STRATEGY:
	 * 1. Backend automatically backfills options[].taxes.total_percentage for existing quotes
	 * 2. Frontend no longer reads from root tax_percentage
	 * 3. All pricing strategies now use per-option tax structure
	 */

	const given_currency =
		tripQuote?.given_quote?.given_currency || costingCurrency
	const functional_currency =
		tripQuote?.given_quote?.functional_currency || costingCurrency
	const currency_pairs: TCurrencyPair[] = tripQuote?.given_quote
		?.currency_pairs || [
		{
			baseCurrency: costingCurrency,
			counterCurrency: costingCurrency,
			ratio: 1,
		},
	]

	const flightCostPriceInCostingCurrency = makeCurrencyConverter(
		currency_pairs
	)(costingCurrency, flightCostPrice)

	const tripQuoteOptions = tripQuotes // atleast one option
		.map(function (tripQuote, optionIndex) {
			const costPriceWithoutFlights = Number(
				formatMoneyByDecimal(
					subtractMoney(
						moneyParseByDecimal(tripQuote?.cost_price || 0, costingCurrency),
						flightCostPriceInCostingCurrency
					)
				)
			)

			const margin = tripQuote?.given_quote
				? calculateMarginFromTotalPrice({
						givenPrice: tripQuote.given_quote.given_price,
						costPriceWithoutFlights,
						flightPrice: Number(
							formatMoneyByDecimal(flightCostPriceInCostingCurrency)
						),
						gstIncluded: Boolean(Number(gstIncluded)),
						taxPercentage:
							tripQuote?.given_quote?.taxes?.total_percentage ||
							tripQuote?.given_quote?.tax_percentage ||
							5,
					})
				: 0

			const margin_percentage = calculateMarginPercentageFromTotalPrice({
				costPriceWithoutFlights,
				margin,
			})

			const cost_prices_per_person =
				(tripQuote?.cost_prices_per_person as INewQuoteState["options"][number]["cost_prices_per_person"]) ||
				[]
			const margins: INewQuoteState["options"][number]["margins"] = tripQuote
				?.given_quote?.margins
				? {
						...tripQuote.given_quote.margins,
						per_component_allocation: tripQuote.given_quote.margins
							.per_component_allocation || {
							hotels_percentage: 0,
							hotels: 0,
							cabs_percentage: 0,
							cabs: 0,
							activities_percentage: 0,
							activities: 0,
							extras_percentage: 0,
							extras: 0,
							flights: 0,
							flights_percentage: 0,
							visas: 0,
							visas_percentage: 0,
						},
						editing_total:
							tripQuote?.given_quote?.margins?.editing_total !== null &&
							tripQuote?.given_quote?.margins?.editing_total !== undefined
								? tripQuote?.given_quote?.margins?.editing_total
								: 1, //Note - There is a reason we are handling this "editing_total" key feild explicitly here instead of relaying on incoming backend api "given_quote?.margins" data
						//during the edit quote flow, there are OLD Quotes which do not have this key in the Database, and when user try to create a quote by "Use This" method and these quotes
						//do not have this key, then this key will be missed in the Quote Form.
					}
				: {
						total: margin,
						total_percentage: margin_percentage,
						use_percentage: 1,
						editing_total: tripQuote?.given_quote?.margins ? 0 : 1,
						rounding_point: isEditing ? undefined : defaultRoundingPoint,
						per_component_allocation: {
							hotels_percentage: 0,
							hotels: 0,
							cabs_percentage: 0,
							cabs: 0,
							activities_percentage: 0,
							activities: 0,
							extras_percentage: 0,
							extras: 0,
							flights: 0,
							flights_percentage: 0,
							visas: 0,
							visas_percentage: 0,
						},
					}

			const per_person_cost_price_components =
				(tripQuote?.per_person_cost_price_components as INewQuoteState["options"][number]["per_person_cost_price_components"]) ||
				{}

			if (
				isTruthy(per_person_cost_price_components.include_children_in_cabs) &&
				per_person_cost_price_components.include_children_in_cabs_start_age ===
					undefined
			) {
				// set the default start age
				per_person_cost_price_components.include_children_in_cabs_start_age = 6
			}

			const gstIncludedForOption = tripQuote?.given_quote
				? tripQuote.given_quote.gst_included
					? "1"
					: "0"
				: "1"

			const givenQuoteTaxes = tripQuote?.given_quote?.taxes
			const givenQuoteTaxesPerComponentAllocation =
				givenQuoteTaxes?.per_component_allocation

			const taxes = {
				editing_total: givenQuoteTaxes ? givenQuoteTaxes.editing_total : 1,
				total_percentage: givenQuoteTaxes
					? givenQuoteTaxes.total_percentage
					: 5,
				total: givenQuoteTaxes ? givenQuoteTaxes.total : 0,
				total_applied_on: givenQuoteTaxes
					? givenQuoteTaxes?.total_applied_on || undefined
					: "cost_plus_markup",
				per_component_allocation: {
					hotels_applied_on:
						givenQuoteTaxesPerComponentAllocation?.hotels_applied_on ||
						"cost_plus_markup",
					hotels_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.hotels_percentage
						: 0,
					hotels: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.hotels
						: 0,
					cabs_applied_on:
						givenQuoteTaxesPerComponentAllocation?.cabs_applied_on ||
						"cost_plus_markup",
					cabs_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.cabs_percentage
						: 0,
					cabs: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.cabs
						: 0,
					activities_applied_on:
						givenQuoteTaxesPerComponentAllocation?.activities_applied_on ||
						"cost_plus_markup",
					activities_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.activities_percentage
						: 0,
					activities: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.activities
						: 0,
					extras_applied_on:
						givenQuoteTaxesPerComponentAllocation?.extras_applied_on ||
						"cost_plus_markup",
					extras_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.extras_percentage
						: 0,
					extras: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.extras
						: 0,
					flights_applied_on:
						givenQuoteTaxesPerComponentAllocation?.flights_applied_on ||
						"cost_plus_markup",
					flights_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation?.flights_percentage
						: 0,
					flights: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.flights
						: 0,
					visas: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.visas
						: 0,
					visas_percentage: givenQuoteTaxesPerComponentAllocation
						? givenQuoteTaxesPerComponentAllocation.visas_percentage
						: 0,
					visas_applied_on:
						givenQuoteTaxesPerComponentAllocation?.visas_applied_on ||
						"cost_plus_markup",
				},
			}

			return addKeyToFieldArrayItem({
				id: optionIndex,
				name: tripQuote?.name || `Deluxe Package`,
				given_price_comments: tripQuote?.given_quote?.comments || "",
				given_price_customer_remarks: isEditing
					? tripQuote?.given_quote?.customer_remarks || ""
					: "", // When editing same quote, preserve remarks. When "Use this Quote", clear remarks for new quote
				margins,
				per_person_cost_price_components: per_person_cost_price_components,
				cost_prices_per_person,
				given_prices_per_person:
					undefined as INewQuoteState["options"][number]["given_prices_per_person"],
				given_price: 0, // this will be auto calculated
				existing_given_price_currency: isEditing
					? tripQuote?.given_quote?.given_currency
					: undefined,
				existing_given_price: isEditing
					? tripQuote?.given_quote?.given_price
					: undefined,
				gst_included: isTruthy(gstIncludedForOption),
				taxes: taxes,
			})
		})

	// only keep the last exchagne rates if editing
	const currency_pairs_obj = isEditing
		? currencyPairsArrayToObject(currency_pairs)
		: {}

	return {
		startDate,
		days,
		no_of_adults: noOfAdults,
		complimentary_count: complimentaryCount,
		children,
		hotel_options: hotelOptions,
		cabs: cabs,
		cabPrice,
		visas: visas,
		visasPrice: visaPrice,
		transportExtras: transport_extras,
		transportExtrasPrice,
		flights: flights,
		flightPrice,
		flightCostPrice,
		otherExtras: other_extras,
		otherExtrasPrice,
		comments: firstQuote?.comments || "",
		options: tripQuoteOptions,
		include_per_person_price: Boolean(
			tripQuote?.cost_prices_per_person?.length
		),
		tax_name,
		costing_currency: costingCurrency,
		given_currency,
		currency_pairs,
		functional_currency,
		currency_pairs_obj,
		isCreating: false,
		fetching_prices: true,
		pricing_strategy: tripQuote?.pricing_strategy || "overall_v2",
		missingCabTypeErrors: [], // It is for frontend side validation
	}
}

function getEditableCabs(
	{
		cabs,
		travel_activities,
		used_cab_types,
		cabs_price_calculation_configurations,
		cabs_price_calculations,
		cabs_tax_percentage,
	}: Pick<
		IQuote,
		| "id"
		| "cabs"
		| "travel_activities"
		| "used_cab_types"
		| "cabs_price_calculation_configurations"
		| "cabs_price_calculations"
		| "cabs_tax_percentage"
	>,
	initialStartDate: Date,
	newStartDate: Date,
	durationInDays: number,
	hasPaxChanged: boolean,
	isUsingExistingQuote: boolean,
	config?: {
		disablePriceResetsUsingRates?: boolean
	}
): IQuoteCabParams {
	let given_price_of_cabs_only: Array<TMoney> = []
	let given_price_of_activities_only: Array<TMoney> = []
	const daywise_services: IQuoteCabParams["daywise_services"] = []
	if (cabs.at(0)?.group_id || travel_activities.at(0)?.group_id) {
		// using the new process
		const transportServices = collect(cabs)
			.groupBy((c) => c.group_id || "")
			.toArray()
			.map((cabs) => {
				const group_id = cabs[0].group_id
				const uniqueUTCTimestamps = collect(
					cabs.map(
						(c) => [c.date, c.date_local] as [string, string | undefined]
					)
				)
					.unique((i) => i[0])
					.toArray()
				const utcTimestampToLocalDatesMap = uniqueUTCTimestamps.reduce(
					(dates, [utcTimestamp, localTimestamp]) => {
						const date = localOrUtcTimestampToLocalDate(
							localTimestamp,
							utcTimestamp
						)
						const stayDayDiff = getDiff(date, initialStartDate, "days")
						// remove the dates if it is not withing the duration range
						const newDate =
							stayDayDiff < durationInDays
								? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
								: undefined
						dates[utcTimestamp] = newDate
						return dates
					},
					{} as Record<string, Date | undefined>
				)
				const newDates = uniqueUTCTimestamps
					.map((c) => utcTimestampToLocalDatesMap[c[0]])
					.filter((nd): nd is Date => Boolean(nd))
				const services = collect(cabs)
					.groupBy((c) => `${c.sort_order}-${c.transport_service_id}`)
					.toArray()
					.map(
						(
							cabs
						): {
							sort_order: number
							transport_service?: TValidTransportServiceDatewisePricesFieldValue & {
								id: number
							}
							travel_activity?: TValidTravelActivityDatewisePricesInputFieldValue & {
								id: number
							}
						} => {
							const {
								id,
								sort_order,
								transport_service,
								comments,
								duration,
								start_time,
							} = cabs[0]
							const startTime = start_time
								? formatDate(parseDate(start_time, "HH:mm:ss"), "HH:mm")
								: undefined
							const durationTime = duration
								? {
										iso: duration,
										name: createDuration(duration).asMinutes() + " Mins",
									}
								: undefined
							const transportServiceValidData = {
								id,
								dates: newDates,
								transport_service,
								duration: durationTime,
								start_time: startTime,
								transport_service_location: transport_service.locations,
								comments,
								cabs: collect(cabs)
									.groupBy(
										(c) =>
											`${c.no_of_cabs}-${c.cab_type_id}-${c.cab_locality?.id || "."}-${c.currency}`
									)
									.toArray()
									.map((cabs) => {
										const { no_of_cabs, cab_type, cab_locality } = cabs[0]
										return addKeyToFieldArrayItem({
											no_of_cabs,
											cab_type,
											cab_locality,
											date_wise_prices: cabs.flatMap(
												({
													date: utcTimestamp,
													date_local: localTimestamp,
													currency,
													calculated_price,
													given_price,
												}) => {
													const date = localOrUtcTimestampToLocalDate(
														localTimestamp,
														utcTimestamp
													)
													const newDate =
														utcTimestampToLocalDatesMap[utcTimestamp]
													let edited_given_price =
														config?.disablePriceResetsUsingRates
															? true
															: calculated_price !== given_price

													let saved_system_calculated_price:
														| number
														| undefined = Number(calculated_price || 0)
													let saved_manual_given_price: number | undefined =
														Number(given_price || 0)
													let disable_price_resets = Boolean(
														config?.disablePriceResetsUsingRates
													)

													// reset the given price if date has changed
													if (newDate && !isSame(newDate, date, "day")) {
														given_price = 0
														edited_given_price = false
														disable_price_resets = false
														//If user has changed trip dates from "EditBasicDetails", then we are disabling the rate change detection logic
														saved_system_calculated_price = undefined
														saved_manual_given_price = undefined
													}

													return newDate
														? [
																{
																	currency,
																	price: Number(calculated_price || 0), //System Rate (Total)
																	per_quantity_price:
																		Number(calculated_price || 0) /
																		(no_of_cabs || 1), //System Rate Per Quantity
																	given_price: Number(given_price || 0),
																	per_quantity_given_price:
																		Number(given_price || 0) /
																		(no_of_cabs || 1),
																	edited_given_price,
																	date: newDate,
																	// Last Quote - Saved data for system rate change detection
																	saved_system_calculated_price,
																	saved_manual_given_price,
																	disable_price_resets,
																},
															]
														: []
												}
											),
										})
									}),
							}
							return {
								sort_order: sort_order || 0,
								transport_service: transportServiceValidData,
								travel_activity: undefined,
							}
						}
					)
				return {
					group_id,
					dates: newDates,
					services,
				}
			})

		given_price_of_cabs_only = transportServices.reduce<Array<TMoney>>(
			(total, { services }) =>
				addMoneyFromDifferentCurrencies(
					total.concat(
						services
							.flatMap((s) => s.transport_service?.cabs || [])
							.reduce<
								Array<TMoney>
							>((total, { date_wise_prices }) => addMoneyFromDifferentCurrencies(total.concat((date_wise_prices || []).reduce<TMoney[]>((total, { given_price, currency }) => addMoneyFromDifferentCurrencies(total.concat([moneyParseByDecimal(given_price || 0, currency)])), []))), []),
						[]
					)
				),
			[]
		)
		// using the new process
		const travelActivities = collect(travel_activities)
			.groupBy((c) => c.group_id || "")
			.toArray()
			.map((travelActivities) => {
				const group_id = travelActivities[0].group_id
				const uniqueUTCTimestamps = collect(
					travelActivities.map(
						(c) => [c.date, c.date_local] as [string, string | undefined]
					)
				)
					.unique((s) => s[0])
					.toArray()
				const utcTimestampToLocaDatesMap = uniqueUTCTimestamps.reduce(
					(dates, [utcTimestamp, localUtcTimestamp]) => {
						const date = localOrUtcTimestampToLocalDate(
							localUtcTimestamp,
							utcTimestamp
						)
						const stayDayDiff = getDiff(date, initialStartDate, "days")
						// remove the dates if it is not withing the duration range
						const newDate =
							stayDayDiff < durationInDays
								? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
								: undefined
						dates[utcTimestamp] = newDate
						return dates
					},
					{} as Record<string, Date | undefined>
				)
				const newDates = uniqueUTCTimestamps
					.map((c) => utcTimestampToLocaDatesMap[c[0]])
					.filter((nd): nd is Date => Boolean(nd))
				const services = collect(travelActivities)
					.groupBy((s) =>
						[
							s.sort_order,
							s.activity.id,
							s.ticket_type?.id || ".",
							s.duration || ".",
							s.slot || ".",
						].join("-")
					)
					.toArray()
					.map(
						(
							activities
						): {
							travel_activity?: TValidTravelActivityDatewisePricesInputFieldValue & {
								id: number
							}
							sort_order: number
							transport_service?: TValidTransportServiceDatewisePricesFieldValue & {
								id: number
							}
						} => {
							const {
								id,
								sort_order,
								activity,
								ticket_type,
								duration,
								slot,
								comments,
							} = activities[0]
							// if the he pax have been changed then we need to reset the ticket tourist configurations
							const ticket_tourist_configurations = hasPaxChanged
								? []
								: collect(
										activities.flatMap((a) =>
											a.ticket_tourist_configurations.map((c) => ({
												...c,
												date: a.date,
												date_local: a.date_local,
											}))
										)
									)
										.groupBy((c) =>
											[c.currency, c.quantity, c.configuration.id].join("-")
										)
										.toArray()
										.map((configs) => {
											const {
												currency,
												quantity,
												configuration,
												complementary,
											} = configs[0]
											return addKeyToFieldArrayItem({
												complementary: isUsingExistingQuote ? 0 : complementary,
												quantity,
												configuration,
												date_wise_prices: configs.flatMap(
													({
														calculated_price,
														given_price,
														per_quantity_given_price,
														date: utcTimestamp,
														date_local: localTimestamp,
													}) => {
														const date = localOrUtcTimestampToLocalDate(
															localTimestamp,
															utcTimestamp
														)
														const newDate =
															utcTimestampToLocaDatesMap[utcTimestamp]
														let edited_given_price =
															config?.disablePriceResetsUsingRates
																? true
																: calculated_price !== given_price

														let saved_system_calculated_price:
															| number
															| undefined = Number(calculated_price || 0)
														let saved_manual_given_price: number | undefined =
															Number(given_price || 0)
														let disable_price_resets = Boolean(
															config?.disablePriceResetsUsingRates
														)

														// reset the given price if date has changed
														if (newDate && !isSame(newDate, date, "day")) {
															given_price = 0
															edited_given_price = false
															per_quantity_given_price = 0

															disable_price_resets = false
															//If user has changed trip dates from "EditBasicDetails", then we are disabling the rate change detection logic
															saved_system_calculated_price = undefined
															saved_manual_given_price = undefined
														}
														return newDate
															? [
																	{
																		currency,
																		price: Number(calculated_price || 0), //System Rate (Total)
																		per_quantity_price:
																			Number(calculated_price || 0) /
																			Number(quantity || 1), //System Rate Per Quantity
																		given_price: Number(given_price || 0),
																		per_quantity_given_price: Number(
																			per_quantity_given_price || 0
																		),
																		edited_given_price,
																		date: newDate,

																		// Last Quote - Saved data for system rate change detection
																		saved_system_calculated_price,
																		saved_manual_given_price,
																		disable_price_resets,
																	},
																]
															: []
													}
												),
											})
										})

							const { durationOption, activitySlot, activitySlotsOptions } =
								getActivityDurationSlotAndOptions(ticket_type, duration, slot)

							return {
								sort_order: sort_order || 0,
								transport_service: undefined,
								travel_activity: {
									id,
									dates: newDates,
									activity,
									ticket_type,
									duration: durationOption,
									slot: activitySlot,
									activity_slots: activitySlotsOptions,
									ticket_tourist_configurations,
									has_complementary: ticket_tourist_configurations.some(
										(c) => c.complementary > 0
									),
									comments,
								},
							}
						}
					)
				return {
					group_id,
					dates: newDates,
					services,
				}
			})
		given_price_of_activities_only = travelActivities.reduce<Array<TMoney>>(
			(total, { services }) =>
				addMoneyFromDifferentCurrencies(
					total.concat(
						services
							.flatMap(
								(s) => s.travel_activity?.ticket_tourist_configurations || []
							)
							.reduce<
								Array<TMoney>
							>((total, { date_wise_prices }) => addMoneyFromDifferentCurrencies(total.concat((date_wise_prices || []).reduce<TMoney[]>((total, { given_price, currency }) => addMoneyFromDifferentCurrencies(total.concat([moneyParseByDecimal(given_price || 0, currency)])), []))), []),
						[]
					)
				),
			[]
		)
		const data = collect(
			(
				[] as Array<
					(typeof transportServices)[number] | (typeof travelActivities)[number]
				>
			)
				.concat(transportServices)
				.concat(travelActivities)
		)
			.groupBy((s) => s.group_id || "")
			.toArray()
		// sort by the group ordering
		data.sort((a, b) => {
			const aGroup = a[0]?.group_id || Infinity
			const bGroup = b[0]?.group_id || Infinity
			if (aGroup === bGroup) return 0
			return aGroup - bGroup
		})
		data.forEach((groupedServices) => {
			const { dates } = groupedServices[0]
			const services: (typeof groupedServices)[number]["services"] =
				groupedServices.flatMap((g) => g.services)
			services.sort((a, b) => a.sort_order - b.sort_order)
			daywise_services.push(
				addKeyToFieldArrayItem({
					dates,
					services: services
						.map((s) =>
							s.transport_service
								? addKeyToFieldArrayItem({
										type: "transport_service" as const,
										service: s.transport_service,
									})
								: s.travel_activity
									? addKeyToFieldArrayItem({
											type: "travel_activity" as const,
											service: s.travel_activity,
										})
									: undefined
						)
						.filter((s): s is Exclude<typeof s, undefined> => Boolean(s)),
				})
			)
		})
	} else {
		const cabParams = collect(cabs)
			.groupBy((c) => `${c.date}-${c.transport_service_id}`)
			.toArray()
			.map((cabs) => {
				const date = utcTimestampToLocalDate(cabs[0].date)
				const stayDayDiff = getDiff(date, initialStartDate, "days")
				// remove the dates if it is not withing the duration range
				const newDate =
					stayDayDiff < durationInDays
						? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
						: undefined
				const cabParams = cabs.map(
					({
						currency,
						no_of_cabs,
						cab_locality,
						cab_type,
						given_price,
						calculated_price,
					}) => {
						let edited_given_price = config?.disablePriceResetsUsingRates
							? true
							: calculated_price !== given_price

						let saved_system_calculated_price: number | undefined = Number(
							calculated_price || 0
						)
						let saved_manual_given_price: number | undefined = Number(
							given_price || 0
						)
						let disable_price_resets = Boolean(
							config?.disablePriceResetsUsingRates
						)

						// reset the given price if date has changed
						if (newDate && !isSame(newDate, date, "day")) {
							given_price = 0
							edited_given_price = false

							disable_price_resets = false
							//If user has changed trip dates from "EditBasicDetails", then we are disabling the rate change detection logic
							saved_system_calculated_price = undefined
							saved_manual_given_price = undefined
						}
						return addKeyToFieldArrayItem({
							no_of_cabs,
							cab_type,
							cab_locality,
							date_wise_prices: newDate
								? [
										{
											currency,
											price: Number(calculated_price || 0), //System Rate (Total)
											per_quantity_price:
												Number(calculated_price || 0) / (no_of_cabs || 1), //System Rate Per Quantity
											given_price: Number(given_price || 0),
											per_quantity_given_price:
												Number(given_price || 0) / (no_of_cabs || 1),
											edited_given_price,
											date: newDate,
											// Last Quote - Saved data for system rate change detection
											saved_system_calculated_price,
											saved_manual_given_price,
											disable_price_resets,
										},
									]
								: [],
						})
					}
				)
				const { id, transport_service, comments } = cabs[0]

				return {
					id,
					dates: newDate ? [newDate] : [],
					transport_service,
					transport_service_location: transport_service.locations,
					cabs: cabParams,
					comments,
				}
			})
		const data = cabParams.reduce(
			(
				cabs: Array<
					(typeof cabParams)[number] & {
						travel_activities: Array<TValidTravelActivityDatewisePricesInputFieldValue>
					}
				>,
				cab
			) => {
				// Try to merge the cabs if sames dates and same service exists
				const foundIndex = cabs.findIndex(
					(c) =>
						c.transport_service.id === cab.transport_service.id &&
						c.comments === cab.comments &&
						c.dates.every((d) => cab.dates.find((a) => isSame(a, d, "day")))
				)
				if (foundIndex !== -1) {
					const existing = cabs[foundIndex]
					existing.cabs = existing.cabs.concat(cab.cabs)
				} else {
					cabs.push({
						...cab,
						travel_activities: [],
						transport_service_location: cab.transport_service.locations,
					})
				}
				return cabs
			},
			[]
		)
		given_price_of_activities_only = data.reduce<Array<TMoney>>(
			(total, { cabs }) =>
				addMoneyFromDifferentCurrencies(
					total.concat(
						(cabs || []).reduce<Array<TMoney>>(
							(total, { date_wise_prices }) =>
								addMoneyFromDifferentCurrencies(
									total.concat(
										(date_wise_prices || []).reduce<TMoney[]>(
											(total, { given_price, currency }) =>
												addMoneyFromDifferentCurrencies(
													total.concat([
														moneyParseByDecimal(given_price || 0, currency),
													])
												),
											[]
										)
									)
								),
							[]
						),
						[]
					)
				),
			[]
		)

		const travelActivities = getEditableTravelActivities(
			travel_activities || [],
			initialStartDate,
			newStartDate,
			durationInDays,
			hasPaxChanged,
			config
		)
		given_price_of_activities_only = travelActivities.reduce<Array<TMoney>>(
			(total, { travel_activities }) =>
				addMoneyFromDifferentCurrencies(
					total.concat(
						(travel_activities || [])
							.flatMap((t) => t.ticket_tourist_configurations)
							.reduce<
								Array<TMoney>
							>((total, { date_wise_prices }) => addMoneyFromDifferentCurrencies(total.concat((date_wise_prices || []).reduce<TMoney[]>((total, { given_price, currency }) => addMoneyFromDifferentCurrencies(total.concat([moneyParseByDecimal(given_price || 0, currency)])), []))), []),
						[]
					)
				),
			[]
		)
		const detachedTravelActivities = travelActivities.filter((activity) => {
			// put the activity to the first matching transport service
			const cab = data.find((cab) => {
				return String(activity.quote_cab_id || "0") === String(cab.id)
			})
			if (cab) {
				if (!cab.travel_activities) {
					cab.travel_activities = []
				}
				cab.travel_activities = cab.travel_activities.concat(
					activity.travel_activities
				)
				return false
			} else {
				return true
			}
		})
		const mergedSimilarDayActivities: typeof detachedTravelActivities = []
		detachedTravelActivities.forEach((a) => {
			const existing = mergedSimilarDayActivities.find(
				(d) =>
					d.dates.length === a.dates.length &&
					d.dates.every((date) => a.dates.find((d) => isSame(d, date, "day")))
			)
			if (existing) {
				existing.travel_activities = existing.travel_activities.concat(
					a.travel_activities
				)
			} else {
				mergedSimilarDayActivities.push(
					addKeyToFieldArrayItem({
						dates: a.dates,
						travel_activities: a.travel_activities,
					})
				)
			}
		})

		data.forEach(({ travel_activities, dates, ...service }) => {
			daywise_services.push(
				addKeyToFieldArrayItem({
					dates,
					services: (
						[] as IQuoteCabParams["daywise_services"][number]["services"]
					)
						.concat([
							addKeyToFieldArrayItem({
								type: "transport_service",
								service,
							}),
						])
						.concat(
							travel_activities.map((t) =>
								addKeyToFieldArrayItem({
									type: "travel_activity",
									service: t,
								})
							)
						),
				})
			)
		})
		// Now push the mergedSimilarDayActivities into daywise_services
		mergedSimilarDayActivities.forEach((activity) => {
			daywise_services.push(
				addKeyToFieldArrayItem({
					dates: activity.dates,
					services: activity.travel_activities.map((activity) =>
						addKeyToFieldArrayItem({
							type: "travel_activity",
							service: activity,
						})
					),
				})
			)
		})
	}

	const given_price = addMoneyFromDifferentCurrencies(
		given_price_of_cabs_only.concat(given_price_of_activities_only)
	)

	const orderedDaywiseServices = orderDaywiseServicesByDate(daywise_services)

	return {
		given_price,
		given_price_of_cabs_only,
		given_price_of_activities_only,
		used_cab_types,
		daywise_services: orderedDaywiseServices,
		cabs_tax_percentage: cabs_tax_percentage
			? Number(cabs_tax_percentage)
			: undefined,
		cabs_price_calculation_configurations:
			cabs_price_calculation_configurations?.map((c) => ({
				...c,
				services: c.services.reduce(
					(services, s) => {
						if (!s.date) return services.concat([{ ...s, date: undefined }])
						const date = utcTimestampToLocalDate(s.date)
						const stayDayDiff = getDiff(date, initialStartDate, "days")
						// remove the dates if it is not withing the duration range
						const newDate =
							stayDayDiff < durationInDays
								? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
								: undefined
						if (newDate) {
							return services.concat([
								{
									...s,
									date: newDate,
								},
							])
						}
						return services
					},
					[] as Required<IQuoteCabParams>["cabs_price_calculation_configurations"][number]["services"]
				),
			})),
		cabs_price_calculations: cabs_price_calculations?.map((c) => ({
			...c,
			metric_quantities: {
				...c.metric_quantities,
				datewise_total_kms: (c.metric_quantities.datewise_total_kms || []).map(
					(t) => ({
						...t,
						date: t.date ? parseDate(t.date) : undefined,
					})
				),
			},
			metric_wise_prices: c.metric_wise_prices.map((m) => {
				let edited_given_quantity = m.quantity !== m.given_quantity

				let edited_given_price =
					m.per_quantity_given_price !== m.per_quantity_price
				// if the pax and start date changed then reset the price
				if (!isSame(newStartDate, initialStartDate, "days") || hasPaxChanged) {
					edited_given_quantity = false
					edited_given_price = false
				}
				return addKeyToFieldArrayItem({
					...m,
					per_quantity_given_price: Number(m.per_quantity_given_price),
					edited_given_quantity,
					edited_given_price,
					// Preparing saved quote data for system rate/quantity change detection between quotes
					saved_system_calculated_price: Number(m.per_quantity_price || 0),
					saved_manual_given_price: Number(m.per_quantity_given_price || 0),
					saved_system_quantity: Number(m.quantity || 0),
					saved_manual_quantity: Number(m.given_quantity || 0),
					disable_price_resets: Boolean(config?.disablePriceResetsUsingRates),
				})
			}),
			services: c.services.reduce(
				(services, s) => {
					if (!s.date)
						return (services || []).concat([{ ...s, date: undefined }])
					const date = utcTimestampToLocalDate(s.date)
					const stayDayDiff = getDiff(date, initialStartDate, "days")
					// remove the dates if it is not withing the duration range
					const newDate =
						stayDayDiff < durationInDays
							? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
							: undefined
					if (newDate) {
						return (services || []).concat([
							{
								...s,
								date: newDate,
							},
						])
					}
					return services
				},
				[] as Required<IQuoteCabParams>["cabs_price_calculations"][number]["services"]
			),
		})),
		add_cab_prices_at_once: cabs_price_calculations?.length ? true : false,
	}
}

function getEditableTravelActivities(
	travelActivities: $PropertyType<IQuote, "travel_activities">,
	initialStartDate: Date,
	newStartDate: Date,
	durationInDays: number,
	clearTicketConfiguration: boolean,
	configuration?: {
		disablePriceResetsUsingRates?: boolean
	}
): Array<
	IQuoteTravelActivityParams["travel_activities"][number] & {
		quote_cab_id?: number
	}
> {
	return collect(travelActivities)
		.groupBy((s) =>
			[
				s.activity.id,
				s.ticket_type?.id || ".",
				s.duration || ".",
				s.slot || ".",
				s.quote_cab_id || ".",
			].join("-")
		)
		.toArray()
		.map((travelActivities) => {
			const data = travelActivities.map(
				({
					date: utcDate,
					date_local: localDate,
					ticket_tourist_configurations,
				}) => {
					const date = localOrUtcTimestampToLocalDate(localDate, utcDate)
					const stayDayDiff = getDiff(date, initialStartDate, "days")
					// remove the dates if it is not withing the duration range
					const newDate =
						stayDayDiff < durationInDays
							? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
							: undefined

					return addKeyToFieldArrayItem({
						dates: newDate ? [newDate] : [],
						ticket_tourist_configurations: clearTicketConfiguration
							? []
							: ticket_tourist_configurations.map(
									({
										calculated_price,
										given_price,
										currency,
										per_quantity_given_price,
										quantity,
										...config
									}) => {
										let edited_given_price =
											configuration?.disablePriceResetsUsingRates
												? true
												: calculated_price !== given_price

										let saved_system_calculated_price: number | undefined =
											Number(calculated_price || 0)
										let saved_manual_given_price: number | undefined = Number(
											given_price || 0
										)
										let disable_price_resets = Boolean(
											configuration?.disablePriceResetsUsingRates
										)
										// reset the given price if date has changed
										if (newDate && !isSame(newDate, date, "day")) {
											given_price = 0
											edited_given_price = false
											per_quantity_given_price = 0

											disable_price_resets = false
											//If user has changed trip dates from "EditBasicDetails", then we are disabling the rate change detection logic
											saved_system_calculated_price = undefined
											saved_manual_given_price = undefined
										}
										return addKeyToFieldArrayItem({
											...config,
											quantity,
											date_wise_prices: newDate
												? [
														{
															currency,
															price: Number(calculated_price || 0), //System Rate (Total)
															per_quantity_price:
																Number(calculated_price || 0) /
																Number(quantity || 1), //System Rate Per Quantity
															given_price: Number(given_price || 0),
															per_quantity_given_price: Number(
																per_quantity_given_price || 0
															),
															edited_given_price,
															date: newDate,

															// Last Quote - Saved data for system rate change detection
															saved_system_calculated_price,
															saved_manual_given_price,
															disable_price_resets,
														},
													]
												: [],
										})
									}
								),
					})
				}
			)
			const dates = data.flatMap((d) => d.dates)
			const { duration, ticket_type, quote_cab_id } = travelActivities[0]
			const ticket_tourist_configurations = collect(
				data.flatMap((d) => d.ticket_tourist_configurations)
			)
				.groupBy((s) => s.configuration.id)
				.toArray()
				.map((configs) => ({
					...configs[0],
					date_wise_prices: configs.flatMap((s) => s.date_wise_prices),
				}))
			return addKeyToFieldArrayItem({
				dates: dates,
				quote_cab_id,
				travel_activities: [
					addKeyToFieldArrayItem({
						...travelActivities[0],
						duration: ticket_type?.durations?.find((d) => d.iso === duration),
						dates,
						ticket_tourist_configurations,
					}),
				],
			})
		})
}

function getEditableCabExtras(
	extras: $PropertyType<IQuote, "transport_extras">,
	initialStartDate: Date,
	newStartDate: Date,
	durationInDays: number
): IQuoteCabExtraParams {
	return extras.map(
		({ given_price, date, date_local, comments, ...others }) => {
			let newDate: Date | undefined = undefined
			if (date) {
				const stayDayDiff = getDiff(
					localOrUtcTimestampToLocalDate(date_local, date),
					initialStartDate,
					"days"
				)
				// remove the dates if it is not withing the duration range
				newDate =
					stayDayDiff < durationInDays
						? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
						: undefined
			}
			return {
				...others,
				price: given_price,
				date: date ? newDate : undefined,
				comments: comments || "",
			}
		}
	)
}

function getEditableOtherExtras(
	extras: $PropertyType<IQuote, "other_extras">,
	initialStartDate: Date,
	newStartDate: Date,
	durationInDays: number
): IQuoteOtherExtraParams {
	return extras.map(
		({ given_price, date, date_local, comments, ...others }) => {
			let newDate: Date | undefined = undefined
			if (date) {
				const stayDayDiff = getDiff(
					localOrUtcTimestampToLocalDate(date_local, date),
					initialStartDate,
					"days"
				)
				// remove the dates if it is not withing the duration range
				newDate =
					stayDayDiff < durationInDays
						? startOf(addUnit(newStartDate, stayDayDiff, "days"), "day")
						: undefined
			}
			return {
				...others,
				price: given_price,
				date: date ? newDate : undefined,
				comments: comments || "",
			}
		}
	)
}

function getEditableFlights(
	flight_form_fill_mode: TFlightFormFillMode,
	flight_type: TFlightType,
	flights: $PropertyType<IQuote, "flights">,
	initialStartDate: Date,
	newStartDate: Date,
	isUsingExistingQuote: boolean,
	noOfAdults: number,
	children: TChildrenArray,
	defaultCostingCurrency: string
): IQuoteFlightParams {
	// In the future, if needed, this condition can be removed when we will use this flights in use this quote.
	// The code below is designed to handle all required cases accordingly (use this quote).
	// Setting the initial price values and shifting the date will work properly in that scenario.
	if (isUsingExistingQuote) return {}
	const [infants, no_of_children, childrenWithMoreAge] =
		getChildrenDistribution(children)
	const initialPrice = {
		currency: defaultCostingCurrency,
		calculated_price: undefined,
		given_price: undefined,
		comments: "",
		no_of_adults: noOfAdults + childrenWithMoreAge,
		children: no_of_children,
		infants: infants,
		cost_per_person_enabled: 0 as const,
		given_per_person_enabled: 0 as const,
	}
	return {
		flight_form_fill_mode: flight_form_fill_mode,
		flight_type: flight_type,
		flights: flights.map((flightItem) => ({
			// The price is reset to the initial pricing when an existing quote is used.
			pricing: isUsingExistingQuote
				? initialPrice
				: {
						currency: flightItem.currency,
						calculated_price: flightItem.calculated_price,
						given_price: flightItem.given_price,
						comments: flightItem.comments,
						no_of_adults: flightItem.no_of_adults,
						children: flightItem.children,
						infants: flightItem.infants,
						per_person_cost_price: flightItem.per_person_cost_price,
						per_person_given_price: flightItem.per_person_given_price,
						cost_per_person_enabled: flightItem.per_person_cost_price ? 1 : 0,
						given_per_person_enabled: flightItem.per_person_given_price ? 1 : 0,
					},

			groups: flightItem.groups.map((group) => {
				//  The group.info array currently contains 6 elements.
				//  This number is expected to remain constant in the future.

				const infoMap = new Map(
					group.info?.map((item) => [item.type, item]) || []
				)

				const additionalOptions: {
					id: number
					name: string
					value: string
				}[] = []

				let baggageAdded = false

				group.info?.forEach((option) => {
					switch (option.type) {
						case "adult_check_in_baggage":
						case "adult_hand_baggage":
						case "infant_hand_baggage":
						case "infant_check_in_baggage":
							if (!baggageAdded) {
								baggageAdded = true
								additionalOptions.push({
									id: 2,
									name: "Baggage",
									value: "baggage",
								})
							}
							break

						case "meal_preference":
							additionalOptions.push({
								id: 4,
								name: "Meals",
								value: "meal",
							})
							break

						case "fare_type":
							additionalOptions.push({
								id: 3,
								name: "Fare Type",
								value: "fare",
							})
							break
					}
				})

				if (group.remarks) {
					additionalOptions.push({
						id: 5,
						name: "Notes for Customer",
						value: "notes_customer",
					})
				}
				return {
					type: group.type,
					additional_options: additionalOptions,
					info: {
						adult_check_in_baggage: infoMap.get("adult_check_in_baggage"),
						adult_hand_baggage: infoMap.get("adult_hand_baggage"),
						infant_check_in_baggage: infoMap.get("infant_check_in_baggage"),
						infant_hand_baggage: infoMap.get("infant_hand_baggage"),
						fare_type: infoMap.get("fare_type"),
						meal_preference: infoMap.get("meal_preference"),
						category_class:
							(infoMap.get("category_class")?.value as TFlightClass) ||
							DEFAULT_FLIGHT_CLASS,
					},
					remarks: group.remarks || "",
					schedules: group.schedules.map(
						({
							source,
							destination,
							departs_at,
							departs_at_local,
							arrives_at,
							arrives_at_local,
							remarks,
							airline,
							number_plate,
							number_plate_formatted,
							airline_id,
							...others
						}) => {
							const { newUtc: newDepartsUtc, newLocal: newDepartsLocal } =
								shiftDateTimeWithFullOffset(
									departs_at_local,
									departs_at,
									initialStartDate,
									newStartDate
								)

							const { newUtc: newArrivesUtc, newLocal: newArrivesLocal } =
								shiftDateTimeWithFullOffset(
									arrives_at_local,
									arrives_at,
									initialStartDate,
									newStartDate
								)

							const newAirline = {
								id: number_plate,
								number_plate_formatted: number_plate_formatted,
								number_plate: number_plate,
								airline: airline,
							}
							return {
								...others,
								departs_at: !departs_at
									? undefined
									: newDepartsUtc || toISOString(clone(newStartDate)),
								arrives_at: !arrives_at
									? undefined
									: newArrivesUtc ||
										toISOString(addUnit(newStartDate, 2, "hours")),
								departs_at_local: !departs_at_local
									? undefined
									: parseDate(newDepartsLocal, "YYYY-MM-DD HH:mm:ss") ||
										clone(newStartDate),
								arrives_at_local: !arrives_at_local
									? undefined
									: parseDate(newArrivesLocal, "YYYY-MM-DD HH:mm:ss") ||
										addUnit(newStartDate, 2, "hours"),
								remarks: remarks || "",
								airline: number_plate ? newAirline : undefined,
								source: source ? addLabelToAirport(source) : undefined,
								destination: destination
									? addLabelToAirport(destination)
									: undefined,
							}
						}
					),
				}
			}),
		})),
	}
}

function getEditableVisaServices(
	visas: TEditVisaServiceInQuote[]
): TVisaServiceInQuote {
	return {
		services: visas.map((visa) => {
			return {
				country: visa.country,
				visa: visa.visa_service,
				visa_type: visa.visa_type,
				given_price: Number(formatMoneyByDecimal(visa.given_price_money)),
				pax: visa.quantity,
				per_quantity_given_price: Number(
					formatMoneyByDecimal(
						divideMoneyBy(visa.given_price_money, visa.quantity)
					)
				),
				comments: visa.comments,
				showComment: Boolean(visa.comments?.length || 0),
			}
		}),
	}
}

// shifts the date time of the flight according to the new start date (in use this quote case the flight date will be set according to the new start date)
function shiftDateTimeWithFullOffset(
	local: string | null | undefined,
	utc: string,
	initialStartDate: Date,
	newStartDate: Date
): { newLocal: string; newUtc: string } {
	const originalUtc = formatDate(utc, "YYYY-MM-DD HH:mm:ss")
	const originalLocal = formatDate(local || utc, "YYYY-MM-DD HH:mm:ss")

	const initialBase = formatDate(
		startOf(initialStartDate, "day"),
		"YYYY-MM-DD HH:mm:ss"
	)
	const newBase = formatDate(
		startOf(newStartDate, "day"),
		"YYYY-MM-DD HH:mm:ss"
	)

	const utcDiff = getDiff(originalUtc, initialBase, "ms")
	const localDiff = getDiff(originalLocal, initialBase, "ms")

	const newLocal = toISOString(addUnit(newBase, localDiff, "ms"))
	const newUtc = toISOString(addUnit(newBase, utcDiff, "ms"))
	return {
		newLocal,
		newUtc,
	}
}

function normalizeDaywiseServices(cabs: IQuoteCabParams) {
	const {
		daywise_services,
		cabs_price_calculations,
		cabs_price_calculation_configurations,
		...others
	} = cabs
	let sortOrder = 0
	const { transportServices, activities } = daywise_services.reduce(
		({ transportServices, activities }, { dates, services }, currentIndex) => {
			const groupId = currentIndex + 1
			services.forEach(({ type, service }) => {
				sortOrder++
				if (type === "transport_service") {
					const {
						cabs: selectedCabTypes,
						duration,
						comments,
						...otherData
					} = service
					dates.forEach((date, i) => {
						selectedCabTypes.forEach((cab) => {
							const {
								date_wise_prices,
								fetching_prices,
								cab_type,
								no_of_cabs,
								...otherCab
							} = cab
							if (!cab_type || !no_of_cabs) {
								return
							}
							const { price, given_price, booked_price, currency } =
								date_wise_prices?.at(i) ||
									date_wise_prices?.at(0) || {
										currency: "INR",
										price: 0,
										given_price: 0,
										booked_price: 0,
									}
							transportServices.push({
								...otherData,
								...otherCab,
								date: toISOString(startOf(date, "day")),
								cab_type,
								no_of_cabs,
								cab_type_id: cab_type.id,
								transport_service_id: otherData.transport_service.id,
								currency,
								calculated_price: price,
								given_price: given_price || 0,
								booked_price: booked_price || 0,
								comments: comments || "",
								fetching_prices: Boolean(fetching_prices),
								group_id: groupId,
								sort_order: sortOrder,
								id: Math.random() * 1000,
								duration: duration?.iso,
							})
						})
					})
				} else if (type === "travel_activity") {
					const { ticket_tourist_configurations, dates, ...activity } = service
					if (
						!activity.activity ||
						!ticket_tourist_configurations?.length ||
						!dates?.length
					)
						return
					dates.forEach((date, i) => {
						activities.push(
							addKeyToFieldArrayItem({
								...activity,
								group_id: groupId,
								sort_order: sortOrder,
								id: Math.random(),
								date: toISOString(startOf(date, "day")),
								duration: activity.duration?.iso,
								ticket_tourist_configurations:
									ticket_tourist_configurations.map(
										({ date_wise_prices, fetching_prices, ...config }) => {
											const {
												price,
												given_price,
												currency,
												per_quantity_given_price,
											} = date_wise_prices?.at(i) ||
												date_wise_prices?.at(0) || {
													currency: "INR",
													calculated_price: 0,
													given_price: 0,
													booked_price: 0,
													per_quantity_given_price: 0,
												}
											return addKeyToFieldArrayItem({
												...config,
												quantity: config.quantity || 0,
												complementary: config.complementary || 0,
												currency,
												calculated_price: price,
												given_price: given_price || 0,
												per_quantity_given_price: per_quantity_given_price || 0,
												fetching_prices: Boolean(fetching_prices),
											})
										}
									),
							})
						)
					})
				}
			})
			return { transportServices, activities }
		},
		{
			transportServices: [] as Array<
				Omit<IQuoteCab, "quote_id"> & {
					transport_service_location: TTransportServiceLocation
					fetching_prices: boolean
				}
			>,
			activities: [] as Array<Omit<IQuoteTravelActivity, "quote_id">>,
		}
	)
	if (cabs_price_calculations?.length && transportServices?.length) {
		const total = cabs.given_price_of_cabs_only
		const currency =
			(total.length
				? total[0].currency
				: cabs_price_calculations[0].currency) || "INR"
		// remove the any given prices from cabs
		transportServices.forEach((t, index) => {
			transportServices[index] = {
				...t,
				currency,
				calculated_price: undefined,
				given_price: 0,
				booked_price: 0,
			}
		})
		// set the given price and current to last cab
		if (total.length <= 1) {
			transportServices[transportServices.length - 1].given_price =
				!total.length ? 0 : Number(formatMoneyByDecimal(total[0]))
		}
	}

	return {
		...others,
		cabs_price_calculations: cabs_price_calculations?.map((s) => ({
			...s,
			id: Math.random() * 1000,
			metric_quantities: {
				...s.metric_quantities,
				datewise_total_kms: (s.metric_quantities.datewise_total_kms || []).map(
					(t) => ({
						...t,
						date: t.date
							? formatDate(parseDate(t.date), "YYYY-MM-DD")
							: undefined,
					})
				),
			},
			services: (s.services || []).map(({ date, ...s }) => ({
				...s,
				date: date ? toISOString(startOf(date, "day")) : undefined,
				id: Math.random() * 1000,
			})),
			metric_wise_prices: (s.metric_wise_prices || []).map((m) => ({
				...m,
				id: Math.random() * 1000,
				given_quantity: m.given_quantity || 0,
				per_quantity_given_price: m.per_quantity_given_price || 0,
			})),
			fetching_prices:
				s.metric_wise_prices?.some((m) => Boolean(m.fetching_prices)) || false,
		})),
		cabs_price_calculation_configurations:
			cabs.cabs_price_calculation_configurations?.map((s) => ({
				...s,
				services: s.services.map(({ date, ...s }) => ({
					id: Math.random() * 1000,
					...s,
					date: date ? toISOString(startOf(date, "day")) : undefined,
				})),
			})),
		cabs: transportServices,
		travel_activities: activities,
	}
}

function normalizeCabExtras(
	cabExtras: IQuoteCabExtraParams
): Array<Omit<IQuoteTransportExtras, "quote_id">> {
	return cabExtras.map(({ date, ...otherData }) => {
		return {
			...otherData,
			id: Math.random(),
			date: date ? toISOString(startOf(date, "day")) : "",
			given_price: otherData.price,
			comments: otherData.comments || "",
		}
	})
}

function normalizeOtherExtras(
	otherExtras: IQuoteOtherExtraParams
): Array<Omit<IQuoteOtherExtras, "quote_id">> {
	return otherExtras.map(({ date, ...otherData }) => {
		return {
			...otherData,
			id: Math.random(),
			date: date ? toISOString(startOf(date, "day")) : "",
			given_price: otherData.price,
			comments: otherData.comments || "",
		}
	})
}

function normalizeVisaServices({
	visas,
	startDate,
}: {
	visas: TVisaServiceInQuote
	startDate: Date
}): {
	country_id: number
	visa_service_id: number
	visa_type_id: number
	quantity: number
	currency: string
	calculated_price: number | null // pax * the system rate for per person
	per_quantity_given_price: number // per / person price from given price
	given_price: number // the given price
	comments: string | null
}[] {
	const bookingFrom = formatDate(startDate, "YYYY-MM-DD HH:mm:ss")
	return visas.services.map((visaService) => {
		const { country, visa, visa_type, pax, given_price, comments } = visaService
		const isExpired = isVisaRateExpired({
			rateValidityDate: visa_type?.price_valid_till,
			startDate: bookingFrom,
		})

		let calculatedPrice: number | null = null
		if (!isExpired && visa_type?.cost_price && pax) {
			const costPerPerson = Number(formatMoneyByDecimal(visa_type.cost_price))
			if (!Number.isNaN(costPerPerson) && !Number.isNaN(Number(pax))) {
				calculatedPrice = costPerPerson * Number(pax)
			}
		}
		const perQuantityGivenPrice =
			given_price && pax ? Number(given_price) / Number(pax) : 0
		return {
			country_id: country?.id ?? 0,
			visa_service_id: visa?.id ?? 0,
			visa_type_id: visa_type?.id ?? 0,
			quantity: pax ?? 0,
			currency: visa?.currency ?? "",
			calculated_price: calculatedPrice, // pax* the system rate for per person
			per_quantity_given_price: perQuantityGivenPrice, // per person price from given price
			given_price: given_price ?? 0, // default to 0 if undefined
			comments: comments ?? null, // ensure null not undefined
		}
	})
}

function SyncIsFetchingPricesField() {
	const { onChange: setIsFetchingPrices } =
		useFieldValue<INewQuoteState["fetching_prices"]>("fetching_prices")
	const { value: hotelOptions } =
		useFieldValue<INewQuoteState["hotel_options"]>("hotel_options")
	const { value: transportServices } =
		useFieldValue<INewQuoteState["cabs"]>("cabs")
	useEffect(() => {
		setIsFetchingPrices(true)
		const h = setTimeout(() => {
			const isFetchingPrices =
				hotelOptions.some((v) =>
					v.hotels ? v.hotels.some((h) => Boolean(h.fetching_prices)) : false
				) ||
				transportServices.daywise_services?.some((v) =>
					v.services.some((v) =>
						Boolean(
							v.type === "transport_service"
								? v.service.cabs?.some((s) => s.fetching_prices)
								: v.service.ticket_tourist_configurations?.some(
										(s) => s.fetching_prices
									)
						)
					)
				) ||
				transportServices.cabs_price_calculations?.some((v) =>
					v.metric_wise_prices?.some((v) => Boolean(v.fetching_prices))
				)
			setIsFetchingPrices(isFetchingPrices)
		}, 1000)
		return () => clearTimeout(h)
	}, [hotelOptions, transportServices, setIsFetchingPrices])
	return null
}

function XHR(xhr: XHRInstance) {
	return {
		/**
		 * Create a new quote for a trip
		 */
		async saveQuote(
			tripId: string | number,
			{
				generationId,
				isAIItinerarybuilder,
				refId,
				startDate,
				days,
				no_of_adults,
				complimentary_count,
				children,
				options,
				hotel_options,
				cabs,
				transportExtras,
				otherExtras,
				flights,
				comments,
				cabPrice,
				transportExtrasPrice,
				flightPrice,
				otherExtrasPrice,

				tax_name,
				currency_pairs,
				functional_currency,
				given_currency,
				costing_currency,
				pricing_strategy,
				visas,
				visasPrice,
				ai_autofilled,
				minMarkupPercentage,
			}: {
				isAIItinerarybuilder?: boolean
				refId?: number
				generationId?: string
				complimentary_count: number
				// shared between options
				startDate: Date
				days: number
				no_of_adults: number
				children: TChildrenArray
				cabs: IQuoteCabParams
				transportExtras: IQuoteCabExtraParams
				flights: IQuoteFlightParams
				otherExtras: IQuoteOtherExtraParams

				cabPrice: Array<TMoney>
				transportExtrasPrice: Array<TMoney>
				flightPrice: Array<TMoney>
				otherExtrasPrice: Array<TMoney>

				comments?: string
				tax_name: string
				currency_pairs: INewQuoteState["currency_pairs"]
				functional_currency: string
				given_currency: string
				costing_currency: string

				options: Array<
					Omit<INewQuoteState["options"][number], "taxes"> & {
						taxes?: INewQuoteState["options"][number]["taxes"]
					}
				>
				hotel_options: INewQuoteState["hotel_options"]

				pricing_strategy?: TGivenQuotePricingStrategy
				visas: TVisaServiceInQuote
				visasPrice: TMoney[]
				ai_autofilled?: 0 | 1
				minMarkupPercentage?: number
			}
		): Promise<ITripQuote> {
			// Avoid saving quote if costing currency and given currency is not same and exhange rate is not provided for the same
			if (costing_currency !== given_currency) {
				const exchangeRate = currency_pairs.find(
					(pair) =>
						pair.baseCurrency === costing_currency &&
						pair.counterCurrency === given_currency
				)

				if (!exchangeRate || !exchangeRate.ratio || exchangeRate.ratio <= 0) {
					throw new Error(
						`Exchange rate from costing currency (${costing_currency}) to given currency (${given_currency}) is not available or invalid`
					)
				}
			}

			const configuration = {
				is_ai_generated: isAIItinerarybuilder ? "1" : "0",
				generation_id: generationId || undefined,
				ref: refId,
				start_date: dateToUTCString(startOf(startDate, "day")),
				start_date_local: formatDate(startOf(startDate, "day"), "YYYY-MM-DD"),
				end_date: dateToUTCString(
					endOf(addUnit(startDate, days - 1, "days"), "day")
				),
				end_date_local: formatDate(
					endOf(addUnit(startDate, days - 1, "days"), "day"),
					"YYYY-MM-DD"
				),
				no_of_adults,
				complimentary_count,
				children,
				timezone_offset: config.timezoneOffset,
				comments,
				tax_name,
				currency_pairs,
				costing_currency,
				functional_currency,
				given_currency,
				pricing_strategy,
			}
			const normalizedDaywiseServices = normalizeDaywiseServices(cabs)
			const sharedServicesAmongOptions = {
				cabs: normalizedDaywiseServices.cabs.map(
					({
						id,
						sort_order,
						group_id,
						date,
						cab_type,
						transport_service,
						cab_locality,
						calculated_price,
						given_price,
						comments,
						no_of_cabs,
						currency,
						start_time,
						duration,
					}) => ({
						id,
						sort_order,
						group_id,
						no_of_cabs,
						comments,
						calculated_price,
						given_price,
						date: dateToUTCString(date),
						date_local: formatDate(date, "YYYY-MM-DD"),
						cab_type: cab_type.name,
						transport_service_id: transport_service.id,
						cab_locality: cab_locality?.name,
						currency,
						start_time,
						duration,
					})
				),
				used_cab_types: (normalizedDaywiseServices.used_cab_types || []).map(
					(c) => ({
						cab_type: c.cab_type.name,
						no_of_cabs: c.no_of_cabs,
					})
				),
				cabs_tax_percentage: normalizedDaywiseServices.cabs_tax_percentage,
				cabs_price_calculation_configurations: (
					normalizedDaywiseServices.cabs_price_calculation_configurations || []
				).map(({ services, metric_preset, id }) => ({
					id,
					metric_preset_id: metric_preset?.id,
					services: services.map((s) => ({
						date: s.date ? dateToUTCString(s.date) : undefined,
						date_local: s.date ? formatDate(s.date, "YYYY-MM-DD") : undefined,
						transport_service_id: s.transport_service.id,
						is_addon: isTruthy(s.is_addon) ? 1 : 0,
					})),
				})),
				cabs_price_calculations: (
					normalizedDaywiseServices.cabs_price_calculations || []
				).map(({ cab_type, services, metric_wise_prices, ...c }) => ({
					...c,
					tax_percentage: normalizedDaywiseServices.cabs_tax_percentage,
					cab_type_id: cab_type.id,
					services: services.map((s) => ({
						date: s.date ? dateToUTCString(s.date) : undefined,
						date_local: s.date ? formatDate(s.date, "YYYY-MM-DD") : undefined,
						transport_service_id: s.transport_service.id,
						is_addon: isTruthy(s.is_addon) ? 1 : 0,
					})),
					metric_wise_prices: metric_wise_prices.map(
						({
							metric,
							quantity,
							given_quantity,
							currency,
							per_quantity_price,
							per_quantity_given_price,
						}) => ({
							metric_id: metric.id,
							quantity,
							given_quantity,
							currency,
							per_quantity_price,
							per_quantity_given_price,
						})
					),
				})),
				travel_activities: normalizeDaywiseServices(cabs).travel_activities.map(
					({
						sort_order,
						group_id,
						quote_cab_id,
						date,
						activity,
						ticket_type,
						duration,
						ticket_tourist_configurations,
						comments,
						slot,
					}) => ({
						quote_cab_id,
						sort_order,
						group_id,
						date: dateToUTCString(date),
						date_local: formatDate(date, "YYYY-MM-DD"),
						activity_id: activity.id,
						ticket_type_id: ticket_type?.id,
						duration,
						slot,
						comments,
						ticket_tourist_configurations: ticket_tourist_configurations.map(
							({ configuration, ...config }) => ({
								configuration_id: configuration.id,
								...config,
							})
						),
					})
				),
				transport_extras: normalizeCabExtras(transportExtras).map(
					({
						date,
						service,
						given_price,
						calculated_price,
						currency,
						booked_price,
						comments,
					}) => ({
						date: date ? dateToUTCString(date) : "",
						date_local: date ? formatDate(date, "YYYY-MM-DD") : "",
						service: service.name,
						currency,
						calculated_price,
						price: given_price,
						booked_price,
						comments,
					})
				),

				flight_form_fill_mode: flights.flight_form_fill_mode,
				flight_type: flights.flight_type,
				flights: (flights.flights || []).map((flightItem) => {
					const {
						cost_per_person_enabled,
						given_per_person_enabled,
						...pricingForApi
					} = flightItem.pricing
					return {
						price: pricingForApi,
						groups: flightItem.groups.map((group) => {
							const additionalOptions = (group.additional_options || []).map(
								(field) => field.value
							)
							const infants = flightItem.pricing.infants > 0

							const rawInfo = [
								{
									type: "adult_check_in_baggage",
									value: additionalOptions?.includes("baggage")
										? group.info?.adult_check_in_baggage?.value
										: undefined,
								},
								{
									type: "adult_hand_baggage",
									value: additionalOptions?.includes("baggage")
										? group.info?.adult_hand_baggage?.value
										: undefined,
								},
								{
									type: "infant_hand_baggage",
									value:
										additionalOptions?.includes("baggage") && infants
											? group.info?.infant_hand_baggage?.value
											: undefined,
								},
								{
									type: "infant_check_in_baggage",
									value:
										additionalOptions?.includes("baggage") && infants
											? group.info?.infant_check_in_baggage?.value
											: undefined,
								},
								{
									type: "meal_preference",
									value: additionalOptions?.includes("meal")
										? group.info?.meal_preference?.value
										: undefined,
								},
								{
									type: "fare_type",
									value: additionalOptions?.includes("fare")
										? group.info?.fare_type?.value
										: undefined,
								},
								{
									type: "category_class",
									value: group.info?.category_class || "Economy",
								},
							]
							return {
								type: group.type,
								remarks: additionalOptions?.includes("notes_customer")
									? group.remarks
									: undefined,
								info: rawInfo.filter(
									(item) =>
										item.value !== undefined &&
										item.value !== null &&
										item.value !== ""
								),
								schedules: group.schedules.map(
									({
										source,
										destination,
										departs_at,
										arrives_at,
										departs_at_local,
										arrives_at_local,
										airline,
										...other
									}) => {
										const departures_at_local = departs_at_local
											? formatDate(departs_at_local, "YYYY-MM-DD HH:mm:ss")
											: undefined
										const arriving_at_local = arrives_at_local
											? formatDate(arrives_at_local, "YYYY-MM-DD HH:mm:ss")
											: undefined
										return {
											source_id: source?.id,
											destination_id: destination?.id,
											departs_at:
												departures_at_local && source?.timezone?.name
													? convertLocalToUtcAccordingToTimezone(
															departures_at_local,
															source?.timezone?.name
														)
													: departures_at_local
														? dateToUTCString(departures_at_local)
														: undefined,
											departs_at_local: departures_at_local,
											arrives_at:
												arriving_at_local && destination?.timezone?.name
													? convertLocalToUtcAccordingToTimezone(
															arriving_at_local,
															destination?.timezone?.name
														)
													: arriving_at_local
														? dateToUTCString(arriving_at_local)
														: undefined,
											arrives_at_local: arriving_at_local,
											airline_id: airline?.airline.id,
											number_plate: airline?.number_plate,
											...other,
										}
									}
								),
							}
						}),
					}
				}),
				other_extras: normalizeOtherExtras(otherExtras).map(
					({
						date,
						service,
						given_price,
						currency,
						calculated_price,
						comments,
						booked_price,
					}) => ({
						date: date ? dateToUTCString(date) : "",
						date_local: date ? formatDate(date, "YYYY-MM-DD") : "",
						service: service.name,
						price: given_price,
						currency,
						calculated_price,
						comments,
						booked_price,
					})
				),
				visas: normalizeVisaServices({ visas, startDate }),
			}
			const tripQuoteOptions = options.map(
				(
					{
						name: optionName,
						cost_prices_per_person,
						per_person_cost_price_components,
						given_price_comments,
						given_price_customer_remarks,
						margins,
						given_price,
						unrounded_given_prices,
						given_prices_per_person,
						gst_included,
						taxes,
						cost_prices_per_component,
						given_prices_per_component,
						per_component_cost_price_components,
					},
					index
				) => {
					// cost price
					const hotelsOption = hotel_options.find(
						(o) => Number(o.option_index) === index
					)
					const hotels = transformHotelsToRequestDataForQuote(
						hotelsOption?.hotels || []
					)
					const hotelPrice = hotelsOption?.hotelPrice || []
					const hotel_extras = transformHotelExtrasToRequestDataForQuote(
						hotelsOption?.hotelExtras || []
					)
					const hotelExtrasPrice = hotelsOption?.hotelExtrasPrice || []

					const anythingAdded =
						hotels.length +
						hotel_extras.length +
						sharedServicesAmongOptions.cabs.length +
						(sharedServicesAmongOptions.travel_activities?.length || 0) +
						sharedServicesAmongOptions.transport_extras.length +
						(sharedServicesAmongOptions.flights?.length || 0) +
						(sharedServicesAmongOptions.flights?.length || 0) +
						(sharedServicesAmongOptions.visas?.length || 0) +
						sharedServicesAmongOptions.other_extras.length
					if (!anythingAdded) {
						throw Error(
							`Please select atleast one service to create the quote (${optionName})`
						)
					}

					if (options.length > 1 && !hotels.length && !hotel_extras.length) {
						throw Error(
							`Please select some hotels or hotel extras for quote option (${optionName})`
						)
					}

					const totalPrice = Number(
						formatMoneyByDecimal(
							makeCurrencyConverter(currency_pairs)(
								costing_currency,
								addMoneyFromDifferentCurrencies([
									...hotelPrice,
									...hotelExtrasPrice,
									...cabPrice,
									...transportExtrasPrice,
									...flightPrice,
									...visasPrice,
									...otherExtrasPrice,
								])
							)
						)
					)
					if (!totalPrice) {
						throw Error(
							"Please provide cost price of service(s). You can add total cost price to any one of the services."
						)
					}

					const totalMarkupPrice =
						(margins?.total || 0) +
						(margins?.per_component_allocation?.flights || 0)

					const markupPercentage = (totalMarkupPrice / totalPrice) * 100

					if (
						minMarkupPercentage !== null &&
						minMarkupPercentage !== undefined &&
						minMarkupPercentage > markupPercentage
					) {
						throw Error(
							`Markup must be atleast ${minMarkupPercentage}% of total cost price in ${optionName} Option`
						)
					}
					const givenPricesPerComponentPayload =
						prepareGivenPricesPerComponentPayloadDispatcher(
							pricing_strategy as TGivenQuotePricingStrategy,
							given_prices_per_component
						)

					return {
						...sharedServicesAmongOptions,
						name: optionName,
						cost_prices_per_person,
						per_person_cost_price_components,
						given_price_comments,
						given_price_customer_remarks,
						margins,
						given_price,
						unrounded_given_prices, //given prices (unrounded) in costing currency
						given_prices_per_person,
						hotels,
						hotel_extras,
						total_price: totalPrice,
						gst_included: Number(gst_included),
						taxes,
						cost_prices_per_component,
						given_prices_per_component: givenPricesPerComponentPayload,
						per_component_cost_price_components,
						ai_autofilled: ai_autofilled,
					}
				}
			)

			const payload = {
				...configuration,
				options: tripQuoteOptions,
			}
			return xhr
				.post(`/trips/${tripId}/quotes`, payload)
				.then((resp) => resp.data.data)
		},
	}
}

/**
 * Submit the sub-forms to show errors
 */
function submitAllForms(document: HTMLDivElement) {
	const buttons =
		document.querySelectorAll<HTMLButtonElement>("[type='submit']")
	for (let i = 0; i < buttons.length; i++) {
		const button = buttons[i]
		if (typeof button.click === "function") button.click()
	}
}

/**
 * Show errors in the forms
 */
function showErrorsFromForms(document: HTMLDivElement, error: string) {
	if (document) {
		const errors: NodeListOf<HTMLButtonElement> =
			document.querySelectorAll("[role='alert']")
		if (errors.length) {
			if (typeof errors[0].scrollIntoView === "function") {
				errors[0].scrollIntoView({
					behavior: "smooth",
					block: "center",
				})
			} else {
				window.alert(error)
			}
		}
	}
}

/**
 * Prepares valid currency pairs for API submission by filtering out unnecessary pairs.
 * Extracts currencies from price arrays and filters to only include necessary exchange rates.
 */
function prepareValidCurrencyPairsForPayload(
	currency_pairs_obj: TCurrencyPairsObject,
	costingCurrency: string,
	givenCurrency: string,
	hotel_options: INewQuoteState["hotel_options"],
	cabPrice: Array<TMoney>,
	transportExtrasPrice: Array<TMoney>,
	flightPrice: Array<TMoney>,
	otherExtrasPrice: Array<TMoney>,
	visasPrice: Array<TMoney>
): Array<TCurrencyPair> {
	// Extract currencies from all price arrays
	const usedCurrencies = new Set<string>()

	// Add essential currencies
	usedCurrencies.add(costingCurrency)

	// Extract from hotel prices
	hotel_options.forEach((option) => {
		option.hotelPrice.forEach((price) => usedCurrencies.add(price.currency))
		option.hotelExtrasPrice.forEach((price) =>
			usedCurrencies.add(price.currency)
		)
	})

	// Extract from all other price arrays
	;[
		cabPrice,
		transportExtrasPrice,
		flightPrice,
		otherExtrasPrice,
		visasPrice,
	].forEach((priceArray) => {
		priceArray.forEach((price) => usedCurrencies.add(price.currency))
	})

	// Convert all currency pairs to array format
	const allCurrencyPairs = currencyPairsObjectToArray(currency_pairs_obj)

	// Filter to only include necessary pairs
	const necessaryCurrencyPairs = allCurrencyPairs.filter((pair) => {
		const { baseCurrency, counterCurrency } = pair

		// Keep same currency pairs if exists in the usedCurrency Pairs (USD/USD = 1)
		if (
			baseCurrency === counterCurrency &&
			usedCurrencies.has(counterCurrency)
		) {
			return true
		}

		// Keep service currency to costing currency pairs
		if (
			usedCurrencies.has(baseCurrency) &&
			counterCurrency === costingCurrency
		) {
			return true
		}

		// Keep costing currency to given currency pair
		if (baseCurrency === costingCurrency && counterCurrency === givenCurrency) {
			return true
		}

		return false
	})

	//There can be case when "currency_pairs" provided by the NewQuoteForm's state do not have costing to given currency pair ratio = 1 when costing and given currency are same,
	//But api expects this data as we can't send empty currency pairs to the api so we need to append this to the "necessaryCurrencyPairs".
	if (costingCurrency === givenCurrency) {
		const sameCostingGivenCurrencyPairRatio = necessaryCurrencyPairs.find(
			(pair) => {
				if (
					pair.baseCurrency === costingCurrency &&
					pair.counterCurrency === givenCurrency &&
					pair.ratio === 1
				) {
					return true
				} else {
					return false
				}
			}
		)

		if (!sameCostingGivenCurrencyPairRatio) {
			necessaryCurrencyPairs.push({
				baseCurrency: costingCurrency,
				counterCurrency: givenCurrency,
				ratio: 1,
			})
		}
	}
	return necessaryCurrencyPairs
}

function VisaServicesPreview({ startDate }: { startDate: Date }) {
	const { value: visas } = useFieldValue<INewQuoteState["visas"]>("visas")
	return (
		<Stack>
			{visas.services?.length ? (
				<Inline gap="4">
					<Box>
						<StepIcon
							color="primary"
							bgColor="primary"
							borderWidth="1"
							borderColor="primary_emphasis"
						>
							<Icons.GlobeWithBottomLine size="6" color="primary" />
						</StepIcon>
					</Box>
					<Stack gap="4" flex="1">
						<Text fontSize={"md"} fontWeight="semibold">
							Visa
						</Text>
						<VisaServicesDetails services={visas} startDate={startDate} />
					</Stack>
				</Inline>
			) : null}
		</Stack>
	)
}

function VisaServicesDetails({
	services,
	hidePrice,
	startDate,
}: Readonly<{
	services: INewQuoteState["visas"]
	hidePrice?: boolean
	startDate: Date
}>) {
	const { xs, sm, md } = useBreakpoints()
	const { value: visasPrice } =
		useFieldValue<INewQuoteState["visasPrice"]>("visasPrice")
	const totalGivenPrice = addMoneyFromDifferentCurrencies(visasPrice)

	return (
		<Stack gap="2">
			{!sm && !xs && !md ? (
				<VisaServicesDetailsDesktopView
					services={services}
					hidePrice={hidePrice}
					startDate={startDate}
				/>
			) : (
				<VisaServicesDetailsMobileView
					services={services}
					hidePrice={hidePrice}
					startDate={startDate}
				/>
			)}
			{hidePrice ? null : (
				<Box textAlign="right">
					<Box
						display="inlineBlock"
						paddingX="4"
						paddingY="1"
						bgColor="subtle"
						borderWidth="1"
						rounded="md"
						fontWeight="semibold"
					>
						Total: <MoneySum money={totalGivenPrice} />
					</Box>
				</Box>
			)}
		</Stack>
	)
}

function VisaServicesDetailsMobileView({
	services,
	hidePrice,
	startDate,
}: Readonly<{
	services: INewQuoteState["visas"]
	hidePrice?: boolean
	startDate: Date
}>) {
	return (
		<Box as="ol" marginBottom="4">
			{services.services
				?.filter((service) => Boolean(service.country))
				?.map((service, index) => (
					<Box
						as="li"
						key={service.visa?.id ? `${service.visa.id}-${index}` : index}
						marginBottom="4"
						rounded="md"
						overflow="hidden"
						bgColor="default"
						borderWidth="1"
					>
						<Stack padding="4" gap="3">
							{/* Country Header */}
							<Inline justifyContent="between" alignItems="center">
								<Text fontWeight="semibold" fontSize="base">
									{service.country?.name}
								</Text>
								<Text fontSize="sm" color="muted">
									Pax:{" "}
									<Text as="span" fontWeight="semibold" color="default">
										{service.pax}
									</Text>
								</Text>
							</Inline>

							{/* Visa Service & Type */}
							<Stack gap="1">
								<Text fontWeight="semibold" fontSize="base">
									{service.visa?.name}
								</Text>
								<Text fontSize="sm">{service.visa_type?.name}</Text>
								<VisaTypeDisabled visaType={service.visa_type} />
							</Stack>

							{/* Visa Details (Entry type, Purpose) */}
							{(service.visa_type?.entry_type ||
								service.visa_type?.purpose) && (
								<Inline gap="1" fontSize="sm" color="muted">
									{joinAttributes(
										service.visa_type?.entry_type ? (
											<Text fontSize="sm">
												{getEntryTypeDisplayName(service.visa_type?.entry_type)}
											</Text>
										) : null,
										service.visa_type?.purpose ? (
											<Text fontSize="sm">
												{getPurposeOfVisaDisplayName(
													service.visa_type?.purpose
												)}
											</Text>
										) : null
									)}
								</Inline>
							)}

							{/* Validity / Duration / Processing Time */}
							{(service.visa_type?.duration_formatted ||
								service.visa_type?.processing_time_in_hours ||
								service.visa_type?.apply_before_days) && (
								<Stack gap="1" fontSize="sm" color="muted">
									{service.visa_type?.duration_formatted ? (
										<Text fontSize="sm">
											Duration {service.visa_type?.duration_formatted}
										</Text>
									) : null}
									{service.visa_type?.processing_time_in_hours ? (
										<Text fontSize="sm">
											Processing Time{" "}
											{visaProcessingTimeStr(
												service.visa_type?.processing_time_in_hours
											)}
										</Text>
									) : null}
									{service.visa_type?.apply_before_days ? (
										<Text fontSize="sm">
											Apply Minimum {service.visa_type?.apply_before_days} Days
											Prior
										</Text>
									) : null}
								</Stack>
							)}

							{/* Price Section */}
							{!hidePrice && (
								<Inline
									justifyContent="between"
									alignItems="center"
									paddingTop="2"
									borderTopWidth="1"
								>
									<Text fontSize="sm" color="muted">
										Price:
									</Text>
									<Inline gap="1" alignItems="baseline">
										<Money
											money={moneyParseByDecimal(
												Number(service.given_price),
												service?.visa?.currency || "INR"
											)}
											showCurrency
											fontWeight="semibold"
											fontSize="base"
										/>
										{service.visa_type?.cost_price &&
										!isVisaRateExpired({
											rateValidityDate: service.visa_type.price_valid_till,
											startDate: formatDate(startDate, "YYYY-MM-DD HH:mm:ss"),
										}) ? (
											<Text fontSize="xs" color="muted">
												{" / "}
												<Money
													money={multipleMoneyBy(
														service.visa_type?.cost_price,
														service.pax || 0
													)}
													showCurrency
													fontSize="xs"
												/>
											</Text>
										) : (
											<Text fontSize="xs" color="muted">
												{" / N/A"}
											</Text>
										)}
									</Inline>
								</Inline>
							)}
						</Stack>
					</Box>
				))}
		</Box>
	)
}

function VisaServicesDetailsDesktopView({
	services,
	startDate,
}: Readonly<{
	services: INewQuoteState["visas"]
	hidePrice?: boolean
	startDate: Date
}>) {
	return (
		<Table
			bordered
			responsive
			striped
			headers={[
				"Country",
				"Service & Service Type",
				"Visa Details",
				"Validity",
				"Pax",
				"Price",
			]}
			alignCols={{ 5: "right" }}
			rows={services.services
				?.filter((service) => Boolean(service.country))
				?.map((service) => [
					<Text fontWeight="semibold" fontSize="base">
						{service.country?.name}
					</Text>,
					<Stack gap="1">
						<Text fontWeight="semibold" fontSize="base">
							{service.visa?.name}
						</Text>
						<Text fontSize="base">{service.visa_type?.name}</Text>
						<VisaTypeDisabled visaType={service.visa_type} />
					</Stack>,
					<Inline gap="1">
						{joinAttributes(
							<Text fontSize="base">
								{getEntryTypeDisplayName(service.visa_type?.entry_type)}
							</Text>,
							<Text fontSize="base">
								{getPurposeOfVisaDisplayName(service.visa_type?.purpose)}
							</Text>
						)}
					</Inline>,
					<Stack>
						<Text>Duration {service.visa_type?.duration_formatted}</Text>
						<Text>
							Processing Time{" "}
							{visaProcessingTimeStr(
								service.visa_type?.processing_time_in_hours
							)}
						</Text>
						{service.visa_type?.apply_before_days ? (
							<Text>
								Apply Minimum {service.visa_type?.apply_before_days} Days Prior
							</Text>
						) : null}
					</Stack>,
					<Text fontWeight="semibold" fontSize="base">
						{service.pax}
					</Text>,

					<Stack>
						<Money
							money={moneyParseByDecimal(
								Number(service.given_price),
								service?.visa?.currency || "INR"
							)}
							showCurrency
							fontWeight="semibold"
							fontSize="base"
						/>
						{service.visa_type?.cost_price &&
						!isVisaRateExpired({
							rateValidityDate: service.visa_type.price_valid_till,
							startDate: formatDate(startDate, "YYYY-MM-DD HH:mm:ss"),
						}) ? (
							<Money
								money={multipleMoneyBy(
									service.visa_type?.cost_price,
									service.pax || 0
								)}
								showCurrency
								fontSize="sm"
							/>
						) : (
							<Text fontSize="xs" color="muted">
								/ N/A
							</Text>
						)}
					</Stack>,
				])}
		/>
	)
}
